import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import axios from "api/axios";
import CoordinateMapPicker from "components/CoordinateMapPicker";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import Pagination from "components/ui/Pagination";
import { getTrackerProfile } from "config/trackerProfiles";
import { useLocationContext } from "context/LocationContext";
import TrackerRegistrationWorkspace from "pages/tracker-registration/components/TrackerRegistrationWorkspace";
import {
  buildRegistrationSectionsFromProgram,
  getProgramStageEventFields,
  getProgramStageOptions,
  isRegistrationFieldVisible,
  PROGRAM_METADATA_FIELDS,
} from "pages/tracker-registration/programMetadata";
import { uploadTrackerFileResource } from "pages/tracker-registration/trackerRegistrationApi";
import {
  buildTrackerRegistrationAttributes,
  buildTrackerRegistrationGeometry,
  getPhoneNumberValidationMessage,
} from "pages/tracker-registration/trackerRegistrationPayload";
import {
  addGeneratedTrackerAttributeValues,
  buildTrackerBulkloadPayload,
  downloadTrackerBulkloadTemplate,
  downloadTrackerBulkloadTemplateWithData,
  formatTrackerBulkloadFileSize,
  getBulkloadPreviewColumns,
  getTrackerBulkloadConfig,
  parseTrackerBulkloadWorkbook,
  runTrackerBulkloadImport,
} from "utils/trackerBulkload";

const DEFAULT_TRACKER_TABLE_CONFIG = {
  maxColumns: 8,
  hiddenColumnsExact: [],
  hiddenColumnsIncludes: [
    "uid",
    "uuid",
    "tracked entity",
    "trackedentity",
    "instance",
    "created",
    "updated",
    "last updated by",
    "stored by",
    "deleted",
  ],
  columnOrder: [],
};
const TRACKER_COLUMN_PREFERENCES_NAMESPACE = "trackerCaptureGridColumns";
const TRACKER_REGISTRATION_STATUS_COLUMN = "Registration Status";
const TRACKER_REGISTRATION_STATUS_DEFINITION = {
  id: "registrationStatus",
  displayName: TRACKER_REGISTRATION_STATUS_COLUMN,
  valueType: "TEXT",
};
const TRACKER_SYSTEM_COLUMN_DEFINITIONS = [
  { id: "orgUnitName", displayName: "Registering unit", valueType: "TEXT" },
  { id: "created", displayName: "Registration date", valueType: "DATE" },
  { id: "inactive", displayName: "Inactive", valueType: "BOOLEAN" },
];
const TRACKER_RELATIONSHIP_RECORD_FIELDS =
  "trackedEntity,trackedEntityInstance,createdAt,inactive,geometry,orgUnit,relationships[relationship,relationshipType,relationshipName,relationshipTypeName,from[trackedEntityInstance[trackedEntityInstance],trackedEntity[trackedEntity],enrollment[enrollment],event[event]],to[trackedEntityInstance[trackedEntityInstance],trackedEntity[trackedEntity],enrollment[enrollment],event[event]]],enrollments[enrollment,program,status,attributes[attribute,displayName,value],events[event,eventDate,occurredAt,scheduledAt,status,programStage,program,orgUnit,dataValues[dataElement,displayName,value]]],attributes[attribute,displayName,value]";
const TRACKER_QUERY_SYSTEM_HEADERS = new Set([
  "instance",
  "created",
  "lastupdated",
  "ou",
  "ouname",
  "te",
  "inactive",
  "potentialduplicate",
]);

const TRACKER_TABLE_PROGRAM_CONFIGS = {};
const TRACKER_RECORDS_PAGE_SIZE = 20;
const TRACKER_RECORDS_FETCH_PAGE_SIZE = 200;
const LOGISTICS_YEAR_ATTRIBUTE_ID = "Ik7fouv4KOA";
const TRACKER_PROGRAM_INDEX_FIELDS =
  "id,displayName,shortName,description,programType,featureType,organisationUnits[id,path],trackedEntityType[id,displayName,featureType],programStages[id,displayName]";

function formatLocationScope(currentLocation) {
  if (!currentLocation || currentLocation.level === "national") {
    return "Ghana (National)";
  }

  if (currentLocation.level === "region") {
    return currentLocation.name;
  }

  if (currentLocation.level === "subdistrict") {
    return `${currentLocation.districtName} / ${currentLocation.name}`;
  }

  return currentLocation.name;
}

function getTrackerWorkspaceDescription(selectedTracker) {
  if (!selectedTracker) {
    return "View, filter, and register tracker records.";
  }

  return "View, filter, and register records for this tracker.";
}

function buildProgramAttributeOrder(program) {
  return (program?.programTrackedEntityAttributes || [])
    .slice()
    .sort((a, b) => (a?.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b?.sortOrder ?? Number.MAX_SAFE_INTEGER))
    .map((entry) =>
      entry?.trackedEntityAttribute?.displayFormName ||
      entry?.trackedEntityAttribute?.displayName ||
      entry?.trackedEntityAttribute?.shortName,
    )
    .filter(Boolean);
}

function normalizeTrackerProgramOptions(rawPrograms, currentLocation) {
  const shouldFilterByLocation =
    currentLocation?.level &&
    currentLocation.level !== "national" &&
    Boolean(currentLocation?.orgUnitId);
  const trackerPrograms = (rawPrograms || [])
    .filter((program) => {
      const programType = String(program?.programType || "").toUpperCase();
      const isTrackerProgram =
        Boolean(program?.id) &&
        (programType === "WITH_REGISTRATION" || program?.trackedEntityType);

      if (!isTrackerProgram || !shouldFilterByLocation) return isTrackerProgram;

      return (program?.organisationUnits || []).some(
        (orgUnit) => orgUnit?.id === currentLocation.orgUnitId,
      );
    })
    .map((program) => {
      return {
        label: program?.displayName || program?.name || program?.shortName || program?.id,
        value: program.id,
        source: "endpoint",
        icon: "Database",
        description: program?.trackedEntityType?.displayName
          ? `${program.trackedEntityType.displayName} tracker`
          : "Tracker records",
        program,
        attributeOrder: buildProgramAttributeOrder(program),
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  return trackerPrograms;
}

function getSelectedTrackerOption(searchParams, trackerOptions) {
  const trackerId = searchParams.get("tracker");
  if (!trackerId) return trackerOptions[0] || null;
  return trackerOptions.find((option) => option.value === trackerId) || trackerOptions[0] || null;
}

function getRelationshipText(relationship) {
  return [
    relationship?.type,
    relationship?.label,
    relationship?.relationshipName,
    relationship?.relationshipTypeName,
    relationship?.relationshipType?.displayName,
    relationship?.relationshipType?.name,
    relationship?.relationshipType,
    relationship?.targetLabel,
  ].filter(Boolean).join(" ");
}

function resolveTrackerOptionFromRelationshipName(relationship, trackerOptions, currentTrackerId = "") {
  const relationshipText = normalizeTrackerText(getRelationshipText(relationship));
  if (!relationshipText) return null;

  const candidates = (Array.isArray(trackerOptions) ? trackerOptions : [])
    .filter((option) => option?.value && option.value !== currentTrackerId)
    .map((option) => {
      const labelText = normalizeTrackerText([
        option?.label,
        option?.program?.displayName,
        option?.program?.name,
        option?.program?.shortName,
      ].join(" "));
      const labelTokens = labelText.split(" ").filter((token) => token.length > 2);
      const tokenMatches = labelTokens.filter((token) => relationshipText.includes(token)).length;
      const exactMatch = labelText && relationshipText.includes(labelText);

      return {
        option,
        score: (exactMatch ? 100 : 0) + tokenMatches,
      };
    })
    .filter((candidate) => candidate.score > 0)
    .sort((left, right) => right.score - left.score);

  return candidates[0]?.option || null;
}

function resolveTrackerOptionFromLinkedRecord(linkedRecord, trackerOptions, currentTrackerId = "") {
  const enrollments = Array.isArray(linkedRecord?.enrollments) ? linkedRecord.enrollments : [];
  const enrolledProgramIds = enrollments
    .map((enrollment) => enrollment?.program?.id || enrollment?.program)
    .filter(Boolean);
  const targetProgramId =
    enrolledProgramIds.find((programId) => programId !== currentTrackerId) ||
    enrolledProgramIds[0] ||
    "";

  if (!targetProgramId) return null;

  return (Array.isArray(trackerOptions) ? trackerOptions : []).find((option) => option?.value === targetProgramId) || {
    value: targetProgramId,
    label: "Linked Tracker",
    source: "relationship",
    icon: "Database",
    description: "Linked tracker record",
    program: null,
    attributeOrder: [],
  };
}

function normalizeTrackerText(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

function isProjectsProgrammesTracker(selectedTracker) {
  const trackerText = normalizeTrackerText(
    [
      selectedTracker?.label,
      selectedTracker?.program?.displayName,
      selectedTracker?.program?.shortName,
      selectedTracker?.program?.description,
    ].join(" "),
  );

  return trackerText.includes("projects and programmes") ||
    trackerText.includes("projects programmes");
}

function getTrackerIdentityText(selectedTracker) {
  return normalizeTrackerText(
    [
      selectedTracker?.label,
      selectedTracker?.program?.displayName,
      selectedTracker?.program?.shortName,
      selectedTracker?.program?.description,
    ].join(" "),
  );
}

function isAnnualActionPlanTracker(selectedTracker) {
  const trackerText = getTrackerIdentityText(selectedTracker);

  return trackerText.includes("annual action plan") || trackerText.includes("aap");
}

function isLogisticsTracker(selectedTracker) {
  const trackerText = getTrackerIdentityText(selectedTracker);

  return trackerText.includes("logistic tracker") || trackerText.includes("logistics tracker");
}

function isMeetingsTracker(selectedTracker) {
  const trackerText = normalizeTrackerText(
    [
      selectedTracker?.label,
      selectedTracker?.program?.displayName,
      selectedTracker?.program?.shortName,
    ].join(" "),
  );
  return trackerText.includes("meetings");
}

function isDocumentsHubTracker(selectedTracker) {
  const trackerText = getTrackerIdentityText(selectedTracker);
  return trackerText.includes("documents hub") || trackerText.includes("document hub");
}

function getRequiredDefaultColumnIds(selectedTracker) {
  return isLogisticsTracker(selectedTracker) ? [LOGISTICS_YEAR_ATTRIBUTE_ID] : [];
}

function isHiddenTimelineStageOption(stageOption, selectedTracker) {
  const stageLabels = [
    stageOption?.label,
    stageOption?.stage?.displayName,
    stageOption?.stage?.shortName,
    stageOption?.stage?.name,
  ].map(normalizeTrackerText);

  if (isProjectsProgrammesTracker(selectedTracker)) {
    return stageLabels.some((stageLabel) => stageLabel === "s p r v1" || stageLabel === "spr v1");
  }

  if (isAnnualActionPlanTracker(selectedTracker)) {
    return stageLabels.some((stageLabel) => /\bapproval\b|\bapproved\b/.test(stageLabel));
  }

  return false;
}

function getVisibleTimelineStageOptions(selectedTracker) {
  return getProgramStageOptions(selectedTracker?.program).filter(
    (stageOption) => !isHiddenTimelineStageOption(stageOption, selectedTracker),
  );
}

function getTrackerSystemColumnDefinitions(selectedTracker) {
  return [
    ...(getVisibleTimelineStageOptions(selectedTracker).length > 0
      ? [TRACKER_REGISTRATION_STATUS_DEFINITION]
      : []),
    ...TRACKER_SYSTEM_COLUMN_DEFINITIONS,
  ];
}

function getTrackerRegistrationStatus(record, selectedTracker) {
  const visibleStageIds = new Set(
    getVisibleTimelineStageOptions(selectedTracker).map((stageOption) => stageOption.value),
  );
  if (visibleStageIds.size === 0) return "";

  const selectedProgramId = selectedTracker?.value || selectedTracker?.program?.id || "";
  const enrollmentEvents = (record?.enrollments || []).flatMap((enrollment) => {
    const enrollmentProgramId = enrollment?.program?.id || enrollment?.program || "";
    if (selectedProgramId && enrollmentProgramId && enrollmentProgramId !== selectedProgramId) {
      return [];
    }

    return Array.isArray(enrollment?.events) ? enrollment.events : [];
  });
  const directEvents = Array.isArray(record?.events) ? record.events : [];
  const completedStageIds = new Set();

  [...enrollmentEvents, ...directEvents].forEach((event) => {
    const eventProgramId = event?.program?.id || event?.program || "";
    const programStageId = event?.programStage?.id || event?.programStage || "";

    if (
      (!selectedProgramId || !eventProgramId || eventProgramId === selectedProgramId) &&
      visibleStageIds.has(programStageId)
    ) {
      completedStageIds.add(programStageId);
    }
  });

  if (completedStageIds.size === 0) {
    return "Timeline pending";
  }

  return completedStageIds.size >= visibleStageIds.size
    ? "Timeline completed"
    : "Timeline in progress";
}

function extractTrackerResponseRecords(data) {
  return data?.instances || data?.trackedEntities || data?.trackedEntityInstances || [];
}

function extractTrackerResponseTotal(data, fallbackCount = 0) {
  const total = data?.pager?.total ?? data?.total;
  return typeof total === "number" ? total : fallbackCount;
}

function extractTrackerResponsePageCount(data, pageSizeFallback = TRACKER_RECORDS_FETCH_PAGE_SIZE) {
  const explicitPageCount = data?.pager?.pageCount;
  if (typeof explicitPageCount === "number" && explicitPageCount > 0) {
    return explicitPageCount;
  }

  const total = data?.pager?.total ?? data?.total;
  const pageSize = data?.pager?.pageSize ?? data?.pageSize ?? pageSizeFallback;

  if (typeof total === "number" && typeof pageSize === "number" && pageSize > 0) {
    return Math.max(1, Math.ceil(total / pageSize));
  }

  return 1;
}

async function loadAllTrackerRecords({ orgUnitId, programId }) {
  const baseQuery =
    `orgUnit=${orgUnitId}` +
    `&program=${programId}` +
    `&pageSize=${TRACKER_RECORDS_FETCH_PAGE_SIZE}` +
    `&totalPages=true` +
    `&order=createdAt:desc` +
    `&fields=${TRACKER_RELATIONSHIP_RECORD_FIELDS}`;

  const firstResponse = await axios.get(`/tracker/trackedEntities?${baseQuery}&page=1`);
  const firstPageData = firstResponse?.data || {};
  const firstPageRecords = Array.isArray(extractTrackerResponseRecords(firstPageData))
    ? extractTrackerResponseRecords(firstPageData)
    : [];
  const total = extractTrackerResponseTotal(firstPageData, firstPageRecords.length);
  const pageCount = extractTrackerResponsePageCount(firstPageData);

  if (pageCount <= 1) {
    return {
      records: firstPageRecords,
      total,
      pageCount: 1,
    };
  }

  const remainingPageRequests = Array.from({ length: pageCount - 1 }, (_, index) =>
    axios.get(`/tracker/trackedEntities?${baseQuery}&page=${index + 2}`),
  );

  const remainingResponses = await Promise.all(remainingPageRequests);
  const remainingRecords = remainingResponses.flatMap((response) => {
    const records = extractTrackerResponseRecords(response?.data || {});
    return Array.isArray(records) ? records : [];
  });

  const records = [...firstPageRecords, ...remainingRecords];

  return {
    records,
    total: typeof total === "number" ? total : records.length,
    pageCount,
  };
}

function getPreferredTrackerAttributeLabel(attribute, savedColumnConfig) {
  const attributeId = attribute?.id || attribute?.attribute;
  const savedLabel = attributeId ? savedColumnConfig?.[attributeId]?.displayName : "";

  return String(
    savedLabel ||
      attribute?.displayFormName ||
      attribute?.displayName ||
      attribute?.shortName ||
      attributeId ||
      "",
  ).trim();
}

function buildAttributeValueMap(attributes, savedColumnConfig, selectedTracker = null) {
  const entries = Array.isArray(attributes) ? attributes : [];
  const metadataAttributeById = new Map(
    (selectedTracker?.program?.programTrackedEntityAttributes || [])
      .map((entry) => entry?.trackedEntityAttribute)
      .filter((attribute) => attribute?.id)
      .map((attribute) => [attribute.id, attribute]),
  );

  return entries.reduce((acc, item, index) => {
    const key = getPreferredTrackerAttributeLabel(item, savedColumnConfig) || `Attribute ${index + 1}`;
    const attributeId = item?.attribute || item?.trackedEntityAttribute || item?.id;
    const metadataAttribute = attributeId ? metadataAttributeById.get(attributeId) : null;
    const metadataKey = metadataAttribute
      ? getPreferredTrackerAttributeLabel(metadataAttribute, savedColumnConfig)
      : "";
    const value = item?.value ?? "";

    if (!(key in acc)) {
      acc[key] = value;
    }

    if (metadataKey && !(metadataKey in acc)) {
      acc[metadataKey] = value;
    }

    return acc;
  }, {});
}

function getEnrollmentProgramId(enrollment) {
  return enrollment?.program?.id || enrollment?.program || "";
}

function getTrackerRecordAttributes(record, selectedTracker = null) {
  const targetProgramId = selectedTracker?.value || selectedTracker?.program?.id || "";
  const topLevelAttributes = Array.isArray(record?.attributes) ? record.attributes : [];
  const enrollmentAttributes = (Array.isArray(record?.enrollments) ? record.enrollments : [])
    .filter((enrollment) => !targetProgramId || getEnrollmentProgramId(enrollment) === targetProgramId)
    .flatMap((enrollment) => (Array.isArray(enrollment?.attributes) ? enrollment.attributes : []));
  const attributesById = new Map();

  [...topLevelAttributes, ...enrollmentAttributes].forEach((attribute) => {
    const attributeId = attribute?.attribute || attribute?.trackedEntityAttribute || attribute?.id;
    if (!attributeId) return;

    const existingAttribute = attributesById.get(attributeId);
    const existingValue = existingAttribute?.value;
    const nextValue = attribute?.value;

    if (!existingAttribute || ((existingValue === undefined || existingValue === "") && nextValue !== undefined && nextValue !== "")) {
      attributesById.set(attributeId, attribute);
    }
  });

  return Array.from(attributesById.values());
}

function buildTrackerSystemValueMap(record, currentLocation, selectedTracker) {
  const recordOrgUnit = record?.orgUnit;
  const recordOrgUnitId =
    typeof recordOrgUnit === "object" ? recordOrgUnit?.id || recordOrgUnit?.value : recordOrgUnit;
  const recordOrgUnitLabel =
    (typeof recordOrgUnit === "object"
      ? recordOrgUnit?.displayName || recordOrgUnit?.name || recordOrgUnit?.label
      : "") ||
    record?.orgUnitName ||
    (recordOrgUnitId && recordOrgUnitId === currentLocation?.orgUnitId ? currentLocation?.name : "") ||
    recordOrgUnitId ||
    "";

  return {
    ...(getVisibleTimelineStageOptions(selectedTracker).length > 0
      ? { [TRACKER_REGISTRATION_STATUS_COLUMN]: getTrackerRegistrationStatus(record, selectedTracker) }
      : {}),
    "Registering unit": recordOrgUnitLabel,
    "Registration date": record?.createdAt || record?.created || "",
    Inactive: typeof record?.inactive === "boolean" ? record.inactive : "",
  };
}

function normalizeColumnMatchValue(value) {
  return String(value || "").trim().toLowerCase();
}

function matchesColumnExact(columnName, candidates = []) {
  const normalized = normalizeColumnMatchValue(columnName);
  return candidates.some((candidate) => normalizeColumnMatchValue(candidate) === normalized);
}

function matchesColumnIncludes(columnName, candidates = []) {
  const normalized = normalizeColumnMatchValue(columnName);
  return candidates.some((candidate) => normalized.includes(normalizeColumnMatchValue(candidate)));
}

function getTrackerTableConfig(selectedTracker) {
  if (!selectedTracker) {
    return DEFAULT_TRACKER_TABLE_CONFIG;
  }

  const programConfig = TRACKER_TABLE_PROGRAM_CONFIGS[selectedTracker.value] || {};

  return {
    ...DEFAULT_TRACKER_TABLE_CONFIG,
    ...programConfig,
    hiddenColumnsExact: [
      ...(DEFAULT_TRACKER_TABLE_CONFIG.hiddenColumnsExact || []),
      ...(programConfig.hiddenColumnsExact || []),
    ],
    hiddenColumnsIncludes: [
      ...(DEFAULT_TRACKER_TABLE_CONFIG.hiddenColumnsIncludes || []),
      ...(programConfig.hiddenColumnsIncludes || []),
    ],
    columnOrder: programConfig.columnOrder || DEFAULT_TRACKER_TABLE_CONFIG.columnOrder || [],
  };
}

function shouldHideTrackerColumn(columnName, trackerTableConfig) {
  if (!columnName) return true;

  if (matchesColumnExact(columnName, trackerTableConfig.hiddenColumnsExact)) return true;
  if (matchesColumnIncludes(columnName, trackerTableConfig.hiddenColumnsIncludes)) return true;
  return false;
}

function getTrackerAttributeColumns(
  records,
  selectedTracker,
  savedColumnConfig,
  maxColumnsFallback = 8,
) {
  const trackerTableConfig = getTrackerTableConfig(selectedTracker);
  const safeRecords = Array.isArray(records) ? records : [];
  const systemColumnDefinitions = getTrackerSystemColumnDefinitions(selectedTracker);
  const systemColumns = systemColumnDefinitions.map((column) => column.displayName);
  const metadataAttributes = (selectedTracker?.program?.programTrackedEntityAttributes || [])
    .slice()
    .sort((left, right) => (left?.sortOrder ?? Number.MAX_SAFE_INTEGER) - (right?.sortOrder ?? Number.MAX_SAFE_INTEGER))
    .filter((entry) => entry?.trackedEntityAttribute?.id);
  const metadataColumns = metadataAttributes
    .map((entry) => getPreferredTrackerAttributeLabel(entry.trackedEntityAttribute, savedColumnConfig))
    .filter((columnName) => !shouldHideTrackerColumn(columnName, trackerTableConfig));
  const metadataColumnSet = new Set(metadataColumns);
  const metadataAttributeIdSet = new Set(
    metadataAttributes.map((entry) => entry.trackedEntityAttribute.id).filter(Boolean),
  );
  const observedExtraColumns = safeRecords
    .flatMap((record) => getTrackerRecordAttributes(record, selectedTracker))
    .filter((item) => !metadataAttributeIdSet.has(item?.attribute))
    .map(
      (item, index) =>
        getPreferredTrackerAttributeLabel(item, savedColumnConfig) || `Attribute ${index + 1}`,
    )
    .filter(
      (columnName, index, columns) =>
        !metadataColumnSet.has(columnName) &&
        !shouldHideTrackerColumn(columnName, trackerTableConfig) &&
        columns.indexOf(columnName) === index,
    );
  const fallbackAllColumns = [...systemColumns, ...metadataColumns, ...observedExtraColumns];
  const metadataColumnById = new Map(
    metadataAttributes.map((entry) => [
      entry.trackedEntityAttribute.id,
      getPreferredTrackerAttributeLabel(entry.trackedEntityAttribute, savedColumnConfig),
    ]),
  );
  const systemColumnById = new Map(
    systemColumnDefinitions.map((column) => [column.id, column.displayName]),
  );
  const savedEntries =
    savedColumnConfig && typeof savedColumnConfig === "object"
      ? Object.values(savedColumnConfig).filter((entry) => entry && typeof entry === "object")
      : [];
  const allColumns = fallbackAllColumns;
  const backendPresetColumns = metadataAttributes
    .filter((entry) => entry?.displayInList === true)
    .map((entry) => getPreferredTrackerAttributeLabel(entry.trackedEntityAttribute, savedColumnConfig))
    .filter((columnName) => allColumns.includes(columnName));
  const requiredDefaultColumns = getRequiredDefaultColumnIds(selectedTracker)
    .map((attributeId) => metadataColumnById.get(attributeId))
    .filter((columnName) => columnName && allColumns.includes(columnName));
  const maxColumns = trackerTableConfig.maxColumns || maxColumnsFallback;
  const fallbackAttributeColumns = metadataColumns.slice(
    0,
    Math.max(maxColumns - systemColumns.length, 1),
  );
  const savedVisibilityByColumn = new Map(
    savedEntries
      .map((entry) => {
        const columnName =
          systemColumnById.get(entry.id) ||
          metadataColumnById.get(entry.id) ||
          allColumns.find(
            (candidate) =>
              normalizeColumnMatchValue(candidate) === normalizeColumnMatchValue(entry.displayName),
          );

        return columnName ? [columnName, entry.show === true] : null;
      })
      .filter(Boolean),
  );
  const savedVisibleColumns = allColumns.filter(
    (columnName) => savedVisibilityByColumn.get(columnName) === true,
  );
  const defaultColumns = [
    ...systemColumns,
    ...requiredDefaultColumns,
    ...(backendPresetColumns.length > 0 ? backendPresetColumns : fallbackAttributeColumns),
  ];
  const savedColumnsWithNewDefaults = [
    ...(systemColumns.includes(TRACKER_REGISTRATION_STATUS_COLUMN) &&
    !savedVisibilityByColumn.has(TRACKER_REGISTRATION_STATUS_COLUMN)
      ? [TRACKER_REGISTRATION_STATUS_COLUMN]
      : []),
    ...requiredDefaultColumns.filter((columnName) => !savedVisibilityByColumn.has(columnName)),
    ...savedVisibleColumns,
  ];
  const columns = (savedEntries.length > 0 ? savedColumnsWithNewDefaults : defaultColumns).filter(
    (columnName, index, selectedColumns) => selectedColumns.indexOf(columnName) === index,
  );

  return {
    columns,
    allColumns,
    hiddenCount: Math.max(allColumns.length - columns.length, 0),
  };
}

function buildTrackerColumnPreferencePayload({
  selectedTracker,
  allColumns,
  visibleColumns,
  savedColumnConfig,
}) {
  const systemColumnDefinitions = getTrackerSystemColumnDefinitions(selectedTracker);
  const metadataAttributes = (selectedTracker?.program?.programTrackedEntityAttributes || [])
    .slice()
    .sort((left, right) => (left?.sortOrder ?? Number.MAX_SAFE_INTEGER) - (right?.sortOrder ?? Number.MAX_SAFE_INTEGER))
    .map((entry) => entry?.trackedEntityAttribute)
    .filter((attribute) => attribute?.id)
    .map((attribute) => ({
      ...attribute,
      displayName: getPreferredTrackerAttributeLabel(attribute, savedColumnConfig),
    }));
  const definitionsByName = new Map(
    [...systemColumnDefinitions, ...metadataAttributes].map((definition) => [
      definition.displayName,
      definition,
    ]),
  );
  const visibleColumnSet = new Set(visibleColumns || []);

  return (allColumns || []).reduce((payload, columnName) => {
    const definition = definitionsByName.get(columnName);
    if (!definition?.id) return payload;

    const savedEntry = savedColumnConfig?.[definition.id] || {};
    payload[definition.id] = {
      ...savedEntry,
      id: definition.id,
      displayName: definition.displayName,
      ...(definition.displayFormName
        ? { displayFormName: definition.displayFormName }
        : {}),
      show: visibleColumnSet.has(columnName),
      valueType: definition.valueType || savedEntry.valueType || "TEXT",
    };

    return payload;
  }, {});
}

function looksLikeDateColumn(columnName) {
  return /date|deadline|period|year|month/i.test(String(columnName || ""));
}

function looksLikeStatusColumn(columnName) {
  return /status|state|approval|fulfillment|result/i.test(String(columnName || ""));
}

function looksLikeCoordinateColumn(columnName) {
  return /coordinate|coordinates|latitude|longitude|gis/i.test(String(columnName || ""));
}

function formatTrackerCoordinateValue(rawValue) {
  const value = String(rawValue || "").trim();

  if (!value) {
    return null;
  }

  const bracketMatch = value.match(/^\[\s*([^,\]]+)\s*,\s*([^,\]]+)\s*\]$/);
  if (bracketMatch) {
    const longitude = String(bracketMatch[1] || "").trim();
    const latitude = String(bracketMatch[2] || "").trim();
    return `Latitude: ${latitude}, Longitude: ${longitude}`;
  }

  const parts = value.split(",").map((part) => part.trim()).filter(Boolean);
  if (parts.length === 2) {
    return `Latitude: ${parts[1]}, Longitude: ${parts[0]}`;
  }

  return null;
}

function formatTrackerCellValue(columnName, rawValue) {
  if (rawValue === undefined || rawValue === null) return "-";

  const value = String(rawValue).trim();
  if (!value) return "-";

  const lower = value.toLowerCase();
  if (lower === "true") return "Yes";
  if (lower === "false") return "No";

  if (looksLikeCoordinateColumn(columnName)) {
    const formattedCoordinate = formatTrackerCoordinateValue(value);
    if (formattedCoordinate) {
      return formattedCoordinate;
    }
  }

  if (looksLikeDateColumn(columnName)) {
    const dateOnlyMatch = /^\d{4}-\d{2}-\d{2}$/.test(value);
    const dateTimeMatch = /^\d{4}-\d{2}-\d{2}t/i.test(value);

    if (dateOnlyMatch || dateTimeMatch) {
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      }
    }
  }

  return value.replace(/\s+/g, " ");
}

function escapeCsvValue(value) {
  const text = String(value ?? "");
  if (/[",\r\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }

  return text;
}

function escapeXmlValue(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function buildXmlTagName(value, fallback = "field") {
  const tagName = String(value || "")
    .trim()
    .replace(/^[^a-zA-Z_]+/, "")
    .replace(/[^a-zA-Z0-9_.-]+/g, "_");

  return tagName || fallback;
}

function buildSafeExportFileName(parts = []) {
  const baseName = parts
    .filter(Boolean)
    .join("-")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return baseName || "tracker-records";
}

function getTrackerRecordExportRows(records, columns) {
  return (records || []).map((record) =>
    (columns || []).map((columnName) =>
      formatTrackerCellValue(columnName, record?.attributeValueMap?.[columnName]),
    ),
  );
}

function getTrackerRecordExportObjects(records, columns) {
  return (records || []).map((record) =>
    (columns || []).reduce((row, columnName) => {
      row[columnName] = formatTrackerCellValue(columnName, record?.attributeValueMap?.[columnName]);
      return row;
    }, {}),
  );
}

function buildTrackerRecordsExportContent({ records, columns, selectedTracker, currentLocation, format }) {
  const normalizedFormat = String(format || "csv").toLowerCase();

  if (normalizedFormat === "json") {
    return {
      extension: "json",
      mimeType: "application/json;charset=utf-8",
      content: JSON.stringify(
        {
          tracker: selectedTracker?.label || "",
          location: formatLocationScope(currentLocation),
          exportedAt: new Date().toISOString(),
          columns,
          records: getTrackerRecordExportObjects(records, columns),
        },
        null,
        2,
      ),
    };
  }

  if (normalizedFormat === "xml") {
    const recordRows = getTrackerRecordExportObjects(records, columns)
      .map((record, recordIndex) => {
        const fields = (columns || [])
          .map((columnName) => {
            const tagName = buildXmlTagName(columnName);
            return `    <${tagName} name="${escapeXmlValue(columnName)}">${escapeXmlValue(record[columnName])}</${tagName}>`;
          })
          .join("\n");

        return `  <record index="${recordIndex + 1}">\n${fields}\n  </record>`;
      })
      .join("\n");

    return {
      extension: "xml",
      mimeType: "application/xml;charset=utf-8",
      content: [
        '<?xml version="1.0" encoding="UTF-8"?>',
        `<trackerRecords tracker="${escapeXmlValue(selectedTracker?.label || "")}" location="${escapeXmlValue(formatLocationScope(currentLocation))}" exportedAt="${new Date().toISOString()}">`,
        recordRows,
        "</trackerRecords>",
      ].join("\n"),
    };
  }

  const csvLines = [
    (columns || []).map(escapeCsvValue).join(","),
    ...getTrackerRecordExportRows(records, columns).map((row) => row.map(escapeCsvValue).join(",")),
  ];

  return {
    extension: "csv",
    mimeType: "text/csv;charset=utf-8",
    content: csvLines.join("\r\n"),
  };
}

function downloadTrackerRecords({ records, columns, selectedTracker, currentLocation, format = "csv" }) {
  const exportFile = buildTrackerRecordsExportContent({ records, columns, selectedTracker, currentLocation, format });
  const blob = new Blob([exportFile.content], { type: exportFile.mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const dateStamp = new Date().toISOString().slice(0, 10);

  link.href = url;
  link.download = `${buildSafeExportFileName([
    selectedTracker?.label || "tracker",
    currentLocation?.name || "records",
    dateStamp,
  ])}.${exportFile.extension}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function getStatusToneClass(value) {
  const normalized = String(value || "").toLowerCase();

  if (normalized === "timeline pending") {
    return "bg-error/10 text-error border-error/20";
  }
  if (normalized === "timeline in progress") {
    return "bg-warning/10 text-warning border-warning/20";
  }
  if (normalized === "timeline completed") {
    return "bg-success/10 text-success border-success/20";
  }

  if (/\b(complete|completed|approved|active|fulfilled|success|yes)\b/.test(normalized)) {
    return "bg-success/10 text-success border-success/20";
  }
  if (/(pending|in progress|ongoing|draft|scheduled)/.test(normalized)) {
    return "bg-warning/10 text-warning border-warning/20";
  }
  if (/(cancelled|canceled|rejected|failed|inactive|no)/.test(normalized)) {
    return "bg-error/10 text-error border-error/20";
  }

  return "bg-muted text-muted-foreground border-border";
}

function detectTrackerColumn(columns, matchers) {
  const safeColumns = Array.isArray(columns) ? columns : [];

  return (
    safeColumns.find((columnName) =>
      matchers.some((matcher) => matcher.test(String(columnName || ""))),
    ) || null
  );
}

function getTrackerRecordId(record, index) {
  return record?.trackedEntity || record?.trackedEntityInstance || record?.id || `record-${index + 1}`;
}

async function fetchRelationshipTrackerRecord(trackedEntityId, programId = "") {
  const programParams = programId ? { program: programId } : {};
  const requestCandidates = [
    () =>
      axios.get(`/tracker/trackedEntities/${trackedEntityId}`, {
        params: {
          fields: TRACKER_RELATIONSHIP_RECORD_FIELDS,
          ...programParams,
        },
      }),
    () =>
      axios.get(`/trackedEntityInstances/${trackedEntityId}`, {
        params: {
          fields: TRACKER_RELATIONSHIP_RECORD_FIELDS,
          ...programParams,
        },
      }),
  ];

  for (const request of requestCandidates) {
    try {
      const response = await request();
      if (response?.data) return response.data;
    } catch (error) {
      // Try the next DHIS2 tracker endpoint shape.
    }
  }

  throw new Error("Unable to load the linked tracker record.");
}

function buildRelationshipRecordSummary(record, fallbackId = "", selectedTracker = null, savedColumnConfig = null) {
  const attributes = getTrackerRecordAttributes(record, selectedTracker);
  const attributeValueMap = buildAttributeValueMap(attributes, savedColumnConfig, selectedTracker);
  const detailItems = attributes
    .map((attribute) => ({
      label: attribute?.displayName || attribute?.attribute || "Field",
      value: formatTrackerCellValue(attribute?.displayName || attribute?.attribute, attribute?.value),
    }))
    .filter((item) => item.value && item.value !== "-");
  const metadataItems = (selectedTracker?.program?.programTrackedEntityAttributes || [])
    .slice()
    .sort((left, right) => (left?.sortOrder ?? Number.MAX_SAFE_INTEGER) - (right?.sortOrder ?? Number.MAX_SAFE_INTEGER))
    .map((entry) => {
      const attribute = entry?.trackedEntityAttribute;
      const label = getPreferredTrackerAttributeLabel(attribute, savedColumnConfig);
      const value = formatTrackerCellValue(label, attributeValueMap[label]);

      return label && value !== "-" ? { label, value } : null;
    })
    .filter(Boolean);
  const displayItems = metadataItems.length > 0 ? metadataItems : detailItems;
  const title = detailItems[0]?.value || getTrackerRecordId(record, 0) || fallbackId;

  return {
    id: getTrackerRecordId(record, 0) || fallbackId,
    title,
    detailItems: displayItems.slice(0, 6),
    valueByAttributeId: attributes.reduce((acc, attribute) => {
      const id = attribute?.attribute || attribute?.trackedEntityAttribute;
      if (id) {
        acc[id] = formatTrackerCellValue(attribute?.displayName || id, attribute?.value);
      }
      return acc;
    }, {}),
    valueByLabel: displayItems.reduce((acc, item) => {
      if (item?.label) acc[item.label] = item.value;
      return acc;
    }, { ...attributeValueMap }),
    record,
  };
}

function getRelationshipMetadataColumns(programMetadata) {
  const attributes = Array.isArray(programMetadata?.programTrackedEntityAttributes)
    ? programMetadata.programTrackedEntityAttributes
    : [];
  const displayAttributes = attributes.filter((entry) => entry?.displayInList);
  const orderedAttributes = (displayAttributes.length > 0 ? displayAttributes : attributes)
    .slice()
    .sort((left, right) => (left?.sortOrder ?? Number.MAX_SAFE_INTEGER) - (right?.sortOrder ?? Number.MAX_SAFE_INTEGER));

  return orderedAttributes
    .map((entry) => {
      const attribute = entry?.trackedEntityAttribute || {};
      const id = attribute?.id;
      if (!id) return null;

      return {
        id,
        label: attribute?.displayName || attribute?.shortName || id,
      };
    })
    .filter(Boolean)
    .slice(0, 5);
}

function getTrackerRecordEnrollment(record, programId) {
  const enrollments = Array.isArray(record?.rawRecord?.enrollments)
    ? record.rawRecord.enrollments
    : Array.isArray(record?.enrollments)
      ? record.enrollments
      : [];

  const matchingEnrollment = enrollments.find((enrollment) => enrollment?.program === programId) || enrollments[0];
  return matchingEnrollment?.enrollment || matchingEnrollment?.enrollmentId || null;
}

function getSelectedOption(options, value) {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  return options.find((option) => option.value === value) || { label: String(value), value };
}

function parseCoordinateValue(value) {
  if (!value) {
    return { latitude: "", longitude: "" };
  }

  if (typeof value === "object") {
    return {
      latitude: String(value?.latitude || ""),
      longitude: String(value?.longitude || ""),
    };
  }

  const parts = String(value).split(",").map((part) => part.trim()).filter(Boolean);
  if (parts.length === 2) {
    return { latitude: parts[1], longitude: parts[0] };
  }

  return { latitude: "", longitude: "" };
}

function isFileValue(value) {
  return typeof File !== "undefined" && value instanceof File;
}

function getStoredFileResourceId(value) {
  if (value === undefined || value === null || value === "" || isFileValue(value)) {
    return "";
  }

  if (typeof value === "object") {
    return String(value?.id || value?.uid || value?.value || "").trim();
  }

  return String(value).trim();
}

function getFileResourceLabel(fileResourceId, fileResourceDetails) {
  const details = fileResourceDetails?.[fileResourceId] || {};

  return (
    details?.displayName ||
    details?.name ||
    details?.fileName ||
    details?.filename ||
    details?.id ||
    fileResourceId
  );
}

function getAxiosBasicAuthHeader() {
  const auth = axios?.defaults?.auth;
  if (!auth?.username || !auth?.password || typeof window === "undefined") {
    return "";
  }

  return `Basic ${window.btoa(`${auth.username}:${auth.password}`)}`;
}

function getFileResourceRequestConfig(extraConfig = {}) {
  const authorization = getAxiosBasicAuthHeader();

  return {
    ...extraConfig,
    headers: {
      ...(extraConfig.headers || {}),
      ...(authorization ? { Authorization: authorization } : {}),
    },
  };
}

function buildEventFileDownloadUrl(eventUid, dataElementUid) {
  if (!eventUid || !dataElementUid) return "";

  const baseUrl = String(axios?.defaults?.baseURL || "").replace(/\/$/, "");
  const params = new URLSearchParams({
    eventUid,
    dataElementUid,
  });

  return `${baseUrl}/events/files?${params.toString()}`;
}

function toTrackerFileRequestUrl(url) {
  if (!url) return url;

  if (String(url).startsWith("/")) {
    return url;
  }

  try {
    const parsed = new URL(url);
    const base = String(axios?.defaults?.baseURL || "").replace(/\/$/, "");

    if (base && String(url).startsWith(base)) {
      const path = parsed.pathname.replace(/^\/api/, "") || parsed.pathname;
      return `${path}${parsed.search}`;
    }

    return parsed.href;
  } catch {
    return url;
  }
}

async function fetchTrackerFilePreviewBlob({ fileResourceId, eventFileDownloadUrl }) {
  const urls = [];

  if (eventFileDownloadUrl) {
    urls.push(toTrackerFileRequestUrl(eventFileDownloadUrl));
  }

  if (fileResourceId) {
    urls.push(`/fileResources/${fileResourceId}/data`);
    urls.push(`/fileResources/${fileResourceId}/data?attachment=true`);
  }

  let response = null;
  let lastError = null;

  for (const url of urls) {
    try {
      response = await axios.get(url, getFileResourceRequestConfig({ responseType: "blob" }));
      break;
    } catch (error) {
      lastError = error;
    }
  }

  if (!response) {
    throw lastError || new Error("Document preview failed.");
  }

  const contentType = response.headers["content-type"] || "application/octet-stream";
  const disposition = response?.headers?.["content-disposition"] || "";
  const dispositionFileName =
    disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1] ||
    disposition.match(/filename="?([^"]+)"?/i)?.[1] ||
    "";
  const resolvedFileName = decodeURIComponent(dispositionFileName || "").trim();
  const blob = new Blob([response.data], { type: contentType });

  return {
    blobUrl: URL.createObjectURL(blob),
    contentType,
    fileName: resolvedFileName,
  };
}

function getFileExtension(fileName = "") {
  const match = String(fileName).trim().match(/\.([a-z0-9]+)$/i);
  return match ? match[1].toLowerCase() : "";
}

const DOWNLOAD_ONLY_EXTENSIONS = new Set([
  "doc",
  "docx",
  "dot",
  "dotx",
  "xls",
  "xlsx",
  "xlsm",
  "xlt",
  "xltx",
  "csv",
  "ppt",
  "pptx",
  "pps",
  "ppsx",
  "zip",
  "rar",
  "7z",
  "rtf",
]);

function getTrackerFilePreviewMode(contentType, fileName = "") {
  const type = String(contentType || "")
    .toLowerCase()
    .split(";")[0]
    .trim();
  const ext = getFileExtension(fileName);

  if (type.startsWith("image/")) return "image";
  if (type === "application/pdf" || ext === "pdf") return "iframe";
  if (type === "text/plain" || type === "text/html") return "iframe";

  if (DOWNLOAD_ONLY_EXTENSIONS.has(ext)) return "download";
  if (/word|excel|spreadsheet|powerpoint|officedocument|msword|ms-excel|ms-powerpoint/i.test(type)) {
    return "download";
  }

  if (type === "application/octet-stream" && ext && ext !== "pdf") return "download";

  return "iframe";
}

async function downloadTrackerFileResource({ fileResourceId, eventFileDownloadUrl, fileName }) {
  const { blobUrl, fileName: resolvedName } = await fetchTrackerFilePreviewBlob({
    fileResourceId,
    eventFileDownloadUrl,
  });
  const link = document.createElement("a");

  link.href = blobUrl;
  link.download = fileName || resolvedName || "document";
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
}

const TRACKER_DOCUMENT_VIEWER_HEIGHT = "min(85vh, 900px)";

function TrackerDocumentDownloadPanel({
  title,
  fileName,
  description,
  onDownload,
  onClose,
  isDownloading,
}) {
  const displayName = fileName || title || "Document";

  return (
    <div
      className="flex flex-col items-center justify-center bg-white px-6 py-14 text-center"
      style={{ minHeight: TRACKER_DOCUMENT_VIEWER_HEIGHT }}
    >
      <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Icon name="Download" size={30} />
      </div>
      <h3 className="text-lg font-semibold text-foreground">Download to view</h3>
      <p className="mt-2 max-w-md text-sm text-muted-foreground">{description}</p>
      <p className="mt-4 rounded-lg border border-border bg-muted/30 px-4 py-2 text-sm font-medium text-foreground">
        {displayName}
      </p>
      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        <Button type="button" onClick={onDownload} disabled={isDownloading}>
          <Icon name={isDownloading ? "Loader2" : "Download"} className={isDownloading ? "animate-spin" : ""} size={16} />
          {isDownloading ? "Preparing download…" : "Download file"}
        </Button>
        <Button type="button" variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}

function TrackerDocumentPreviewContent({
  title = "Document",
  fileName = "",
  fileResourceId,
  eventFileDownloadUrl,
  active,
  onClose,
}) {
  const [previewUrl, setPreviewUrl] = useState("");
  const [resolvedFileName, setResolvedFileName] = useState("");
  const [viewMode, setViewMode] = useState("loading");
  const [loading, setLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const blobUrlRef = useRef(null);

  const revokeBlobUrl = () => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  };

  useEffect(() => {
    if (!active || (!fileResourceId && !eventFileDownloadUrl)) {
      revokeBlobUrl();
      setPreviewUrl("");
      setResolvedFileName("");
      setViewMode("loading");
      setLoading(false);
      return undefined;
    }

    let isMounted = true;

    const loadPreview = async () => {
      revokeBlobUrl();
      setPreviewUrl("");
      setResolvedFileName("");
      setViewMode("loading");
      setLoading(true);

      const displayNameHint = fileName || title;
      if (getTrackerFilePreviewMode("", displayNameHint) === "download") {
        if (!isMounted) return;
        setResolvedFileName(displayNameHint);
        setViewMode("download");
        setLoading(false);
        return;
      }

      try {
        const result = await fetchTrackerFilePreviewBlob({
          fileResourceId,
          eventFileDownloadUrl,
        });
        const displayName = result.fileName || fileName || title;
        const mode = getTrackerFilePreviewMode(result.contentType, displayName);

        if (!isMounted) {
          URL.revokeObjectURL(result.blobUrl);
          return;
        }

        blobUrlRef.current = result.blobUrl;
        setResolvedFileName(displayName);

        if (mode === "download") {
          setViewMode("download");
          setPreviewUrl("");
          return;
        }

        setPreviewUrl(result.blobUrl);
        setViewMode(mode);
      } catch (previewError) {
        if (!isMounted) return;
        console.error("Tracker document preview failed:", previewError);
        setViewMode("download");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadPreview();

    return () => {
      isMounted = false;
      revokeBlobUrl();
    };
  }, [active, fileResourceId, eventFileDownloadUrl, fileName, title]);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      if (blobUrlRef.current) {
        const link = document.createElement("a");
        link.href = blobUrlRef.current;
        link.download = resolvedFileName || fileName || title || "document";
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        link.remove();
        return;
      }

      await downloadTrackerFileResource({
        fileResourceId,
        eventFileDownloadUrl,
        fileName: resolvedFileName || fileName || title,
      });
    } catch (downloadError) {
      console.error("Tracker document download failed:", downloadError);
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadDescription =
    viewMode === "download" && !loading
      ? "This file type cannot be previewed in the browser (for example Excel, Word, or PowerPoint). Download it to open on your device."
      : "Preview is not available for this file. You can still download it and open it locally.";

  if (loading) {
    return (
      <div
        className="flex h-full w-full items-center justify-center bg-white text-sm text-muted-foreground"
        style={{ minHeight: TRACKER_DOCUMENT_VIEWER_HEIGHT }}
      >
        <Icon name="Loader2" size={28} className="animate-spin" />
      </div>
    );
  }

  if (viewMode === "download") {
    return (
      <TrackerDocumentDownloadPanel
        title={title}
        fileName={resolvedFileName || fileName}
        description={downloadDescription}
        onDownload={handleDownload}
        onClose={onClose}
        isDownloading={isDownloading}
      />
    );
  }

  if (!previewUrl) {
    return null;
  }

  if (viewMode === "image") {
    return (
      <div className="flex h-full w-full items-center justify-center overflow-auto bg-neutral-950 p-4">
        <img
          src={previewUrl}
          alt={title}
          className="max-h-full max-w-full object-contain"
          style={{ maxHeight: TRACKER_DOCUMENT_VIEWER_HEIGHT }}
        />
      </div>
    );
  }

  return (
    <iframe
      src={previewUrl}
      title={title}
      className="block h-full w-full border-0 bg-white"
      style={{ minHeight: TRACKER_DOCUMENT_VIEWER_HEIGHT, height: TRACKER_DOCUMENT_VIEWER_HEIGHT }}
    />
  );
}

function TrackerDocumentPreviewModal({
  isOpen,
  onClose,
  title,
  fileName,
  fileResourceId,
  eventFileDownloadUrl,
}) {
  useEffect(() => {
    if (!isOpen || typeof document === "undefined") return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <>
      <div className="fixed inset-0 z-[1200] bg-black/70" onClick={onClose} aria-hidden="true" />
      <div className="fixed inset-0 z-[1300] flex items-center justify-center p-3 md:p-6">
        <div
          role="dialog"
          aria-modal="true"
          aria-label={title || "Document preview"}
          className="relative w-full max-w-6xl overflow-hidden rounded-lg bg-white shadow-2xl"
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 z-10 rounded-full bg-black/60 p-2 text-white transition-colors hover:bg-black/80"
            aria-label="Close document preview"
          >
            <Icon name="X" size={18} />
          </button>
          <TrackerDocumentPreviewContent
            title={title}
            fileName={fileName}
            fileResourceId={fileResourceId}
            eventFileDownloadUrl={eventFileDownloadUrl}
            active={isOpen}
            onClose={onClose}
          />
        </div>
      </div>
    </>,
    document.body,
  );
}

function collectRecordDocumentItems(eventRecords, selectedTracker, fileResourceDetails, programStageOptions) {
  const items = [];

  (eventRecords || []).forEach((eventItem) => {
    const stageLabel =
      programStageOptions.find((option) => option.value === eventItem?.programStage)?.label ||
      eventItem?.programStage ||
      "Event";
    const stage =
      programStageOptions.find((option) => option.value === eventItem?.programStage)?.stage || null;
    const fileFields = getProgramStageEventFields(stage).filter((field) => field?.type === "file");
    const dataMap = buildExistingEventDataValueMap(eventItem);

    fileFields.forEach((field) => {
      const fileResourceId = getStoredFileResourceId(dataMap[field.name]);
      if (!fileResourceId) return;

      items.push({
        id: `${eventItem?.event || "event"}:${field.name}`,
        eventUid: eventItem?.event,
        dataElementUid: field.name,
        label: `${stageLabel} — ${field.label}`,
        fileResourceId,
        eventFileDownloadUrl: buildEventFileDownloadUrl(eventItem?.event, field.name),
        fileName: getFileResourceLabel(fileResourceId, fileResourceDetails),
      });
    });
  });

  return items;
}

function TrackerRecordDocumentsPanel({ documents, onOpenDocument, isLoading, error }) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
        <Icon name="Loader2" size={20} className="animate-spin" />
        Loading documents…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
        {error}
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-10 text-center">
        <Icon name="Paperclip" size={28} className="mx-auto text-muted-foreground" />
        <p className="mt-3 text-sm text-muted-foreground">No uploaded documents are linked to this record yet.</p>
      </div>
    );
  }

  return (
    <ul className="divide-y divide-border rounded-lg border border-border bg-background">
      {documents.map((document) => (
        <li key={document.id}>
          <button
            type="button"
            onClick={() =>
              onOpenDocument?.({
                title: document.fileName || document.label,
                fileName: document.fileName,
                fileResourceId: document.fileResourceId,
                eventFileDownloadUrl: document.eventFileDownloadUrl,
              })
            }
            className="flex w-full flex-col gap-1 px-4 py-3 text-left text-sm text-foreground transition-colors hover:bg-muted/40"
          >
            <span className="font-medium text-primary underline-offset-2 hover:underline">{document.label}</span>
            <span className="truncate text-xs text-muted-foreground">{document.fileName}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function buildInitialEventFormValues(fields) {
  return (fields || []).reduce(
    (acc, field) => ({
      ...acc,
      ...(field?.isSectionHeader
        ? {}
        : { [field.name]: field?.type === "coordinate" ? { latitude: "", longitude: "" } : "" }),
    }),
    {},
  );
}

const PLANNED_ACTIVITY_SECTION_RULES = [
  {
    title: "AAP Progress Report",
    matchers: [
      /report\s*date|^date$/i,
      /completed\s+on\s+time/i,
      /^status$/i,
      /expenditure/i,
      /completed\s+within\s+budget/i,
      /support\s+document/i,
    ],
  },
  {
    title: "Gender Support Program Data",
    matchers: [
      /gender\s+support/i,
      /activity\s+been\s+supported/i,
      /evidence\s+of\s+support/i,
      /support\s+giv/i,
    ],
  },
  {
    title: "Nutrition-oriented Interventions",
    matchers: [
      /nutrition/i,
      /where\s+was\s+it\s+published/i,
      /website\s+link/i,
      /upload\s+picture/i,
    ],
  },
  {
    title: "Tree Planting Report",
    matchers: [
      /tree\s+planting/i,
      /reports?\s+on\s+tree/i,
      /upload\s+report/i,
    ],
  },
];

function getPlannedActivitySectionTitle(field) {
  const label = String(field?.label || "").trim();

  return PLANNED_ACTIVITY_SECTION_RULES.find((section) =>
    section.matchers.some((matcher) => matcher.test(label)),
  )?.title || null;
}

function buildPlannedActivitySections(fields) {
  const formFields = (fields || []).filter((field) => !field?.isSectionHeader);
  const matchedSectionTitles = new Set(
    formFields
      .map(getPlannedActivitySectionTitle)
      .filter(Boolean),
  );

  if (matchedSectionTitles.size < 2) {
    return [];
  }

  const sectionsByTitle = PLANNED_ACTIVITY_SECTION_RULES.map((section) => ({
    title: section.title,
    fields: [],
    implicit: false,
  }));
  const fallbackSection = sectionsByTitle[0];

  formFields.forEach((field) => {
    const sectionTitle = getPlannedActivitySectionTitle(field);
    const targetSection =
      sectionsByTitle.find((section) => section.title === sectionTitle) || fallbackSection;

    targetSection.fields.push(field);
  });

  return sectionsByTitle.filter((section) => section.fields.length > 0);
}

function buildEventFieldSections(fields, fallbackTitle) {
  const plannedActivitySections = buildPlannedActivitySections(fields);
  if (plannedActivitySections.length > 0) {
    return plannedActivitySections;
  }

  const sections = [];
  let currentSection = {
    title: fallbackTitle || "Event Details",
    fields: [],
    implicit: true,
  };

  (fields || []).forEach((field) => {
    if (field?.isSectionHeader) {
      if (currentSection.fields.length > 0 || !currentSection.implicit) {
        sections.push(currentSection);
      }

      currentSection = {
        title: field.label,
        fields: [],
        implicit: false,
      };
      return;
    }

    currentSection.fields.push(field);
  });

  if (currentSection.fields.length > 0 || sections.length === 0) {
    sections.push(currentSection);
  }

  return sections;
}

function getEventSectionTitle(section, selectedTracker) {
  const normalizedTitle = String(section?.title || "").trim().toLowerCase();
  const trackerLabel = String(selectedTracker?.label || "").trim();

  if (trackerLabel && (normalizedTitle === "planned activity report" || normalizedTitle === "aap progress report")) {
    return `${trackerLabel} Progress Report`;
  }

  return section?.title || "Event Details";
}

function getEventSectionInnerLabel(section, selectedTracker) {
  const normalizedTitle = String(section?.title || "").trim().toLowerCase();
  const trackerLabel = String(selectedTracker?.label || "").trim();

  if (trackerLabel && normalizedTitle === "aap progress report") {
    return `${trackerLabel} Progress Report`;
  }

  return section?.title || "Event Details";
}

function validateEventForm(fields, formData) {
  const errors = {};

  (fields || []).forEach((field) => {
    if (field?.isSectionHeader) return;

    const rawValue = formData?.[field.name];
    const hasValue =
      field?.type === "coordinate"
        ? Boolean(rawValue?.latitude && rawValue?.longitude)
        : rawValue !== undefined && rawValue !== null && String(rawValue).trim() !== "";

    if (field?.required && !hasValue) {
      errors[field.name] = `${field.label} is required.`;
      return;
    }

    const phoneValidationMessage = getPhoneNumberValidationMessage(field, rawValue);
    if (phoneValidationMessage) {
      errors[field.name] = phoneValidationMessage;
    }
  });

  return errors;
}

function normalizeEventDataValue(field, value) {
  if (value === undefined || value === null || value === "") return "";

  if (field?.type === "coordinate") {
    const coordinateValue = parseCoordinateValue(value);
    if (!coordinateValue.latitude || !coordinateValue.longitude) return "";
    return `[${coordinateValue.longitude},${coordinateValue.latitude}]`;
  }

  return String(value);
}

function formatEventPrintValue(field, value, fileResourceDetails = {}) {
  if (value === undefined || value === null || value === "") return "-";

  if (field?.type === "coordinate") {
    const coordinateValue = parseCoordinateValue(value);
    if (!coordinateValue.latitude && !coordinateValue.longitude) return "-";
    return [coordinateValue.latitude, coordinateValue.longitude].filter(Boolean).join(", ");
  }

  if (field?.type === "file") {
    const storedFileResourceId = getStoredFileResourceId(value);
    if (isFileValue(value)) return value.name;
    if (storedFileResourceId) return getFileResourceLabel(storedFileResourceId, fileResourceDetails);
    return "-";
  }

  if (field?.type === "select") {
    const option = getSelectedOption(field?.options || [], value);
    return option?.label || String(value);
  }

  return String(value);
}

function extractImportedTrackerEventId(responseData) {
  const objectReports =
    responseData?.bundleReport?.typeReportMap?.EVENT?.objectReports ||
    responseData?.bundleReport?.typeReports?.EVENT?.objectReports ||
    responseData?.response?.importSummaries ||
    responseData?.importSummaries ||
    [];

  const firstReport = Array.isArray(objectReports) ? objectReports[0] : null;

  return (
    firstReport?.uid ||
    firstReport?.reference ||
    firstReport?.href?.split("/").filter(Boolean).pop() ||
    null
  );
}

function mergeTrackerEvents(existingEvents, incomingEvents) {
  const eventMap = new Map();

  (Array.isArray(existingEvents) ? existingEvents : []).forEach((eventItem) => {
    const key = eventItem?.event;
    if (key) {
      eventMap.set(key, eventItem);
    }
  });

  (Array.isArray(incomingEvents) ? incomingEvents : []).forEach((eventItem) => {
    const key = eventItem?.event;
    if (key) {
      eventMap.set(key, {
        ...(eventMap.get(key) || {}),
        ...eventItem,
      });
    }
  });

  return Array.from(eventMap.values()).sort((left, right) => {
    const leftDate = left?.occurredAt || left?.eventDate || left?.scheduledAt || "";
    const rightDate = right?.occurredAt || right?.eventDate || right?.scheduledAt || "";
    return String(leftDate).localeCompare(String(rightDate));
  });
}

function getTrackerEventOwnerId(eventItem) {
  return (
    eventItem?.trackedEntity ||
    eventItem?.trackedEntityInstance ||
    eventItem?.trackedEntityId ||
    eventItem?.enrollment ||
    eventItem?.enrollmentId ||
    ""
  );
}

function eventBelongsToRecord(eventItem, trackedEntityId, enrollmentId) {
  const ownerId = getTrackerEventOwnerId(eventItem);

  if (!ownerId) {
    return true;
  }

  return ownerId === trackedEntityId || ownerId === enrollmentId;
}

async function buildEventDataValues(fields, formData, existingDataValueMap = {}) {
  const dataValues = [];

  for (const field of fields || []) {
    if (field?.isSectionHeader) continue;
    let value = formData?.[field.name];

    if (field?.type === "file" && isFileValue(value)) {
      value = await uploadTrackerFileResource(value);
    }

    const normalizedValue = normalizeEventDataValue(field, value);
    if (normalizedValue) {
      dataValues.push({
        dataElement: field.name,
        value: normalizedValue,
      });
    } else if (field?.type === "file" && existingDataValueMap[field.name]) {
      dataValues.push({
        dataElement: field.name,
        value: "",
      });
    }
  }

  return dataValues;
}

function buildExistingEventDataValueMap(eventItem) {
  return (eventItem?.dataValues || []).reduce((acc, dataValue) => {
    if (!dataValue?.dataElement) return acc;
    return {
      ...acc,
      [dataValue.dataElement]: dataValue?.value || "",
    };
  }, {});
}

function buildTrackerAttachmentRows(eventRecords, programStageOptions, fileResourceDetails = {}) {
  const stageById = new Map(
    (programStageOptions || []).map((stageOption) => [stageOption.value, stageOption]),
  );
  const rows = [];

  (Array.isArray(eventRecords) ? eventRecords : []).forEach((eventItem) => {
    const stageOption = stageById.get(eventItem?.programStage);
    const fileFieldById = new Map(
      getProgramStageEventFields(stageOption?.stage)
        .filter((field) => field?.type === "file")
        .map((field) => [field.name, field]),
    );

    (Array.isArray(eventItem?.dataValues) ? eventItem.dataValues : []).forEach((dataValue) => {
      const fileField = fileFieldById.get(dataValue?.dataElement);
      const fileResourceId = getStoredFileResourceId(dataValue?.value);
      if (!fileField || !fileResourceId) return;

      rows.push({
        id: `${eventItem?.event || "event"}:${dataValue.dataElement}:${fileResourceId}`,
        fileResourceId,
        fileName: getFileResourceLabel(fileResourceId, fileResourceDetails),
        fieldLabel: fileField?.label || dataValue?.displayName || dataValue?.dataElement || "Attachment",
        stageLabel: stageOption?.label || eventItem?.programStage || "Timeline event",
        eventDate: eventItem?.occurredAt || eventItem?.eventDate || eventItem?.scheduledAt || "",
        eventId: eventItem?.event || "",
        dataElementId: dataValue?.dataElement || "",
      });
    });
  });

  return rows;
}

function getNumberInputStep(field) {
  if (field?.type !== "number") return undefined;

  const valueType = String(field?.valueType || "").toUpperCase();
  return valueType === "NUMBER" || valueType === "PERCENTAGE" ? "any" : "1";
}

function TrackerEventFormField({
  field,
  value,
  error,
  onChange,
  fileResourceDetails,
  onOpenDocumentPreview,
  eventFileDownloadUrl,
}) {
  const wrapperClassName = field?.span === "full" ? "md:col-span-2" : "";
  const valueType = String(field?.valueType || "").toUpperCase();
  const fileInputRef = useRef(null);
  const isBooleanField =
    valueType === "BOOLEAN" ||
    valueType === "TRUE_ONLY" ||
    (
      field?.type === "select" &&
      (field?.options || []).length === 2 &&
      (field?.options || []).every((option) => ["true", "false"].includes(String(option?.value).toLowerCase()))
    );

  if (isBooleanField) {
    const booleanOptions = [
      { label: "Yes", value: "true" },
      { label: "No", value: "false" },
    ];

    return (
      <div className={wrapperClassName}>
        <label className="mb-2 block text-sm font-medium text-foreground">
          {field.label}
          {field.required && <span className="ml-1 text-destructive">*</span>}
        </label>
        <div className="inline-flex rounded-lg border border-border bg-background p-1">
          {booleanOptions.map((option) => {
            const isSelected = String(value) === option.value;

            return (
              <button
                key={option.value}
                type="button"
                onClick={() => onChange(field.name, option.value)}
                className={`min-w-20 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  isSelected
                    ? "bg-[#16325a] text-white shadow-sm"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
        {field?.description && !error && <p className="mt-1 text-sm text-muted-foreground">{field.description}</p>}
        {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
      </div>
    );
  }

  if (field?.type === "select") {
    const options = field?.options || [];

    return (
      <div className={wrapperClassName}>
        <Select
          label={field.label}
          required={field.required}
          options={options}
          value={getSelectedOption(options, value)}
          onChange={(option) => onChange(field.name, option?.value || "")}
          placeholder={field.placeholder || `Select ${field.label.toLowerCase()}`}
          error={error}
          description={field?.description}
          searchable
        />
      </div>
    );
  }

  if (field?.type === "textarea") {
    return (
      <div className={wrapperClassName}>
        <label className="mb-2 block text-sm font-medium text-foreground">
          {field.label}
          {field.required && <span className="ml-1 text-destructive">*</span>}
        </label>
        <textarea
          value={value || ""}
          onChange={(event) => onChange(field.name, event?.target?.value || "")}
          placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
          rows={4}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-[#16325a] focus-visible:shadow-[0_0_0_3px_rgba(22,50,90,0.12)]"
        />
        {field?.description && !error && <p className="mt-1 text-sm text-muted-foreground">{field.description}</p>}
        {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
      </div>
    );
  }

  if (field?.type === "coordinate") {
    const coordinateValue = parseCoordinateValue(value);

    return (
      <div className={wrapperClassName}>
        <label className="mb-2 block text-sm font-medium text-foreground">
          {field.label}
          {field.required && <span className="ml-1 text-destructive">*</span>}
        </label>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Input
            type="number"
            label="Latitude"
            value={coordinateValue.latitude}
            onChange={(event) =>
              onChange(field.name, { ...coordinateValue, latitude: event?.target?.value || "" })
            }
            step="any"
          />
          <Input
            type="number"
            label="Longitude"
            value={coordinateValue.longitude}
            onChange={(event) =>
              onChange(field.name, { ...coordinateValue, longitude: event?.target?.value || "" })
            }
            step="any"
          />
        </div>
        <div className="mt-3">
          <CoordinateMapPicker
            value={coordinateValue}
            onChange={(nextCoordinate) => onChange(field.name, nextCoordinate)}
            label={field.label}
          />
        </div>
        {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
      </div>
    );
  }

  if (field?.type === "file") {
    const storedFileResourceId = getStoredFileResourceId(value);
    const fileLabel = isFileValue(value)
      ? value.name
      : storedFileResourceId
        ? getFileResourceLabel(storedFileResourceId, fileResourceDetails)
        : "";
    const hasFile = Boolean(fileLabel);
    const handleClearFile = (clickEvent) => {
      clickEvent?.preventDefault?.();
      clickEvent?.stopPropagation?.();
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      onChange(field.name, "");
    };
    const handleOpenPreview = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!storedFileResourceId) return;
      onOpenDocumentPreview?.({
        title: fileLabel || field.label,
        fileName: fileLabel || field.label,
        fileResourceId: storedFileResourceId,
        eventFileDownloadUrl,
      });
    };

    return (
      <div className={wrapperClassName}>
        <label className="mb-2 block text-sm font-medium text-foreground">
          {field.label}
          {field.required && <span className="ml-1 text-destructive">*</span>}
        </label>
        <input
          ref={fileInputRef}
          type="file"
          accept={String(field?.valueType || "").toUpperCase() === "IMAGE" ? "image/*" : undefined}
          onChange={(event) => onChange(field.name, event?.target?.files?.[0] || "")}
          className="sr-only"
        />
        <div
          className={`flex min-h-12 w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm ${
            hasFile
              ? "border-emerald-200 bg-emerald-50 text-emerald-900"
              : "border-input bg-background text-muted-foreground"
          }`}
        >
          <div className="flex min-w-0 items-center gap-2">
            <Icon name={hasFile ? "Paperclip" : "Upload"} size={16} className="shrink-0" />
            {storedFileResourceId ? (
              <button
                type="button"
                onClick={handleOpenPreview}
                className="min-w-0 truncate text-left font-medium text-emerald-800 underline-offset-2 hover:underline"
                title={`View ${fileLabel}`}
              >
                {fileLabel}
              </button>
            ) : (
              <span className={`min-w-0 truncate ${hasFile ? "font-medium" : ""}`}>
                {hasFile ? fileLabel : "No file uploaded"}
              </span>
            )}
          </div>
          {hasFile ? (
            <button
              type="button"
              onClick={handleClearFile}
              className="shrink-0 rounded-md border border-destructive/30 bg-background px-3 py-1.5 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10"
            >
              Delete
            </button>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="shrink-0 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted"
            >
              Upload file
            </button>
          )}
        </div>
        {storedFileResourceId && (
          <p className="mt-1 text-xs text-muted-foreground">
            Click the file name to view it, or delete it to upload a replacement.
          </p>
        )}
        {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
      </div>
    );
  }

  return (
    <div className={wrapperClassName}>
      <Input
        type={field?.type || "text"}
        label={field.label}
        required={field.required}
        value={value || ""}
        onChange={(event) => onChange(field.name, event?.target?.value || "")}
        placeholder={field.placeholder}
        min={field?.type === "number" ? "0" : undefined}
        step={getNumberInputStep(field)}
        error={error}
        description={field?.description}
      />
    </div>
  );
}

function NewTimelineEventModal({
  isOpen,
  stageOptions,
  stageId,
  eventDate,
  onStageChange,
  onDateChange,
  onClose,
  onConfirm,
}) {
  if (!isOpen) return null;

  const selectedStageOption = stageOptions.find((option) => option.value === stageId) || stageOptions[0] || null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[1200] bg-black/50 backdrop-blur-[2px]" onClick={onClose} />
      <div className="fixed inset-0 z-[1300] flex items-end justify-center p-0 sm:items-center sm:p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Create timeline event"
          className="max-h-[92vh] w-full overflow-y-auto rounded-t-2xl border border-border bg-card p-4 shadow-xl sm:max-w-lg sm:rounded-2xl sm:p-5"
        >
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground">New Timeline Event</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Select the event box to add and set its report date.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Close"
            >
              <Icon name="X" size={18} />
            </button>
          </div>

          <div className="space-y-4">
            <Select
              label="Event"
              options={stageOptions}
              value={selectedStageOption}
              onChange={(option) => onStageChange(option?.value || "")}
              placeholder="Select event"
            />
            <Input
              type="date"
              label="Report Date"
              value={eventDate}
              onChange={(event) => onDateChange(event?.target?.value || "")}
              required
            />
          </div>

          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button type="button" onClick={onConfirm} disabled={!selectedStageOption || !eventDate} className="w-full sm:w-auto">
              <Icon name="ArrowRight" size={16} />
              Continue
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

function extractImportReference(responseData) {
  return (
    responseData?.response?.importSummaries?.[0]?.reference ||
    responseData?.response?.importSummaries?.[0]?.href?.split("/").filter(Boolean).pop() ||
    responseData?.response?.reference ||
    responseData?.reference ||
    ""
  );
}

function getRelationshipConstraintProgramId(constraint) {
  return constraint?.program?.id || constraint?.program?.program || constraint?.program || "";
}

function getRelationshipConstraintTetId(constraint) {
  return (
    constraint?.trackedEntityType?.id ||
    constraint?.trackedEntityType?.trackedEntityType ||
    constraint?.trackedEntityType ||
    ""
  );
}

function getRelationshipConstraintLabel(constraint) {
  return (
    constraint?.program?.displayName ||
    constraint?.program?.name ||
    constraint?.trackedEntityType?.displayName ||
    constraint?.trackedEntityType?.name ||
    "Related tracker"
  );
}

function constraintMatchesTracker(constraint, programId, trackedEntityTypeId) {
  const constraintProgramId = getRelationshipConstraintProgramId(constraint);
  const constraintTetId = getRelationshipConstraintTetId(constraint);

  return Boolean(
    (programId && constraintProgramId === programId) ||
    (trackedEntityTypeId && constraintTetId === trackedEntityTypeId),
  );
}

function buildRelationshipOptions(relationshipTypes, selectedTracker) {
  const programId = selectedTracker?.value || selectedTracker?.program?.id || "";
  const trackedEntityTypeId = selectedTracker?.program?.trackedEntityType?.id || "";

  return (Array.isArray(relationshipTypes) ? relationshipTypes : [])
    .flatMap((relationshipType) => {
      const fromConstraint = relationshipType?.fromConstraint || {};
      const toConstraint = relationshipType?.toConstraint || {};
      const currentIsFrom = constraintMatchesTracker(fromConstraint, programId, trackedEntityTypeId);
      const currentIsTo = constraintMatchesTracker(toConstraint, programId, trackedEntityTypeId);
      const options = [];

      if (currentIsFrom) {
        options.push({
          id: `${relationshipType.id}:from`,
          relationshipTypeId: relationshipType.id,
          label: relationshipType.displayName || relationshipType.name || relationshipType.id,
          direction: "from",
          currentSide: "from",
          targetSide: "to",
          targetProgramId: getRelationshipConstraintProgramId(toConstraint),
          targetTrackedEntityTypeId: getRelationshipConstraintTetId(toConstraint),
          targetLabel: getRelationshipConstraintLabel(toConstraint),
          relationshipType,
        });
      }

      if (currentIsTo) {
        options.push({
          id: `${relationshipType.id}:to`,
          relationshipTypeId: relationshipType.id,
          label: relationshipType.displayName || relationshipType.name || relationshipType.id,
          direction: "to",
          currentSide: "to",
          targetSide: "from",
          targetProgramId: getRelationshipConstraintProgramId(fromConstraint),
          targetTrackedEntityTypeId: getRelationshipConstraintTetId(fromConstraint),
          targetLabel: getRelationshipConstraintLabel(fromConstraint),
          relationshipType,
        });
      }

      return options;
    })
    .filter((option) => option.relationshipTypeId)
    .sort((left, right) => left.label.localeCompare(right.label));
}

function flattenRegistrationSections(sections) {
  return (sections || []).flatMap((section) => section?.fields || []);
}

const RELATIONSHIP_PREFILL_LABEL_ALIASES = {
  "meeting decision type": [
    "meeting type",
    "dpat meeting type",
  ],
  "meeting reference number": [
    "minute file number",
    "invitation letter ref number",
    "invitation letter reference number",
  ],
};

function buildRelationshipSourceValueMaps(record) {
  const sourceRecord = record?.rawRecord || record;
  const attributes = getTrackerRecordAttributes(sourceRecord);
  const byId = {};
  const byLabel = {};

  attributes.forEach((attribute) => {
    const id = attribute?.attribute || attribute?.trackedEntityAttribute;
    const label = attribute?.displayName || attribute?.name || "";
    const value = attribute?.value;

    if (id && value !== undefined && value !== null && String(value).trim() !== "") {
      byId[id] = value;
    }
    if (label && value !== undefined && value !== null && String(value).trim() !== "") {
      byLabel[normalizeTrackerText(label)] = value;
      const strippedLabel = String(label).replace(/^\s*(TEI|DPAT)\s*\|\s*/i, "").trim();
      if (strippedLabel) {
        byLabel[normalizeTrackerText(strippedLabel)] = value;
      }
    }
  });

  Object.entries(record?.attributeValueMap || {}).forEach(([label, value]) => {
    if (!label || value === undefined || value === null || String(value).trim() === "" || value === "-") return;
    const normalizedLabel = normalizeTrackerText(label);
    if (!byLabel[normalizedLabel]) {
      byLabel[normalizedLabel] = value;
    }
  });

  return { byId, byLabel };
}

function normalizeRelationshipPrefillValue(field, value) {
  if (value === undefined || value === null) return "";

  if (field?.type === "date") {
    return String(value).slice(0, 10);
  }

  if (field?.type === "select") {
    const options = Array.isArray(field?.options) ? field.options : [];
    const normalizedValue = normalizeTrackerText(value);
    const matchedOption = options.find((option) => {
      return (
        String(option?.value || "") === String(value) ||
        normalizeTrackerText(option?.label) === normalizedValue ||
        normalizeTrackerText(option?.value) === normalizedValue
      );
    });

    return matchedOption?.value || value;
  }

  return value;
}

function getRelationshipSourceValue(field, sourceValueMaps) {
  if (!field?.name) return "";

  if (sourceValueMaps?.byId?.[field.name] !== undefined) {
    return normalizeRelationshipPrefillValue(field, sourceValueMaps.byId[field.name]);
  }

  const normalizedLabel = normalizeTrackerText(field?.label);
  if (normalizedLabel && sourceValueMaps?.byLabel?.[normalizedLabel] !== undefined) {
    return normalizeRelationshipPrefillValue(field, sourceValueMaps.byLabel[normalizedLabel]);
  }

  const aliasLabels = RELATIONSHIP_PREFILL_LABEL_ALIASES[normalizedLabel] || [];
  for (const aliasLabel of aliasLabels) {
    const normalizedAlias = normalizeTrackerText(aliasLabel);
    if (normalizedAlias && sourceValueMaps?.byLabel?.[normalizedAlias] !== undefined) {
      return normalizeRelationshipPrefillValue(field, sourceValueMaps.byLabel[normalizedAlias]);
    }
  }

  return "";
}

function buildRelationshipInitialValues(sections, relationshipOption, selectedTracker, sourceRecord) {
  const sourceValueMaps = buildRelationshipSourceValueMaps(sourceRecord);

  return flattenRegistrationSections(sections).reduce((acc, field) => {
    const label = String(field?.label || "").toLowerCase();
    const sourceValue = getRelationshipSourceValue(field, sourceValueMaps);

    if (field?.type === "coordinate") {
      acc[field.name] = { latitude: "", longitude: "" };
      return acc;
    }

    if (sourceValue !== "") {
      acc[field.name] = sourceValue;
      return acc;
    }

    if (/\bmeeting\s+type\b/.test(label)) {
      acc[field.name] = relationshipOption?.targetLabel || relationshipOption?.label || selectedTracker?.label || "";
      return acc;
    }

    acc[field.name] = "";
    return acc;
  }, {});
}

function isRelationshipGeneratedField(field) {
  const label = String(field?.label || "").toLowerCase();
  return Boolean(field?.generated || /\bmeeting\s+type\b/.test(label));
}

function getSelectedRelationshipOption(options, value) {
  if (value === undefined || value === null || value === "") return null;
  return options.find((option) => option.value === value) || { label: String(value), value };
}

function RelationshipRegistrationField({ field, value, error, onChange }) {
  const wrapperClassName = field?.span === "full" ? "sm:col-span-2" : "";
  const isGenerated = isRelationshipGeneratedField(field);

  if (field?.type === "select") {
    const options = Array.isArray(field?.options) ? field.options : [];

    return (
      <div className={wrapperClassName}>
        <Select
          label={field.label}
          required={field.required}
          options={options}
          value={getSelectedRelationshipOption(options, value)}
          onChange={(option) => onChange(field.name, option?.value || "")}
          placeholder={field.placeholder || `Select ${String(field.label || "value").toLowerCase()}`}
          error={error}
          description={field?.description}
          disabled={isGenerated}
          searchable
        />
      </div>
    );
  }

  if (field?.type === "textarea") {
    return (
      <div className={wrapperClassName}>
        <label className="mb-2 block text-sm font-medium text-foreground">
          {field.label}
          {field.required && <span className="ml-1 text-destructive">*</span>}
        </label>
        <textarea
          value={value || ""}
          onChange={(event) => onChange(field.name, event?.target?.value || "")}
          placeholder={field.placeholder || `Enter ${String(field.label || "details").toLowerCase()}`}
          rows={4}
          readOnly={isGenerated}
          disabled={isGenerated}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-[#16325a] focus-visible:shadow-[0_0_0_3px_rgba(22,50,90,0.12)] disabled:cursor-not-allowed disabled:opacity-60"
        />
        {field?.description && !error && <p className="mt-1 text-sm text-muted-foreground">{field.description}</p>}
        {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
      </div>
    );
  }

  if (field?.type === "coordinate") {
    const coordinateValue = typeof value === "object" && value ? value : { latitude: "", longitude: "" };

    return (
      <div className={wrapperClassName}>
        <label className="mb-2 block text-sm font-medium text-foreground">
          {field.label}
          {field.required && <span className="ml-1 text-destructive">*</span>}
        </label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input
            type="number"
            label="Latitude"
            value={coordinateValue.latitude || ""}
            onChange={(event) => onChange(field.name, { ...coordinateValue, latitude: event?.target?.value || "" })}
            step="any"
            disabled={isGenerated}
          />
          <Input
            type="number"
            label="Longitude"
            value={coordinateValue.longitude || ""}
            onChange={(event) => onChange(field.name, { ...coordinateValue, longitude: event?.target?.value || "" })}
            step="any"
            disabled={isGenerated}
          />
        </div>
        <div className="mt-3">
          <CoordinateMapPicker
            value={coordinateValue}
            onChange={(nextCoordinate) => onChange(field.name, nextCoordinate)}
            disabled={isGenerated}
            label={field.label}
          />
        </div>
        {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
      </div>
    );
  }

  if (field?.type === "file") {
    return (
      <div className={wrapperClassName}>
        <label className="mb-2 block text-sm font-medium text-foreground">
          {field.label}
          {field.required && <span className="ml-1 text-destructive">*</span>}
        </label>
        <input
          type="file"
          accept={String(field?.valueType || "").toUpperCase() === "IMAGE" ? "image/*" : undefined}
          onChange={(event) => onChange(field.name, event?.target?.files?.[0] || "")}
          disabled={isGenerated}
          className="block w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground file:mr-3 file:rounded-md file:border-0 file:bg-muted file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-foreground hover:file:bg-muted/80 disabled:cursor-not-allowed disabled:opacity-60"
        />
        {value?.name && <p className="mt-1 text-sm text-muted-foreground">{value.name}</p>}
        {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
      </div>
    );
  }

  return (
    <div className={wrapperClassName}>
      <Input
        type={field?.type || "text"}
        label={field.label}
        required={field.required}
        value={value || ""}
        onChange={(event) => onChange(field.name, event?.target?.value || "")}
        placeholder={field.placeholder}
        min={field?.type === "number" ? "0" : undefined}
        step={getNumberInputStep(field)}
        error={error}
        description={field?.description}
        readOnly={isGenerated}
        disabled={isGenerated}
      />
    </div>
  );
}

function validateRelationshipRegistrationForm(sections, formData) {
  const validationErrors = {};

  flattenRegistrationSections(sections).forEach((field) => {
    if (isRelationshipGeneratedField(field)) return;

    const value = formData?.[field.name];
    const hasValue = (() => {
      if (value === undefined || value === null || value === "") return false;
      if (field?.type === "file") return isFileValue(value) || Boolean(getStoredFileResourceId(value)) || String(value).trim() !== "";
      if (field?.type === "coordinate") return Boolean(value?.latitude || value?.longitude);
      if (typeof value === "object") return Object.values(value).some((item) => String(item || "").trim() !== "");
      return String(value).trim() !== "";
    })();

    if (field?.required && !hasValue) {
      validationErrors[field.name] = `${field.label} is required`;
      return;
    }

    const phoneValidationMessage = getPhoneNumberValidationMessage(field, value);
    if (phoneValidationMessage) {
      validationErrors[field.name] = phoneValidationMessage;
    }
  });

  return validationErrors;
}

function getRelationshipFieldLabelById(sections) {
  return new Map(
    flattenRegistrationSections(sections)
      .filter((field) => field?.name)
      .map((field) => [field.name, field.label || field.name]),
  );
}

function collectRelationshipConflictMessages(responseData) {
  const messages = [];
  const pushMessage = (message) => {
    const text = String(message || "").trim();
    if (text && !messages.includes(text)) messages.push(text);
  };

  const visitSummary = (summary) => {
    (summary?.conflicts || []).forEach((conflict) => pushMessage(conflict?.value || conflict?.message));
    (summary?.importSummaries || []).forEach(visitSummary);
    if (summary?.enrollments) visitSummary(summary.enrollments);
    if (summary?.events) visitSummary(summary.events);
  };

  visitSummary(responseData?.response || responseData);
  return messages;
}

function formatRelationshipRegistrationError(error, sections) {
  const conflictMessages = collectRelationshipConflictMessages(error?.response?.data);
  const fieldLabelById = getRelationshipFieldLabelById(sections);
  const duplicateMessage = conflictMessages.find((message) =>
    /Non-unique attribute value/i.test(message),
  );

  if (duplicateMessage) {
    const match = duplicateMessage.match(/Non-unique attribute value ['`]([^'`]+)['`] for attribute ([a-zA-Z0-9]{11})/i);
    const duplicateValue = match?.[1] || "";
    const attributeId = match?.[2] || "";
    const fieldLabel = fieldLabelById.get(attributeId) || attributeId || "this field";

    return duplicateValue
      ? `${fieldLabel} "${duplicateValue}" already exists. Enter a unique ${fieldLabel} before registering this relationship.`
      : `${fieldLabel} already exists. Enter a unique ${fieldLabel} before registering this relationship.`;
  }

  if (conflictMessages.length > 0) {
    return conflictMessages[0];
  }

  return error?.message || "Unable to register this relationship. Please try again.";
}

async function uploadRelationshipFileFields(formData, sections) {
  const nextFormData = { ...formData };
  const fileFields = flattenRegistrationSections(sections).filter((field) => field?.type === "file");

  for (const field of fileFields) {
    const value = nextFormData[field.name];
    if (typeof File !== "undefined" && value instanceof File) {
      nextFormData[field.name] = await uploadTrackerFileResource(value);
    }
  }

  return nextFormData;
}

async function submitTrackerRelationshipRegistration({
  relationshipOption,
  targetProgramMetadata,
  formData,
  sections,
  currentOrgUnitId,
  currentTrackedEntityId,
}) {
  const targetProgramId = targetProgramMetadata?.id || relationshipOption?.targetProgramId;
  const targetTrackedEntityTypeId =
    targetProgramMetadata?.trackedEntityType?.id || relationshipOption?.targetTrackedEntityTypeId;
  const submittedDate = new Date().toISOString().slice(0, 10);
  const attributes = buildTrackerRegistrationAttributes(formData, sections);
  const geometry = buildTrackerRegistrationGeometry(formData, sections);

  if (!relationshipOption?.relationshipTypeId) {
    throw new Error("Choose a relationship type before registering.");
  }

  if (!targetProgramId || !targetTrackedEntityTypeId) {
    throw new Error("The selected relationship is missing target tracker metadata.");
  }

  if (!currentOrgUnitId || !currentTrackedEntityId) {
    throw new Error("This record is missing the required tracker context.");
  }

  if (attributes.length === 0 && !geometry) {
    throw new Error("Enter at least one field before registering the related record.");
  }

  const generatedPayload = await addGeneratedTrackerAttributeValues(
    {
      trackedEntities: [
        {
          attributes,
          enrollments: [
            {
              attributes: attributes.map((attribute) => ({ ...attribute })),
            },
          ],
        },
      ],
    },
    targetProgramMetadata,
  );
  const generatedTrackedEntity = generatedPayload?.trackedEntities?.[0] || {};
  const trackedEntityAttributes = Array.isArray(generatedTrackedEntity?.attributes)
    ? generatedTrackedEntity.attributes
    : attributes;
  const enrollmentAttributes = Array.isArray(generatedTrackedEntity?.enrollments?.[0]?.attributes)
    ? generatedTrackedEntity.enrollments[0].attributes
    : trackedEntityAttributes.map((attribute) => ({ ...attribute }));

  const teiResponse = await axios.post("/trackedEntityInstances", {
    trackedEntityType: targetTrackedEntityTypeId,
    orgUnit: currentOrgUnitId,
    attributes: trackedEntityAttributes,
    ...(geometry ? { geometry } : {}),
  });
  const relatedTrackedEntityId = extractImportReference(teiResponse?.data);

  if (!relatedTrackedEntityId) {
    throw new Error("The related record was created, but no tracked entity id was returned.");
  }

  const enrollmentResponse = await axios.post("/enrollments", {
    trackedEntityInstance: relatedTrackedEntityId,
    program: targetProgramId,
    status: "ACTIVE",
    enrollmentDate: submittedDate,
    incidentDate: submittedDate,
    orgUnit: currentOrgUnitId,
    attributes: enrollmentAttributes,
  });

  const fromTrackedEntityId =
    relationshipOption.currentSide === "from" ? currentTrackedEntityId : relatedTrackedEntityId;
  const toTrackedEntityId =
    relationshipOption.currentSide === "from" ? relatedTrackedEntityId : currentTrackedEntityId;

  const relationshipResponse = await axios.post("/relationships", {
    relationshipType: relationshipOption.relationshipTypeId,
    from: {
      trackedEntityInstance: {
        trackedEntityInstance: fromTrackedEntityId,
      },
    },
    to: {
      trackedEntityInstance: {
        trackedEntityInstance: toTrackedEntityId,
      },
    },
  });

  return {
    trackedEntityId: relatedTrackedEntityId,
    enrollmentId: extractImportReference(enrollmentResponse?.data),
    relationshipId: extractImportReference(relationshipResponse?.data),
  };
}

function normalizeTrackerDateValue(columnName, rawValue) {
  const value = String(rawValue || "").trim();
  if (!value) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  if (/^\d{4}-\d{2}$/.test(value)) return `${value}-01`;
  if (/^\d{4}$/.test(value) && /year/i.test(String(columnName || ""))) return `${value}-01-01`;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;

  return parsed.toISOString().slice(0, 10);
}

function getTrackerSortValue(columnName, rawValue) {
  const normalizedDateValue = normalizeTrackerDateValue(columnName, rawValue);
  if (normalizedDateValue && looksLikeDateColumn(columnName)) {
    return normalizedDateValue;
  }

  const compactValue = String(rawValue ?? "").replace(/,/g, "").trim();
  if (compactValue && /^-?\d+(\.\d+)?$/.test(compactValue)) {
    return Number(compactValue);
  }

  return formatTrackerCellValue(columnName, rawValue).toLowerCase();
}

function buildTrackerFilterOptions(records, columnName, allLabel, seededValues = []) {
  if (!columnName) {
    return [{ label: allLabel, value: "all" }];
  }

  const uniqueValues = Array.from(
    new Set(
      [...seededValues, ...records
        .map((record) => formatTrackerCellValue(columnName, record?.attributeValueMap?.[columnName]))
        .filter((value) => value && value !== "-")],
    ),
  ).sort((a, b) => a.localeCompare(b));

  return [
    { label: allLabel, value: "all" },
    ...uniqueValues.map((value) => ({
      label: value,
      value,
    })),
  ];
}

function TrackerRecordsTable({
  records,
  visibleColumns,
  allColumns,
  sortConfig,
  onSortChange,
  onOpenRecord,
  onEdit,
  emptyTitle,
  emptyDescription,
}) {
  const [expandedRecordId, setExpandedRecordId] = useState(null);
  const [openMenuRecordId, setOpenMenuRecordId] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    setExpandedRecordId(null);
    setOpenMenuRecordId(null);
  }, [records, visibleColumns]);

  useEffect(() => {
    if (!openMenuRecordId) return undefined;

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuRecordId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenuRecordId]);

  if (!records.length) {
    return (
      <div className="card">
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Icon name="ClipboardList" size={48} className="text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">{emptyTitle}</h3>
          <p className="text-sm text-muted-foreground">{emptyDescription}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="space-y-3 md:hidden">
        {records.map((record, rowIndex) => {
          const primaryColumn =
            visibleColumns.find((columnName) => columnName !== TRACKER_REGISTRATION_STATUS_COLUMN) ||
            visibleColumns[0];
          const primaryValue = primaryColumn
            ? formatTrackerCellValue(primaryColumn, record?.attributeValueMap?.[primaryColumn])
            : record.recordId;
          const summaryColumns = visibleColumns
            .filter((columnName) => columnName !== primaryColumn)
            .slice(0, 4);
          const detailColumns = allColumns.filter((columnName) => {
            const value = formatTrackerCellValue(columnName, record?.attributeValueMap?.[columnName]);
            return value !== "-";
          });
          const isExpanded = expandedRecordId === record.recordId;
          const isMenuOpen = openMenuRecordId === record.recordId;

          return (
            <article
              key={`${record.recordId}:mobile`}
              className="rounded-xl border border-border bg-background p-3 shadow-sm"
            >
              <button
                type="button"
                onClick={() => onRowClick(record)}
                className="flex w-full items-start justify-between gap-3 text-left"
              >
                <div className="min-w-0">
                  <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                    {primaryColumn || "Record"}
                  </p>
                  <p className="mt-1 break-words text-base font-semibold text-primary">
                    {primaryValue}
                  </p>
                </div>
                <Icon name="ChevronRight" size={18} className="mt-1 shrink-0 text-muted-foreground" />
              </button>

              {summaryColumns.length > 0 && (
                <dl className="mt-3 grid gap-2">
                  {summaryColumns.map((columnName) => {
                    const value = formatTrackerCellValue(columnName, record?.attributeValueMap?.[columnName]);
                    if (value === "-") return null;

                    return (
                      <div key={`${record.recordId}:mobile:${columnName}`} className="rounded-lg bg-muted/30 px-3 py-2">
                        <dt className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                          {columnName}
                        </dt>
                        <dd className="mt-1 break-words text-sm text-foreground">
                          {looksLikeStatusColumn(columnName) ? (
                            <span
                              className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${getStatusToneClass(
                                value,
                              )}`}
                            >
                              {value}
                            </span>
                          ) : (
                            value
                          )}
                        </dd>
                      </div>
                    );
                  })}
                </dl>
              )}

              {isExpanded && detailColumns.length > 0 && (
                <div className="mt-3 grid gap-2 border-t border-border pt-3">
                  {detailColumns.slice(0, 12).map((columnName) => (
                    <div key={`${record.recordId}:mobile-detail:${columnName}`} className="rounded-lg border border-border bg-card px-3 py-2">
                      <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                        {columnName}
                      </p>
                      <p className="mt-1 break-words text-sm text-foreground">
                        {formatTrackerCellValue(columnName, record?.attributeValueMap?.[columnName])}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-3 flex items-center justify-between gap-2 border-t border-border pt-3">
                <button
                  type="button"
                  onClick={() => setExpandedRecordId(isExpanded ? null : record.recordId)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={15} />
                  {isExpanded ? "Less" : "Details"}
                </button>

                <div className="relative" ref={isMenuOpen ? menuRef : null}>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setOpenMenuRecordId(isMenuOpen ? null : record.recordId)}
                  >
                    <Icon name="MoreHorizontal" size={16} />
                    Actions
                  </Button>

                  {isMenuOpen && (
                    <div className={`absolute right-0 z-50 min-w-[140px] rounded-lg border border-border bg-background shadow-md ${rowIndex >= records.length - 1 ? "bottom-10" : "top-10"}`}>
                      <button
                        type="button"
                        className="flex w-full items-center gap-2 rounded-t-lg px-3 py-2 text-sm text-foreground transition-colors hover:bg-muted"
                        onClick={() => {
                          onOpenRecord?.(record);
                          setOpenMenuRecordId(null);
                        }}
                      >
                        <Icon name="Eye" size={15} />
                        View
                      </button>
                      <button
                        type="button"
                        className="flex w-full items-center gap-2 rounded-b-lg px-3 py-2 text-sm text-foreground transition-colors hover:bg-muted"
                        onClick={() => {
                          onEdit?.(record.rawRecord);
                          setOpenMenuRecordId(null);
                        }}
                      >
                        <Icon name="Pencil" size={15} />
                        Edit
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <div className="hidden overflow-x-auto md:block">
        <table className="w-full min-w-[960px]">
          <thead>
            <tr className="border-b-2 border-border">
              <th className="text-left py-4 px-4 text-sm font-semibold text-foreground w-8" />
              {visibleColumns.map((columnName) => {
                const isSortedColumn = sortConfig?.key === columnName;

                return (
                  <th
                    key={columnName}
                    className="text-left py-4 px-4 text-sm font-semibold text-foreground cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => onSortChange(columnName)}
                    title={columnName}
                  >
                    <div className="flex items-center gap-2">
                      <span className="truncate">{columnName}</span>
                      <Icon
                        name="ArrowUpDown"
                        size={14}
                        className={isSortedColumn ? "text-foreground" : "text-muted-foreground"}
                      />
                    </div>
                  </th>
                );
              })}
              <th className="text-center py-4 px-4 text-sm font-semibold text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {records.map((record, rowIndex) => {
              const isExpanded = expandedRecordId === record.recordId;
              const isNearBottom = rowIndex >= records.length - 1;
              const detailColumns = allColumns.filter((columnName) => {
                const value = formatTrackerCellValue(columnName, record?.attributeValueMap?.[columnName]);
                return value !== "-";
              });

              return (
                <React.Fragment key={record.recordId}>
                  <tr
                    className="cursor-pointer border-b border-border hover:bg-muted/30 transition-colors"
                    onClick={() => onOpenRecord?.(record)}
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onOpenRecord?.(record);
                      }
                    }}
                  >
                    <td className="py-4 px-4">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setExpandedRecordId(isExpanded ? null : record.recordId);
                        }}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        aria-label={isExpanded ? "Collapse row" : "Expand row"}
                      >
                        <Icon name={isExpanded ? "ChevronDown" : "ChevronRight"} size={16} />
                      </button>
                    </td>

                    {visibleColumns.map((columnName, columnIndex) => {
                      const rawValue = record?.attributeValueMap?.[columnName];
                      const textValue = formatTrackerCellValue(columnName, rawValue);
                      const isStatusCell = textValue !== "-" && looksLikeStatusColumn(columnName);
                      const isPrimaryColumn = columnIndex === 0;

                      return (
                        <td
                          key={`${record.recordId}:${columnName}`}
                          className={`py-4 px-4 align-top ${isPrimaryColumn ? "text-foreground" : "text-muted-foreground"}`}
                          title={textValue === "-" ? undefined : textValue}
                        >
                          {isStatusCell ? (
                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusToneClass(
                                textValue,
                              )}`}
                            >
                              {textValue}
                            </span>
                          ) : isPrimaryColumn ? (
                            <span className="font-medium text-primary">
                              {textValue}
                            </span>
                          ) : (
                            <span className="block max-w-[220px] truncate">{textValue}</span>
                          )}
                        </td>
                      );
                    })}

                    <td className="py-4 px-4">
                      <div className="relative flex justify-center" ref={openMenuRecordId === record.recordId ? menuRef : null}>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(event) => {
                            event.stopPropagation();
                            setOpenMenuRecordId(openMenuRecordId === record.recordId ? null : record.recordId);
                          }}
                          className="h-8 w-8 p-0"
                        >
                          <Icon name="MoreHorizontal" size={16} />
                        </Button>

                        {openMenuRecordId === record.recordId && (
                          <div className={`absolute right-0 z-50 min-w-[130px] rounded-lg border border-border bg-background shadow-md ${isNearBottom ? "bottom-9" : "top-9"}`}>
                            <button
                              type="button"
                              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors rounded-t-lg"
                              onClick={(event) => {
                                event.stopPropagation();
                                onOpenRecord?.(record);
                                setOpenMenuRecordId(null);
                              }}
                            >
                              <Icon name="Eye" size={15} />
                              View
                            </button>
                            <button
                              type="button"
                              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors rounded-b-lg"
                              onClick={(event) => {
                                event.stopPropagation();
                                onEdit?.(record.rawRecord);
                                setOpenMenuRecordId(null);
                              }}
                            >
                              <Icon name="Pencil" size={15} />
                              Edit
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>

                  {isExpanded && (
                    <tr className="border-b border-border bg-muted/20">
                      <td colSpan={visibleColumns.length + 2} className="py-4 px-4">
                        <div className="pl-8 space-y-4">
                          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                            <span className="inline-flex items-center rounded-full bg-background px-2.5 py-1 border border-border">
                              Record ID: {record.recordId}
                            </span>
                            <span className="inline-flex items-center rounded-full bg-background px-2.5 py-1 border border-border">
                              {detailColumns.length} populated field{detailColumns.length === 1 ? "" : "s"}
                            </span>
                          </div>

                          {detailColumns.length > 0 ? (
                            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                              {detailColumns.map((columnName) => (
                                <div key={`${record.recordId}:detail:${columnName}`} className="rounded-lg border border-border bg-background p-3">
                                  <p className="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
                                    {columnName}
                                  </p>
                                  <p className="mt-2 text-sm text-foreground break-words">
                                    {formatTrackerCellValue(columnName, record?.attributeValueMap?.[columnName])}
                                  </p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground">No additional populated details are available for this record.</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TrackerRecordDetailWorkspace({
  record,
  selectedTracker,
  currentLocation,
  allColumns,
  trackerOptions = [],
  onBack,
  onEdit,
  onViewingTrackerChange,
}) {
  const [activeTab, setActiveTab] = useState("overview");
  const [eventRecords, setEventRecords] = useState([]);
  const [isEventsLoading, setIsEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState("");
  const [selectedStageId, setSelectedStageId] = useState("");
  const [eventDate, setEventDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [selectedTimelineItemId, setSelectedTimelineItemId] = useState("");
  const [isNewEventModalOpen, setIsNewEventModalOpen] = useState(false);
  const [draftStageId, setDraftStageId] = useState("");
  const [draftEventDate, setDraftEventDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [eventFormData, setEventFormData] = useState({});
  const [eventFormErrors, setEventFormErrors] = useState({});
  const [fileResourceDetails, setFileResourceDetails] = useState({});
  const [isSubmittingEvent, setIsSubmittingEvent] = useState(false);
  const [eventSubmitMessage, setEventSubmitMessage] = useState("");
  const [relationshipTypes, setRelationshipTypes] = useState([]);
  const [isRelationshipTypesLoading, setIsRelationshipTypesLoading] = useState(false);
  const [hasLoadedRelationshipTypes, setHasLoadedRelationshipTypes] = useState(false);
  const [relationshipError, setRelationshipError] = useState("");
  const [relationshipSubmitMessage, setRelationshipSubmitMessage] = useState("");
  const [isRelationshipAddOpen, setIsRelationshipAddOpen] = useState(false);
  const [selectedRelationshipOptionId, setSelectedRelationshipOptionId] = useState("");
  const [targetRelationshipProgramMetadata, setTargetRelationshipProgramMetadata] = useState(null);
  const [targetRelationshipFieldIds, setTargetRelationshipFieldIds] = useState(null);
  const [isTargetRelationshipMetadataLoading, setIsTargetRelationshipMetadataLoading] = useState(false);
  const [relationshipFormData, setRelationshipFormData] = useState({});
  const [relationshipFormErrors, setRelationshipFormErrors] = useState({});
  const [isSubmittingRelationship, setIsSubmittingRelationship] = useState(false);
  const [createdRelationshipRows, setCreatedRelationshipRows] = useState([]);
  const [linkedRelationshipContext, setLinkedRelationshipContext] = useState(null);
  const [loadingLinkedRelationshipId, setLoadingLinkedRelationshipId] = useState("");
  const [linkedRelationshipError, setLinkedRelationshipError] = useState("");
  const [relationshipRecordSummaries, setRelationshipRecordSummaries] = useState({});
  const [relationshipProgramMetadataById, setRelationshipProgramMetadataById] = useState({});
  const [documentPreview, setDocumentPreview] = useState(null);
  const detailColumns = useMemo(
    () =>
      (allColumns || []).filter((columnName) => {
        const value = formatTrackerCellValue(columnName, record?.attributeValueMap?.[columnName]);
        return value !== "-";
      }),
    [allColumns, record],
  );
  const registeringUnitTitle = formatTrackerCellValue(
    "Registering unit",
    record?.attributeValueMap?.["Registering unit"],
  );
  const primaryTitle =
    (registeringUnitTitle !== "-"
      ? registeringUnitTitle
      : detailColumns
          .filter((columnName) => columnName !== TRACKER_REGISTRATION_STATUS_COLUMN)
          .map((columnName) => formatTrackerCellValue(columnName, record?.attributeValueMap?.[columnName]))
          .find((value) => value && value !== "-")) ||
    record?.recordId ||
    "Tracker Record";
  const allProgramStageOptions = useMemo(
    () => getProgramStageOptions(selectedTracker?.program),
    [selectedTracker?.program],
  );
  const programStageOptions = useMemo(
    () => allProgramStageOptions.filter(
      (stageOption) => !isHiddenTimelineStageOption(stageOption, selectedTracker),
    ),
    [allProgramStageOptions, selectedTracker],
  );
  const hiddenProgramStageIds = useMemo(
    () =>
      new Set(
        allProgramStageOptions
          .filter((stageOption) => isHiddenTimelineStageOption(stageOption, selectedTracker))
          .map((stageOption) => stageOption.value)
          .filter(Boolean),
      ),
    [allProgramStageOptions, selectedTracker],
  );
  const relationshipOptions = useMemo(
    () => buildRelationshipOptions(relationshipTypes, selectedTracker),
    [relationshipTypes, selectedTracker],
  );
  const hasStoredRelationships = Boolean(record?.rawRecord?.relationships?.length);
  const supportsRelationships = relationshipOptions.length > 0 || hasStoredRelationships;
  const tabs = useMemo(
    () => [
      { id: "overview", label: "Overview", icon: "PanelTop" },
      ...(programStageOptions.length > 0
        ? [{ id: "timeline", label: "Timeline", icon: "Clock3" }]
        : []),
      ...(supportsRelationships
        ? [{ id: "relationship", label: "Relationship", icon: "Network" }]
        : []),
      { id: "attachments", label: "Attachments", icon: "Paperclip" },
    ],
    [programStageOptions.length, supportsRelationships],
  );

  useEffect(() => {
    if (tabs.some((tab) => tab.id === activeTab)) return;
    setActiveTab("overview");
  }, [activeTab, tabs]);
  const attachmentRows = useMemo(
    () => buildTrackerAttachmentRows(eventRecords, allProgramStageOptions, fileResourceDetails),
    [allProgramStageOptions, eventRecords, fileResourceDetails],
  );
  const timelineItems = useMemo(
    () =>
      eventRecords
        .filter((timelineEvent) => !hiddenProgramStageIds.has(timelineEvent?.programStage))
        .map((timelineEvent) => {
          const stageLabel =
            programStageOptions.find((option) => option.value === timelineEvent?.programStage)?.label ||
            timelineEvent?.programStage ||
            "Event";
          const displayDate = timelineEvent?.occurredAt || timelineEvent?.eventDate || timelineEvent?.scheduledAt || "";

          return {
            id: timelineEvent?.event,
            event: timelineEvent,
            stageLabel,
            displayDate,
            status: timelineEvent?.status || "OPEN",
          };
        }),
    [eventRecords, hiddenProgramStageIds, programStageOptions],
  );
  const selectedTimelineEvent =
    timelineItems.find((item) => item.id === selectedTimelineItemId)?.event || null;
  const isDraftTimelineEvent = selectedTimelineItemId === "draft";
  const selectedStageOption =
    programStageOptions.find((option) => option.value === selectedStageId) || programStageOptions[0] || null;
  const selectedProgramStage = selectedStageOption?.stage || null;
  const eventFields = useMemo(
    () => getProgramStageEventFields(selectedProgramStage),
    [selectedProgramStage],
  );
  const eventFieldSections = useMemo(
    () => buildEventFieldSections(eventFields, selectedProgramStage?.displayName || selectedStageOption?.label),
    [eventFields, selectedProgramStage?.displayName, selectedStageOption?.label],
  );
  const printableEventSections = useMemo(
    () =>
      eventFieldSections.map((section) => ({
        ...section,
        title: getEventSectionTitle(section, selectedTracker),
      })),
    [eventFieldSections, selectedTracker],
  );
  const trackedEntityId = record?.recordId || record?.rawRecord?.trackedEntity || record?.rawRecord?.trackedEntityInstance;
  const enrollmentId = getTrackerRecordEnrollment(record, selectedTracker?.value);
  const currentOrgUnitId = currentLocation?.orgUnitId || record?.rawRecord?.orgUnit || null;
  const relationshipRows = useMemo(() => {
    const relationships = Array.isArray(record?.rawRecord?.relationships)
      ? record.rawRecord.relationships
      : [];

    const embeddedRows = relationships.map((relationship, index) => {
      const fromId =
        relationship?.from?.trackedEntity?.trackedEntity ||
        relationship?.from?.trackedEntityInstance?.trackedEntityInstance ||
        relationship?.from?.event?.event ||
        relationship?.from?.enrollment?.enrollment ||
        "";
      const toId =
        relationship?.to?.trackedEntity?.trackedEntity ||
        relationship?.to?.trackedEntityInstance?.trackedEntityInstance ||
        relationship?.to?.event?.event ||
        relationship?.to?.enrollment?.enrollment ||
        "";
      const linkedId = fromId === trackedEntityId ? toId : fromId || toId;
      const direction = fromId === trackedEntityId ? "Outgoing" : toId === trackedEntityId ? "Incoming" : "Linked";

      return {
        id: relationship?.relationship || `${record?.recordId || "relationship"}:${index}`,
        type:
          relationship?.relationshipTypeName ||
          relationship?.relationshipName ||
          relationship?.relationshipType?.displayName ||
          relationship?.relationshipType ||
          "Relationship",
        direction,
        linkedId: linkedId || "Linked record",
        linkedTrackedEntityId: linkedId,
        relationshipName: relationship?.relationshipName,
        relationshipTypeName: relationship?.relationshipTypeName,
        relationshipType: relationship?.relationshipType,
        status: relationship?.status || "",
      };
    });

    return [...createdRelationshipRows, ...embeddedRows];
  }, [createdRelationshipRows, record, trackedEntityId]);
  const selectedRelationshipOption = useMemo(
    () => relationshipOptions.find((option) => option.id === selectedRelationshipOptionId) || null,
    [relationshipOptions, selectedRelationshipOptionId],
  );
  const targetRelationshipSections = useMemo(
    () => buildRegistrationSectionsFromProgram(targetRelationshipProgramMetadata),
    [targetRelationshipProgramMetadata],
  );
  const visibleTargetRelationshipSections = useMemo(
    () => {
      const allowedFieldIds = Array.isArray(targetRelationshipFieldIds)
        ? new Set(targetRelationshipFieldIds)
        : null;

      return targetRelationshipSections
        .map((section) => ({
          ...section,
          fields: (section.fields || []).filter((field) => {
            const isSelectedRelationshipField = !allowedFieldIds || allowedFieldIds.has(field.name);
            return isSelectedRelationshipField && isRegistrationFieldVisible(field, relationshipFormData);
          }),
        }))
        .filter((section) => section.fields.length > 0);
    },
    [relationshipFormData, targetRelationshipFieldIds, targetRelationshipSections],
  );
  const relationshipTableColumns = useMemo(() => {
    const metadataColumns = relationshipRows
      .flatMap((relationship) => {
        const metadata =
          relationship?.targetProgramMetadata ||
          relationshipProgramMetadataById[relationship?.targetProgramId];
        return getRelationshipMetadataColumns(metadata);
      })
      .filter(Boolean);
    const metadataColumnById = new Map();

    metadataColumns.forEach((column) => {
      if (!metadataColumnById.has(column.id)) {
        metadataColumnById.set(column.id, column);
      }
    });

    if (metadataColumnById.size > 0) {
      return Array.from(metadataColumnById.values()).slice(0, 5);
    }

    const seenLabels = new Set();
    const columns = [];

    relationshipRows.forEach((relationship) => {
      const linkedId = relationship?.linkedTrackedEntityId || relationship?.linkedId;
      const detailItems = relationshipRecordSummaries[linkedId]?.detailItems || [];

      detailItems.forEach((item) => {
        if (!item?.label || seenLabels.has(item.label)) return;
        seenLabels.add(item.label);
        columns.push({ id: item.label, label: item.label, valueSource: "label" });
      });
    });

    return columns.slice(0, 5);
  }, [relationshipProgramMetadataById, relationshipRecordSummaries, relationshipRows]);

  useEffect(() => {
    const selectedStageIsVisible = programStageOptions.some((option) => option.value === selectedStageId);
    const draftStageIsVisible = programStageOptions.some((option) => option.value === draftStageId);

    if ((!selectedStageId || !selectedStageIsVisible) && programStageOptions[0]?.value) {
      setSelectedStageId(programStageOptions[0].value);
    }
    if ((!draftStageId || !draftStageIsVisible) && programStageOptions[0]?.value) {
      setDraftStageId(programStageOptions[0].value);
    }
  }, [draftStageId, programStageOptions, selectedStageId]);

  useEffect(() => {
    if (!selectedTimelineItemId || selectedTimelineItemId === "draft") return;
    if (timelineItems.some((item) => item.id === selectedTimelineItemId)) return;

    setSelectedTimelineItemId(timelineItems[0]?.id || "");
  }, [selectedTimelineItemId, timelineItems]);

  useEffect(() => {
    if (activeTab !== "relationship" || relationshipRows.length === 0) return undefined;

    const rowsToLoad = relationshipRows.filter((relationship) => {
      const linkedId = relationship?.linkedTrackedEntityId || relationship?.linkedId;
      return linkedId && linkedId !== "Linked record" && !relationshipRecordSummaries[linkedId];
    });

    if (rowsToLoad.length === 0) return undefined;

    let isMounted = true;

    Promise.allSettled(
      rowsToLoad.map((relationship) => {
        const linkedId = relationship?.linkedTrackedEntityId || relationship?.linkedId;
        const relationshipTracker =
          relationship?.trackerContext ||
          resolveTrackerOptionFromRelationshipName(relationship, trackerOptions, selectedTracker?.value);

        return fetchRelationshipTrackerRecord(linkedId, relationshipTracker?.value).then((linkedRecord) => {
          const linkedTracker =
            relationshipTracker ||
            resolveTrackerOptionFromLinkedRecord(linkedRecord, trackerOptions, selectedTracker?.value) ||
            resolveTrackerOptionFromRelationshipName(relationship, trackerOptions, selectedTracker?.value);

          return {
            linkedId,
            summary: buildRelationshipRecordSummary(linkedRecord, linkedId, linkedTracker),
            targetProgramId: linkedTracker?.value,
            targetProgramMetadata: linkedTracker?.program,
          };
        });
      }),
    ).then((results) => {
      if (!isMounted) return;

      setRelationshipRecordSummaries((prev) => {
        const next = { ...prev };
        results.forEach((result) => {
          if (result.status === "fulfilled" && result.value?.linkedId) {
            next[result.value.linkedId] = result.value.summary;
          }
        });
        return next;
      });
      setRelationshipProgramMetadataById((prev) => {
        let hasChanges = false;
        const next = { ...prev };

        results.forEach((result) => {
          if (
            result.status === "fulfilled" &&
            result.value?.targetProgramId &&
            result.value?.targetProgramMetadata &&
            !next[result.value.targetProgramId]
          ) {
            next[result.value.targetProgramId] = result.value.targetProgramMetadata;
            hasChanges = true;
          }
        });

        return hasChanges ? next : prev;
      });
    });

    return () => {
      isMounted = false;
    };
  }, [activeTab, relationshipRecordSummaries, relationshipRows, selectedTracker?.value, trackerOptions]);

  useEffect(() => {
    if (selectedTimelineEvent) return;
    setEventFormData(buildInitialEventFormValues(eventFields));
    setEventFormErrors({});
  }, [eventFields, selectedStageId, selectedTimelineEvent]);

  useEffect(() => {
    if (!selectedTimelineEvent) return;

    setSelectedStageId(selectedTimelineEvent?.programStage || "");
    setEventDate(
      (selectedTimelineEvent?.occurredAt || selectedTimelineEvent?.eventDate || selectedTimelineEvent?.scheduledAt || "")
        .slice(0, 10),
    );
    setEventFormData(
      (selectedTimelineEvent?.dataValues || []).reduce((acc, dataValue) => ({
        ...acc,
        [dataValue?.dataElement]: dataValue?.value || "",
      }), {}),
    );
    setEventFormErrors({});
    setEventSubmitMessage("");
  }, [selectedTimelineEvent]);

  useEffect(() => {
    const fileResourceIds = eventFields
      .filter((field) => field?.type === "file")
      .map((field) => getStoredFileResourceId(eventFormData?.[field.name]))
      .filter(Boolean)
      .filter((fileResourceId) => !fileResourceDetails[fileResourceId]);

    if (fileResourceIds.length === 0) return undefined;

    let isMounted = true;

    Promise.allSettled(
      fileResourceIds.map((fileResourceId) =>
        axios
          .get(`/fileResources/${fileResourceId}`, getFileResourceRequestConfig())
          .then((response) => ({ fileResourceId, details: response?.data || { id: fileResourceId } })),
      ),
    ).then((results) => {
      if (!isMounted) return;

      setFileResourceDetails((prev) => {
        const next = { ...prev };

        results.forEach((result, index) => {
          const fallbackId = fileResourceIds[index];
          if (result.status === "fulfilled") {
            next[result.value.fileResourceId] = result.value.details;
          } else if (fallbackId) {
            next[fallbackId] = { id: fallbackId };
          }
        });

        return next;
      });
    });

    return () => {
      isMounted = false;
    };
  }, [eventFields, eventFormData, fileResourceDetails]);

  useEffect(() => {
    const fileResourceIds = attachmentRows
      .map((attachment) => attachment.fileResourceId)
      .filter(Boolean)
      .filter((fileResourceId) => !fileResourceDetails[fileResourceId]);

    if (fileResourceIds.length === 0) return undefined;

    let isMounted = true;

    Promise.allSettled(
      fileResourceIds.map((fileResourceId) =>
        axios
          .get(`/fileResources/${fileResourceId}`, getFileResourceRequestConfig())
          .then((response) => ({ fileResourceId, details: response?.data || { id: fileResourceId } })),
      ),
    ).then((results) => {
      if (!isMounted) return;

      setFileResourceDetails((prev) => {
        const next = { ...prev };

        results.forEach((result, index) => {
          const fallbackId = fileResourceIds[index];
          if (result.status === "fulfilled") {
            next[result.value.fileResourceId] = result.value.details;
          } else if (fallbackId) {
            next[fallbackId] = { id: fallbackId };
          }
        });

        return next;
      });
    });

    return () => {
      isMounted = false;
    };
  }, [attachmentRows, fileResourceDetails]);

  const loadRecordEvents = async () => {
    if (!selectedTracker?.value || !trackedEntityId) {
      setEventRecords([]);
      return;
    }

    setIsEventsLoading(true);
    setEventsError("");

    try {
      const eventFields =
        "event,eventDate,occurredAt,scheduledAt,status,programStage,program,orgUnit,trackedEntity,trackedEntityInstance,enrollment,dataValues[dataElement,displayName,value]";
      const requestCandidates = [
        {
          program: selectedTracker.value,
          trackedEntity: trackedEntityId,
          pageSize: 100,
          order: "occurredAt:desc",
          fields: eventFields,
        },
        enrollmentId
          ? {
              program: selectedTracker.value,
              enrollment: enrollmentId,
              pageSize: 100,
              order: "occurredAt:desc",
              fields: eventFields,
            }
          : null,
        currentOrgUnitId
          ? {
              program: selectedTracker.value,
              orgUnit: currentOrgUnitId,
              pageSize: 500,
              order: "occurredAt:desc",
              fields: eventFields,
            }
          : null,
      ].filter(Boolean);

      const responses = await Promise.allSettled(
        requestCandidates.map((params) => axios.get("/tracker/events", { params })),
      );
      const embeddedEnrollmentEvents = (record?.rawRecord?.enrollments || [])
        .filter((enrollment) => !enrollmentId || enrollment?.enrollment === enrollmentId)
        .flatMap((enrollment) =>
          (Array.isArray(enrollment?.events) ? enrollment.events : []).map((eventItem) => ({
            ...eventItem,
            enrollment: eventItem?.enrollment || enrollment?.enrollment,
            trackedEntity: eventItem?.trackedEntity || trackedEntityId,
          })),
        );
      const nextEvents = responses
        .filter((result) => result.status === "fulfilled")
        .flatMap((result) => {
          const events = result?.value?.data?.events;
          return Array.isArray(events) ? events : [];
        })
        .concat(embeddedEnrollmentEvents)
        .filter((eventItem) => eventBelongsToRecord(eventItem, trackedEntityId, enrollmentId));

      setEventRecords((prev) => {
        const mergedEvents = mergeTrackerEvents(prev, nextEvents);
        if (!selectedTimelineItemId && mergedEvents[0]?.event) {
          setSelectedTimelineItemId(mergedEvents[0].event);
        }
        return mergedEvents;
      });

      if (nextEvents.length === 0 && responses.every((result) => result.status === "rejected")) {
        throw responses[0]?.reason || new Error("All event requests failed");
      }
    } catch (error) {
      console.error("Failed to load tracker record events:", error);
      setEventRecords([]);
      setEventsError("Unable to load events for this record.");
    } finally {
      setIsEventsLoading(false);
    }
  };

  const handleOpenNewEventModal = () => {
    setDraftStageId(selectedStageId || programStageOptions[0]?.value || "");
    setDraftEventDate(new Date().toISOString().slice(0, 10));
    setIsNewEventModalOpen(true);
  };

  const handleConfirmNewEvent = () => {
    setSelectedStageId(draftStageId || programStageOptions[0]?.value || "");
    setEventDate(draftEventDate || new Date().toISOString().slice(0, 10));
    setEventFormData(buildInitialEventFormValues(getProgramStageEventFields(
      programStageOptions.find((option) => option.value === draftStageId)?.stage || programStageOptions[0]?.stage,
    )));
    setEventFormErrors({});
    setEventSubmitMessage("");
    setSelectedTimelineItemId("draft");
    setIsNewEventModalOpen(false);
  };

  const recordDocuments = useMemo(
    () => collectRecordDocumentItems(eventRecords, selectedTracker, fileResourceDetails, programStageOptions),
    [eventRecords, selectedTracker, fileResourceDetails, programStageOptions],
  );

  useEffect(() => {
    const fileResourceIds = recordDocuments
      .map((document) => document.fileResourceId)
      .filter(Boolean)
      .filter((fileResourceId) => !fileResourceDetails[fileResourceId]);

    if (fileResourceIds.length === 0) return undefined;

    let isMounted = true;

    Promise.allSettled(
      fileResourceIds.map((fileResourceId) =>
        axios
          .get(`/fileResources/${fileResourceId}`, getFileResourceRequestConfig())
          .then((response) => ({ fileResourceId, details: response?.data || { id: fileResourceId } })),
      ),
    ).then((results) => {
      if (!isMounted) return;

      setFileResourceDetails((prev) => {
        const next = { ...prev };

        results.forEach((result, index) => {
          const fallbackId = fileResourceIds[index];
          if (result.status === "fulfilled") {
            next[result.value.fileResourceId] = result.value.details;
          } else if (fallbackId) {
            next[fallbackId] = { id: fallbackId };
          }
        });

        return next;
      });
    });

    return () => {
      isMounted = false;
    };
  }, [recordDocuments, fileResourceDetails]);

  useEffect(() => {
    if (activeTab === "timeline" || activeTab === "attachments") {
      loadRecordEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedTracker?.value, trackedEntityId]);

  const loadRelationshipTypeOptions = useCallback(async ({ force = false } = {}) => {
    if (isRelationshipTypesLoading) return;
    if (!force && hasLoadedRelationshipTypes) return;

    const controller = new AbortController();
    let timeoutId;

    setIsRelationshipTypesLoading(true);
    setHasLoadedRelationshipTypes(false);
    setRelationshipError("");

    try {
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = window.setTimeout(() => {
          controller.abort();
          reject(new Error("Relationship options took too long to load. Please try again."));
        }, 15000);
      });
      const requestPromise = axios.get("/relationshipTypes", {
        signal: controller.signal,
        timeout: 15000,
        params: {
          paging: false,
          fields: "id,name,displayName,fromConstraint,toConstraint",
        },
      });
      const response = await Promise.race([requestPromise, timeoutPromise]);
      const loadedRelationshipTypes =
        response?.data?.relationshipTypes ||
        response?.data?.relationshipType ||
        response?.data?.items ||
        [];

      setRelationshipTypes(Array.isArray(loadedRelationshipTypes) ? loadedRelationshipTypes : []);
    } catch (error) {
      console.error("Failed to load tracker relationship types:", error);
      setRelationshipError(error?.message || "Unable to load relationship options for this tracker.");
      setRelationshipTypes([]);
    } finally {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      setIsRelationshipTypesLoading(false);
      setHasLoadedRelationshipTypes(true);
    }
  }, [hasLoadedRelationshipTypes, isRelationshipTypesLoading]);

  useEffect(() => {
    loadRelationshipTypeOptions();
  }, [loadRelationshipTypeOptions]);

  useEffect(() => {
    if (!selectedRelationshipOptionId) return;
    if (relationshipOptions.some((option) => option.id === selectedRelationshipOptionId)) return;

    setSelectedRelationshipOptionId("");
    setTargetRelationshipProgramMetadata(null);
    setTargetRelationshipFieldIds(null);
    setRelationshipFormData({});
    setRelationshipFormErrors({});
  }, [relationshipOptions, selectedRelationshipOptionId]);

  useEffect(() => {
    if (!selectedRelationshipOption?.targetProgramId) {
      setTargetRelationshipProgramMetadata(null);
      setTargetRelationshipFieldIds(null);
      setRelationshipFormData({});
      setRelationshipFormErrors({});
      return undefined;
    }

    let isMounted = true;

    const loadTargetProgramMetadata = async () => {
      setIsTargetRelationshipMetadataLoading(true);
      setRelationshipError("");
      setTargetRelationshipProgramMetadata(null);
      setTargetRelationshipFieldIds(null);

      try {
        const [metadataResponse, queryResponse] = await Promise.all([
          axios.get(`/programs/${selectedRelationshipOption.targetProgramId}`, {
            params: {
              fields: PROGRAM_METADATA_FIELDS,
            },
          }),
          currentOrgUnitId
            ? axios.get("/trackedEntityInstances/query.json", {
                params: {
                  ou: currentOrgUnitId,
                  ouMode: "SELECTED",
                  order: "created:desc",
                  program: selectedRelationshipOption.targetProgramId,
                  pageSize: 1,
                  page: 1,
                  totalPages: false,
                },
              })
            : Promise.resolve({ data: null }),
        ]);

        const selectedFieldIds = (queryResponse?.data?.headers || [])
          .map((header) => header?.name)
          .filter(Boolean)
          .filter((headerName) => !TRACKER_QUERY_SYSTEM_HEADERS.has(String(headerName).toLowerCase()));

        if (isMounted) {
          setTargetRelationshipProgramMetadata(metadataResponse?.data || null);
          setTargetRelationshipFieldIds(selectedFieldIds.length > 0 ? selectedFieldIds : null);
        }
      } catch (error) {
        console.error("Failed to load target relationship tracker metadata:", error);
        if (isMounted) {
          setRelationshipError("Unable to load the registration form for this relationship.");
          setTargetRelationshipProgramMetadata(null);
          setTargetRelationshipFieldIds(null);
        }
      } finally {
        if (isMounted) {
          setIsTargetRelationshipMetadataLoading(false);
        }
      }
    };

    loadTargetProgramMetadata();

    return () => {
      isMounted = false;
    };
  }, [currentOrgUnitId, selectedRelationshipOption]);

  useEffect(() => {
    if (!selectedRelationshipOption || targetRelationshipSections.length === 0) {
      return;
    }

    setRelationshipFormData(buildRelationshipInitialValues(
      targetRelationshipSections,
      selectedRelationshipOption,
      selectedTracker,
      record,
    ));
    setRelationshipFormErrors({});
  }, [record, selectedRelationshipOption, selectedTracker, targetRelationshipSections]);

  const handleEventFieldChange = (fieldName, value) => {
    setEventFormData((prev) => ({ ...prev, [fieldName]: value }));
    setEventFormErrors((prev) => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const handleSubmitEvent = async (event, statusOverride = null) => {
    event?.preventDefault();
    setEventSubmitMessage("");

    const validationErrors = validateEventForm(eventFields, eventFormData);
    if (Object.keys(validationErrors).length > 0) {
      setEventFormErrors(validationErrors);
      return;
    }

    if (!selectedProgramStage?.id || !trackedEntityId || !currentOrgUnitId) {
      setEventsError("This record is missing the required tracker context for event creation.");
      return;
    }

    setIsSubmittingEvent(true);
    setEventsError("");

    try {
      const existingDataValueMap = buildExistingEventDataValueMap(selectedTimelineEvent);
      const dataValues = await buildEventDataValues(eventFields, eventFormData, existingDataValueMap);
      const occurredAt = eventDate || new Date().toISOString().slice(0, 10);
      const nextStatus = statusOverride || selectedTimelineEvent?.status || "COMPLETED";
      const payload = {
        events: [
          {
            ...(selectedTimelineEvent?.event ? { event: selectedTimelineEvent.event } : {}),
            program: selectedTracker.value,
            programStage: selectedProgramStage.id,
            orgUnit: currentOrgUnitId,
            trackedEntity: trackedEntityId,
            ...(enrollmentId ? { enrollment: enrollmentId } : {}),
            occurredAt,
            status: nextStatus,
            dataValues,
          },
        ],
      };

      const response = await axios.post("/tracker", payload);
      const importedEventId =
        selectedTimelineEvent?.event ||
        extractImportedTrackerEventId(response?.data) ||
        `draft-${Date.now()}`;
      const savedEvent = {
        event: importedEventId,
        program: selectedTracker.value,
        programStage: selectedProgramStage.id,
        orgUnit: currentOrgUnitId,
        trackedEntity: trackedEntityId,
        ...(enrollmentId ? { enrollment: enrollmentId } : {}),
        occurredAt,
        status: nextStatus,
        dataValues,
      };

      setEventRecords((prev) => {
        return mergeTrackerEvents(prev, [savedEvent]);
      });
      setSelectedTimelineItemId(importedEventId);
      setEventSubmitMessage(
        nextStatus === "COMPLETED"
          ? "Event completed successfully."
          : selectedTimelineEvent
            ? "Event updated successfully."
            : "Event added successfully.",
      );
      if (!selectedTimelineEvent) {
        setEventFormData(buildInitialEventFormValues(eventFields));
      }
      setTimeout(loadRecordEvents, 1200);
    } catch (error) {
      console.error("Failed to submit tracker event:", error);
      setEventsError("Unable to add this event. Check the required fields and try again.");
    } finally {
      setIsSubmittingEvent(false);
    }
  };

  const handleSaveEvent = (event) => {
    handleSubmitEvent(event, "COMPLETED");
  };

  const handlePrintEventForm = () => {
    setEventsError("");
    window.requestAnimationFrame(() => window.print());
  };

  const handleDownloadFileResource = async (fileResourceId, fileName) => {
    setEventsError("");

    try {
      await downloadTrackerFileResource(fileResourceId, fileName);
    } catch (error) {
      console.error("Failed to download tracker file resource:", error);
      const status = error?.response?.status;
      setEventsError(
        status
          ? `Unable to download this file (${fileResourceId}). The server returned ${status}.`
          : `Unable to download this file (${fileResourceId}). Please try again.`,
      );
    }
  };

  const handleRelationshipFieldChange = (fieldName, value) => {
    const dependentFields = targetRelationshipSections
      .flatMap((section) => section.fields || [])
      .filter(
        (field) =>
          field?.visibleWhen?.fieldName === fieldName ||
          field?.hiddenWhen?.fieldName === fieldName,
      );

    setRelationshipFormData((prev) => {
      const nextFormData = {
        ...prev,
        [fieldName]: value,
      };

      dependentFields.forEach((field) => {
        if (!isRegistrationFieldVisible(field, nextFormData)) {
          nextFormData[field.name] = "";
        }
      });

      return nextFormData;
    });
    setRelationshipFormErrors((prev) => {
      const dependentFieldNames = new Set(dependentFields.map((field) => field.name));
      if (!prev?.[fieldName] && !Object.keys(prev || {}).some((key) => dependentFieldNames.has(key))) return prev;
      const next = { ...prev };
      delete next[fieldName];
      dependentFieldNames.forEach((name) => delete next[name]);
      return next;
    });
    setRelationshipError("");
    setRelationshipSubmitMessage("");
  };

  const handleSubmitRelationship = async (event) => {
    event?.preventDefault();
    setRelationshipError("");
    setRelationshipSubmitMessage("");

    if (!selectedRelationshipOption) {
      setRelationshipError("Choose a relationship type before registering.");
      return;
    }

    const validationErrors = validateRelationshipRegistrationForm(
      visibleTargetRelationshipSections,
      relationshipFormData,
    );

    if (Object.keys(validationErrors).length > 0) {
      setRelationshipFormErrors(validationErrors);
      return;
    }

    setIsSubmittingRelationship(true);

    try {
      const preparedFormData = await uploadRelationshipFileFields(
        relationshipFormData,
        visibleTargetRelationshipSections,
      );
      const result = await submitTrackerRelationshipRegistration({
        relationshipOption: selectedRelationshipOption,
        targetProgramMetadata: targetRelationshipProgramMetadata,
        formData: preparedFormData,
        sections: visibleTargetRelationshipSections,
        currentOrgUnitId,
        currentTrackedEntityId: trackedEntityId,
      });

      setCreatedRelationshipRows((prev) => [
        {
          id: result.relationshipId || `${selectedRelationshipOption.id}:${result.trackedEntityId}`,
          type: selectedRelationshipOption.label,
          direction: selectedRelationshipOption.currentSide === "from" ? "Outgoing" : "Incoming",
          linkedId: result.trackedEntityId,
          linkedTrackedEntityId: result.trackedEntityId,
          targetProgramId: selectedRelationshipOption.targetProgramId,
          targetLabel: selectedRelationshipOption.targetLabel,
          targetProgramMetadata: targetRelationshipProgramMetadata,
          trackerContext: {
            value: selectedRelationshipOption.targetProgramId,
            label: selectedRelationshipOption.targetLabel || selectedRelationshipOption.label,
            program: targetRelationshipProgramMetadata,
          },
          status: "Created",
        },
        ...prev,
      ]);
      if (selectedRelationshipOption.targetProgramId && targetRelationshipProgramMetadata) {
        setRelationshipProgramMetadataById((prev) => ({
          ...prev,
          [selectedRelationshipOption.targetProgramId]: targetRelationshipProgramMetadata,
        }));
      }
      setRelationshipSubmitMessage("Relationship registered successfully.");
      setIsRelationshipAddOpen(false);
      setSelectedRelationshipOptionId("");
      setTargetRelationshipProgramMetadata(null);
      setTargetRelationshipFieldIds(null);
      setRelationshipFormData({});
      setRelationshipFormErrors({});
    } catch (error) {
      console.error("Failed to register relationship:", error);
      setRelationshipError(formatRelationshipRegistrationError(error, visibleTargetRelationshipSections));
    } finally {
      setIsSubmittingRelationship(false);
    }
  };

  const handleOpenRelationshipRecord = async (relationship) => {
    const linkedTrackedEntityId = relationship?.linkedTrackedEntityId || relationship?.linkedId;

    if (!linkedTrackedEntityId || linkedTrackedEntityId === "Linked record") {
      setLinkedRelationshipError("This relationship does not include a linked tracker record id.");
      return;
    }

    setLoadingLinkedRelationshipId(relationship.id);
    setLinkedRelationshipError("");

    try {
      const trackerFromRelationshipName = resolveTrackerOptionFromRelationshipName(
        relationship,
        trackerOptions,
        selectedTracker?.value,
      );
      const linkedRecord =
        relationshipRecordSummaries[linkedTrackedEntityId]?.record ||
        await fetchRelationshipTrackerRecord(
          linkedTrackedEntityId,
          relationship?.targetProgramId || relationship?.trackerContext?.value || trackerFromRelationshipName?.value,
        );
      const linkedEnrollments = Array.isArray(linkedRecord?.enrollments) ? linkedRecord.enrollments : [];
      const trackerFromLinkedRecord = resolveTrackerOptionFromLinkedRecord(
        linkedRecord,
        trackerOptions,
        selectedTracker?.value,
      );
      const linkedProgramId =
        relationship?.targetProgramId ||
        trackerFromLinkedRecord?.value ||
        trackerFromRelationshipName?.value ||
        linkedEnrollments.find((enrollment) => enrollment?.program === trackerFromRelationshipName?.value)?.program ||
        linkedEnrollments.find((enrollment) => enrollment?.program && enrollment.program !== selectedTracker?.value)?.program ||
        linkedEnrollments.find((enrollment) => enrollment?.program)?.program ||
        "";
      const existingLinkedTracker = linkedProgramId
        ? trackerOptions.find((option) => option.value === linkedProgramId)
        : null;
      let linkedProgramMetadata =
        existingLinkedTracker?.program ||
        relationship?.targetProgramMetadata ||
        relationshipProgramMetadataById[linkedProgramId] ||
        null;

      if (linkedProgramId && !linkedProgramMetadata) {
        try {
          const response = await axios.get(`/programs/${linkedProgramId}`, {
            params: {
              fields: PROGRAM_METADATA_FIELDS,
            },
          });
          linkedProgramMetadata = response?.data || null;
        } catch (error) {
          linkedProgramMetadata = null;
        }
      }

      if (linkedProgramId && linkedProgramMetadata) {
        setRelationshipProgramMetadataById((prev) => ({
          ...prev,
          [linkedProgramId]: linkedProgramMetadata,
        }));
      }

      const linkedTracker =
        existingLinkedTracker ||
        (trackerFromLinkedRecord?.value === linkedProgramId ? trackerFromLinkedRecord : null) ||
        (linkedProgramId
          ? {
              value: linkedProgramId,
              label:
                linkedProgramMetadata?.displayName ||
                linkedProgramMetadata?.name ||
                relationship?.targetLabel ||
                relationship?.type ||
                "Linked Tracker",
              source: "relationship",
              icon: "Database",
              description: linkedProgramMetadata?.trackedEntityType?.displayName
                ? `${linkedProgramMetadata.trackedEntityType.displayName} tracker`
                : "Linked tracker record",
              program: linkedProgramMetadata,
              attributeOrder: (linkedProgramMetadata?.programTrackedEntityAttributes || [])
                .slice()
                .sort((a, b) => (a?.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b?.sortOrder ?? Number.MAX_SAFE_INTEGER))
                .map((entry) => entry?.trackedEntityAttribute?.displayName)
                .filter(Boolean),
            }
          : {
              value: relationship?.targetProgramId || "linked-tracker",
              label: relationship?.targetLabel || relationship?.type || "Linked Tracker",
              source: "relationship",
              icon: "Database",
              description: "Linked tracker record",
              program: relationship?.targetProgramMetadata || null,
              attributeOrder: [],
            });
      const normalizedLinkedRecord = {
        recordId: getTrackerRecordId(linkedRecord, 0),
        attributeValueMap: {
          ...buildTrackerSystemValueMap(linkedRecord, currentLocation, linkedTracker),
          ...buildAttributeValueMap(getTrackerRecordAttributes(linkedRecord, linkedTracker), null, linkedTracker),
        },
        rawRecord: linkedRecord,
      };
      const linkedColumns = getTrackerAttributeColumns(
        [linkedRecord],
        linkedTracker,
        null,
        12,
      ).allColumns;

      setLinkedRelationshipContext({
        record: normalizedLinkedRecord,
        selectedTracker: linkedTracker,
        allColumns: linkedColumns,
      });
      onViewingTrackerChange?.(linkedTracker);
    } catch (error) {
      console.error("Failed to open linked relationship record:", error);
      setLinkedRelationshipError(error?.message || "Unable to open this linked tracker record.");
    } finally {
      setLoadingLinkedRelationshipId("");
    }
  };

  if (linkedRelationshipContext) {
    return (
      <TrackerRecordDetailWorkspace
        record={linkedRelationshipContext.record}
        selectedTracker={linkedRelationshipContext.selectedTracker}
        currentLocation={currentLocation}
        allColumns={linkedRelationshipContext.allColumns}
        trackerOptions={trackerOptions}
        onBack={() => {
          setLinkedRelationshipContext(null);
          onViewingTrackerChange?.(selectedTracker);
        }}
        onEdit={onEdit}
        onViewingTrackerChange={onViewingTrackerChange}
      />
    );
  }
  const handleDeleteTimelineEvent = async () => {
    const eventUid = selectedTimelineEvent?.event;
    if (!eventUid || isDraftTimelineEvent || String(eventUid).startsWith("draft-")) {
      setEventsError("Cannot delete this event.");
      return;
    }

    const stageLabel = selectedStageOption?.label || "this event";
    if (!window.confirm(`Delete "${stageLabel}"? This cannot be undone.`)) {
      return;
    }

    setIsSubmittingEvent(true);
    setEventsError("");
    setEventSubmitMessage("");

    try {
      await axios.post(
        "/tracker",
        { events: [{ event: eventUid }] },
        { params: { importStrategy: "DELETE" } },
      );

      setEventRecords((prev) => prev.filter((item) => item.event !== eventUid));
      setSelectedTimelineItemId("");
      setEventFormData(buildInitialEventFormValues(eventFields));
      setEventFormErrors({});
      setEventSubmitMessage("Event deleted successfully.");
      setTimeout(loadRecordEvents, 800);
    } catch (error) {
      console.error("Failed to delete tracker event via tracker API:", error);

      try {
        await axios.delete(`/events/${eventUid}`);
        setEventRecords((prev) => prev.filter((item) => item.event !== eventUid));
        setSelectedTimelineItemId("");
        setEventFormData(buildInitialEventFormValues(eventFields));
        setEventFormErrors({});
        setEventSubmitMessage("Event deleted successfully.");
        setTimeout(loadRecordEvents, 800);
      } catch (fallbackError) {
        console.error("Failed to delete tracker event via events API:", fallbackError);
        setEventsError("Unable to delete this event. Please try again.");
      }
    } finally {
      setIsSubmittingEvent(false);
    }
  };

  const handleOpenDocumentPreview = (preview) => {
    if (!preview?.fileResourceId && !preview?.eventFileDownloadUrl) return;
    setDocumentPreview(preview);
  };

  return (
    <>
      <div className="px-3 py-3 sm:px-4 sm:py-4 md:px-6">
      <style>
        {`
          .tracker-event-print-sheet {
            display: none;
          }

          @media print {
            body * {
              visibility: hidden !important;
            }

            .tracker-event-print-sheet,
            .tracker-event-print-sheet * {
              visibility: visible !important;
            }

            .tracker-event-print-sheet {
              background: #ffffff;
              color: #111827;
              display: block !important;
              font-family: Arial, sans-serif;
              left: 0;
              padding: 0;
              position: absolute;
              top: 0;
              width: 100%;
            }

            .tracker-event-print-sheet header {
              border-bottom: 2px solid #16325a;
              margin-bottom: 18px;
              padding-bottom: 12px;
            }

            .tracker-event-print-sheet h1 {
              font-size: 22px;
              margin: 0 0 8px;
            }

            .tracker-event-print-sheet h2 {
              background: #f3f4f6 !important;
              border: 1px solid #d1d5db;
              font-size: 15px;
              margin: 18px 0 0;
              padding: 10px 12px;
            }

            .tracker-event-print-sheet .print-meta {
              color: #4b5563;
              display: grid;
              gap: 4px;
              font-size: 13px;
            }

            .tracker-event-print-sheet section {
              break-inside: avoid;
              page-break-inside: avoid;
            }

            .tracker-event-print-sheet table {
              border-collapse: collapse;
              width: 100%;
            }

            .tracker-event-print-sheet th,
            .tracker-event-print-sheet td {
              border: 1px solid #d1d5db;
              font-size: 13px;
              padding: 9px 10px;
              text-align: left;
              vertical-align: top;
            }

            .tracker-event-print-sheet th {
              background: #fafafa !important;
              font-weight: 700;
              width: 34%;
            }

            @page {
              margin: 18mm;
            }
          }
        `}
      </style>
      <div className="tracker-event-print-sheet" aria-hidden="true">
        <header>
          <h1>{selectedTracker?.label || "Tracker"} {selectedStageOption?.label || "Event"} Form</h1>
          <div className="print-meta">
            <div><strong>Tracker:</strong> {selectedTracker?.label || "-"}</div>
            <div><strong>Event:</strong> {selectedStageOption?.label || selectedProgramStage?.displayName || "-"}</div>
            <div><strong>Report Date:</strong> {eventDate || "-"}</div>
            <div><strong>Record:</strong> {primaryTitle}</div>
          </div>
        </header>

        {printableEventSections.length > 0 ? (
          printableEventSections.map((section, sectionIndex) => (
            <section key={`print:${section.title}:${sectionIndex}`}>
              <h2>{section?.title || "Event Details"}</h2>
              <table>
                <tbody>
                  {section?.fields?.length > 0 ? (
                    section.fields.map((field) => (
                      <tr key={`print:${section.title}:${field.name}`}>
                        <th>{field.label}</th>
                        <td>{formatEventPrintValue(field, eventFormData?.[field.name], fileResourceDetails)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td>No fields configured for this section.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
          ))
        ) : (
          <p>No printable fields available.</p>
        )}
      </div>
      <main className="container mx-auto px-0 py-2 space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <button
              type="button"
              onClick={onBack}
              className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              <Icon name="ArrowLeft" size={16} />
              Back to records
            </button>
            <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">
              {selectedTracker?.label || "Tracker"} record
            </p>
            <h1 className="mt-1 text-2xl font-bold text-foreground md:text-3xl">{primaryTitle}</h1>
            <div className="mt-3 flex flex-wrap gap-2 text-xs text-muted-foreground">
              <span className="inline-flex items-center rounded-full border border-border bg-card px-2.5 py-1">
                Record ID: {record?.recordId}
              </span>
              <span className="inline-flex items-center rounded-full border border-border bg-card px-2.5 py-1">
                {formatLocationScope(currentLocation)}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <Button type="button" variant="outline" onClick={() => onEdit?.(record?.rawRecord)}>
              <Icon name="Pencil" size={16} />
              Edit Record
            </Button>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card shadow-sm">
          <div className="flex overflow-x-auto border-b border-border px-3">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={[
                  "inline-flex items-center gap-2 border-b-2 px-3 py-3 text-sm font-medium transition-colors whitespace-nowrap",
                  activeTab === tab.id
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <Icon name={tab.icon} size={16} />
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-3 sm:p-5">
            {activeTab === "overview" && (
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {detailColumns.length > 0 ? (
                  detailColumns.map((columnName) => (
                    <div key={`${record?.recordId}:detail-page:${columnName}`} className="rounded-lg border border-border bg-background p-3">
                      <p className="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
                        {columnName}
                      </p>
                      <p className="mt-2 text-sm text-foreground break-words">
                        {formatTrackerCellValue(columnName, record?.attributeValueMap?.[columnName])}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">No populated fields are available for this record.</p>
                )}
              </div>
            )}

            {activeTab === "timeline" && (
              <div className="space-y-5">
                <section className="rounded-xl border border-border bg-background p-3 sm:p-4">
                  <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <h2 className="text-base font-semibold text-foreground">Timeline Data Entry</h2>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Select an event box to view or update its form, or add a new event to the flow.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 sm:flex">
                      <Button type="button" variant="outline" size="sm" onClick={loadRecordEvents} disabled={isEventsLoading} className="w-full sm:w-auto">
                        <Icon name={isEventsLoading ? "Loader2" : "RefreshCw"} className={isEventsLoading ? "animate-spin" : ""} size={14} />
                        Refresh
                      </Button>
                      <Button type="button" size="sm" onClick={handleOpenNewEventModal} disabled={programStageOptions.length === 0} className="w-full sm:w-auto">
                        <Icon name="Plus" size={14} />
                        New Event
                      </Button>
                    </div>
                  </div>

                  <div className="overflow-x-auto pb-2">
                    <div className="flex min-w-max items-center gap-2 sm:min-w-0">
                      {timelineItems.map((item, index) => {
                        const isSelected = selectedTimelineItemId === item.id;
                        const isCompleted = String(item.status || "").toUpperCase() === "COMPLETED";
                        const eventBoxClassName = isCompleted
                          ? isSelected
                            ? "border-emerald-600 bg-emerald-100 text-emerald-950 shadow-sm"
                            : "border-emerald-500 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                          : isSelected
                            ? "border-[#16325a] bg-[#f6de4a] text-slate-900 shadow-sm"
                            : "border-dashed border-amber-500/70 bg-amber-50 text-amber-800 hover:bg-amber-100";

                        return (
                          <React.Fragment key={item.id || `${item.stageLabel}-${index}`}>
                            <button
                              type="button"
                              onClick={() => setSelectedTimelineItemId(item.id)}
                              className={`min-h-20 w-36 rounded-lg border-2 px-3 py-3 text-center text-xs transition-colors sm:min-h-24 sm:w-40 ${eventBoxClassName}`}
                            >
                              <span className="block font-medium">
                                {item.displayDate ? formatTrackerCellValue("date", item.displayDate) : "No date"}
                              </span>
                              <span className="mt-1 block font-semibold">{item.stageLabel}</span>
                              <span className="mt-1 block text-[10px]">({item.status})</span>
                            </button>
                            {index < timelineItems.length - 1 && (
                              <Icon name="ArrowRight" size={18} className="text-muted-foreground" />
                            )}
                          </React.Fragment>
                        );
                      })}

                      {isDraftTimelineEvent && (
                        <>
                          {timelineItems.length > 0 && <Icon name="ArrowRight" size={18} className="text-muted-foreground" />}
                          <button
                            type="button"
                            onClick={() => setSelectedTimelineItemId("draft")}
                            className="min-h-20 w-36 rounded-lg border-2 border-[#16325a] bg-[#f6de4a] px-3 py-3 text-center text-xs font-semibold text-slate-900 shadow-sm sm:min-h-24 sm:w-40"
                          >
                            <span className="block">{formatTrackerCellValue("date", eventDate)}</span>
                            <span className="mt-1 block">{selectedStageOption?.label || "New Event"}</span>
                            <span className="mt-1 block text-[10px] font-medium">(draft)</span>
                          </button>
                        </>
                      )}

                      {!isDraftTimelineEvent && timelineItems.length === 0 && (
                        <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-8 text-center">
                          <Icon name="Clock3" size={24} className="mx-auto text-muted-foreground" />
                          <p className="mt-2 text-sm text-muted-foreground">No timeline events have been added yet.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </section>

                {programStageOptions.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-8 text-center">
                    <Icon name="Info" size={24} className="mx-auto text-muted-foreground" />
                    <p className="mt-2 text-sm text-muted-foreground">
                      No event stages are configured for this tracker.
                    </p>
                  </div>
                ) : selectedTimelineItemId ? (
                  <form className="space-y-4" onSubmit={handleSaveEvent}>
                    {eventFields.length > 0 ? (
                      <div className="space-y-4">
                        {eventFieldSections.map((section, sectionIndex) => (
                          <section
                            key={`${selectedStageId}:section:${section.title}:${sectionIndex}`}
                            className="rounded-lg border border-border bg-card"
                          >
                            <div className="border-b border-border bg-muted/50 px-4 py-3">
                              <h3 className="text-base font-semibold text-foreground">
                                {getEventSectionTitle(section, selectedTracker)}
                              </h3>
                            </div>
                            <div className="p-3 sm:p-4">
                              {!section.implicit && (
                                <div className="mb-4 rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm font-medium text-sky-700">
                                  {getEventSectionInnerLabel(section, selectedTracker)}
                                </div>
                              )}
                              <div className="grid gap-4 sm:grid-cols-2">
                                {section.fields.map((field) => (
                                  <TrackerEventFormField
                                    key={`${selectedStageId}:${field.name}`}
                                    field={field}
                                    value={eventFormData[field.name]}
                                    error={eventFormErrors[field.name]}
                                    onChange={handleEventFieldChange}
                                    fileResourceDetails={fileResourceDetails}
                                    onOpenDocumentPreview={handleOpenDocumentPreview}
                                    eventFileDownloadUrl={buildEventFileDownloadUrl(
                                      selectedTimelineEvent?.event,
                                      field.name,
                                    )}
                                  />
                                ))}
                              </div>
                            </div>
                          </section>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-6 text-sm text-muted-foreground">
                        This stage has no visible data elements. Saving will create a dated event only.
                      </div>
                    )}

                    {eventsError && (
                      <div className="rounded-lg border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                        {eventsError}
                      </div>
                    )}
                    {eventSubmitMessage && (
                      <div className="rounded-lg border border-success/20 bg-success/10 px-3 py-2 text-sm text-success">
                        {eventSubmitMessage}
                      </div>
                    )}

                    <div className="flex flex-col gap-3 rounded-xl border border-border bg-background p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4">
                      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                        <Button type="submit" disabled={isSubmittingEvent} className="w-full sm:w-auto">
                          <Icon
                            name={isSubmittingEvent ? "Loader2" : "CheckCircle2"}
                            className={isSubmittingEvent ? "animate-spin" : ""}
                            size={16}
                          />
                          {isSubmittingEvent ? "Completing..." : "Complete"}
                        </Button>
                      </div>
                      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                        <Button type="button" variant="outline" onClick={handlePrintEventForm} className="w-full sm:w-auto">
                          <Icon name="Printer" size={16} />
                          Print form
                        </Button>
                        {!isDraftTimelineEvent && (
                          <Button type="button" variant="destructive" className="w-full sm:w-auto">
                            <Icon name="Trash2" size={16} />
                            Delete
                          </Button>
                        )}
                      </div>
                    </div>
                  </form>
                ) : (
                  <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-8 text-center">
                    <Icon name="MousePointerClick" size={24} className="mx-auto text-muted-foreground" />
                    <p className="mt-2 text-sm text-muted-foreground">
                      Select a timeline box or create a new event to open its form.
                    </p>
                  </div>
                )}

                <NewTimelineEventModal
                  isOpen={isNewEventModalOpen}
                  stageOptions={programStageOptions}
                  stageId={draftStageId}
                  eventDate={draftEventDate}
                  onStageChange={setDraftStageId}
                  onDateChange={setDraftEventDate}
                  onClose={() => setIsNewEventModalOpen(false)}
                  onConfirm={handleConfirmNewEvent}
                />
              </div>
            )}

            {activeTab === "relationship" && (
              <div className="space-y-4">
                <div className="rounded-xl border border-border bg-background p-4">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="flex items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Icon name="Network" size={20} />
                      </div>
                      <div>
                        <h2 className="text-base font-semibold text-foreground">Relationship</h2>
                        <p className="mt-1 text-sm text-muted-foreground">
                          Register and view tracker records linked to this record.
                        </p>
                      </div>
                    </div>
                    {relationshipOptions.length > 0 && (
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => {
                          setIsRelationshipAddOpen(true);
                          setRelationshipError("");
                          setRelationshipSubmitMessage("");
                          loadRelationshipTypeOptions({ force: relationshipTypes.length === 0 });
                        }}
                        className="w-full sm:w-auto"
                      >
                        <Icon name="Plus" size={14} />
                        Add Relationship
                      </Button>
                    )}
                  </div>
                </div>

                {relationshipRows.length > 0 ? (
                  <div className="space-y-3">
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <h3 className="text-sm font-semibold text-foreground">Existing Relationships</h3>
                      <p className="text-xs text-muted-foreground">Click a row to open the linked tracker record.</p>
                    </div>
                    {linkedRelationshipError && (
                      <div className="rounded-lg border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                        {linkedRelationshipError}
                      </div>
                    )}
                    <div className="overflow-x-auto rounded-xl border border-border bg-background">
                      <table className="w-full min-w-[760px] text-sm">
                        <thead className="border-b border-border bg-muted/40">
                          <tr className="text-left">
                            <th className="px-4 py-3 font-medium text-muted-foreground">Relationship</th>
                            {relationshipTableColumns.length > 0 ? (
                              relationshipTableColumns.map((column) => (
                                <th key={`relationship-column:${column.id}`} className="px-4 py-3 font-medium text-muted-foreground">
                                  {column.label}
                                </th>
                              ))
                            ) : (
                              <th className="px-4 py-3 font-medium text-muted-foreground">Registered details</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {relationshipRows.map((relationship) => {
                            const isLoadingRelationship = loadingLinkedRelationshipId === relationship.id;
                            const linkedId = relationship?.linkedTrackedEntityId || relationship?.linkedId;
                            const linkedSummary = relationshipRecordSummaries[linkedId];

                            return (
                              <tr
                                key={relationship.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => handleOpenRelationshipRecord(relationship)}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    handleOpenRelationshipRecord(relationship);
                                  }
                                }}
                                className="cursor-pointer border-b border-border/60 transition-colors last:border-b-0 hover:bg-muted/30 focus:bg-muted/30 focus:outline-none"
                              >
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    {isLoadingRelationship ? (
                                      <Icon name="Loader2" size={14} className="shrink-0 animate-spin text-muted-foreground" />
                                    ) : (
                                      <Icon name="ExternalLink" size={14} className="shrink-0 text-muted-foreground" />
                                    )}
                                    <span className="font-medium text-foreground">{relationship.type}</span>
                                  </div>
                                </td>
                                {relationshipTableColumns.length > 0 ? (
                                  relationshipTableColumns.map((column) => {
                                    const cellValue =
                                      column.valueSource === "label"
                                        ? linkedSummary?.valueByLabel?.[column.label]
                                        : linkedSummary?.valueByAttributeId?.[column.id];
                                    const displayValue = linkedSummary ? (cellValue && cellValue !== "-" ? cellValue : "-") : "Loading...";

                                    return (
                                      <td key={`${relationship.id}:${column.id}`} className="px-4 py-3 text-muted-foreground">
                                        <span className="block max-w-[220px] truncate" title={displayValue}>
                                          {displayValue}
                                        </span>
                                      </td>
                                    );
                                  })
                                ) : (
                                  <td className="px-4 py-3 text-muted-foreground">
                                    {linkedSummary?.detailItems?.length > 0 ? (
                                      linkedSummary.detailItems[0].value
                                    ) : linkedSummary ? (
                                      "-"
                                    ) : (
                                      "Loading details..."
                                    )}
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-8 text-center">
                    <Icon name="Network" size={28} className="mx-auto text-muted-foreground" />
                    <h2 className="mt-3 text-base font-semibold text-foreground">No relationships found</h2>
                    <p className="mx-auto mt-1 max-w-xl text-sm text-muted-foreground">
                      Relationship records for this tracker will appear here once they are available on the tracker record.
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "attachments" && (
              <div className="space-y-4">
                <div className="rounded-xl border border-border bg-background p-4">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Icon name="Paperclip" size={20} />
                      </div>
                      <div>
                        <h2 className="text-base font-semibold text-foreground">Attachments</h2>
                        <p className="mt-1 text-sm text-muted-foreground">
                          Files uploaded through timeline events for this tracker record.
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={loadRecordEvents}
                      disabled={isEventsLoading}
                      className="w-full sm:w-auto"
                    >
                      <Icon name={isEventsLoading ? "Loader2" : "RefreshCw"} className={isEventsLoading ? "animate-spin" : ""} size={14} />
                      Refresh
                    </Button>
                  </div>
                </div>

                {eventsError && (
                  <div className="rounded-lg border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                    {eventsError}
                  </div>
                )}

                {isEventsLoading && attachmentRows.length === 0 ? (
                  <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-4 py-4 text-sm text-muted-foreground">
                    <Icon name="Loader2" size={16} className="animate-spin" />
                    Loading attachments...
                  </div>
                ) : attachmentRows.length > 0 ? (
                  <div className="overflow-x-auto rounded-xl border border-border bg-background">
                    <table className="w-full min-w-[760px] text-sm">
                      <thead className="border-b border-border bg-muted/40">
                        <tr className="text-left">
                          <th className="px-4 py-3 font-medium text-muted-foreground">File</th>
                          <th className="px-4 py-3 font-medium text-muted-foreground">Field</th>
                          <th className="px-4 py-3 font-medium text-muted-foreground">Timeline Event</th>
                          <th className="px-4 py-3 font-medium text-muted-foreground">Date</th>
                          <th className="px-4 py-3 font-medium text-muted-foreground">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {attachmentRows.map((attachment) => {
                          const downloadUrl = buildEventFileDownloadUrl(attachment.eventId, attachment.dataElementId);
                          const fileName = getFileResourceLabel(attachment.fileResourceId, fileResourceDetails);

                          return (
                            <tr key={attachment.id} className="border-b border-border/60 last:border-b-0">
                              <td className="px-4 py-3">
                                <div className="flex min-w-0 items-center gap-2">
                                  <Icon name="FileText" size={16} className="shrink-0 text-muted-foreground" />
                                  <span className="max-w-[260px] truncate font-medium text-foreground" title={fileName}>
                                    {fileName}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-muted-foreground">{attachment.fieldLabel}</td>
                              <td className="px-4 py-3 text-muted-foreground">{attachment.stageLabel}</td>
                              <td className="px-4 py-3 text-muted-foreground">
                                {attachment.eventDate ? formatTrackerCellValue("date", attachment.eventDate) : "-"}
                              </td>
                              <td className="px-4 py-3">
                                {downloadUrl ? (
                                  <a
                                    href={downloadUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted"
                                  >
                                    <Icon name="Download" size={14} />
                                    Download
                                  </a>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => handleDownloadFileResource(attachment.fileResourceId, fileName)}
                                    className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted"
                                  >
                                    <Icon name="Download" size={14} />
                                    Download
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-8 text-center">
                    <Icon name="Paperclip" size={28} className="mx-auto text-muted-foreground" />
                    <h2 className="mt-3 text-base font-semibold text-foreground">No attachments found</h2>
                    <p className="mx-auto mt-1 max-w-xl text-sm text-muted-foreground">
                      Files uploaded on timeline events will appear here once they are available for this tracker record.
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab !== "overview" && activeTab !== "timeline" && activeTab !== "relationship" && activeTab !== "attachments" && (
              <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-8 text-center">
                <Icon
                  name={tabs.find((tab) => tab.id === activeTab)?.icon || "PanelTop"}
                  size={28}
                  className="mx-auto text-muted-foreground"
                />
                <h2 className="mt-3 text-base font-semibold text-foreground">
                  {tabs.find((tab) => tab.id === activeTab)?.label} workspace
                </h2>
                <p className="mx-auto mt-1 max-w-xl text-sm text-muted-foreground">
                  This area is ready for the remaining old-system tracker duties once the exact workflow is confirmed.
                </p>
              </div>
            )}
          </div>
        </div>

        {isRelationshipAddOpen && createPortal(
          <>
            <div
              className="fixed inset-0 z-[1200] bg-black/50 backdrop-blur-[2px]"
              onClick={() => {
                if (isSubmittingRelationship) return;
                setIsRelationshipAddOpen(false);
                setSelectedRelationshipOptionId("");
                setTargetRelationshipProgramMetadata(null);
                setTargetRelationshipFieldIds(null);
                setRelationshipFormData({});
                setRelationshipFormErrors({});
                setRelationshipError("");
              }}
            />
            <div className="fixed inset-0 z-[1300] flex items-end justify-center p-0 sm:items-center sm:p-4">
              <div
                role="dialog"
                aria-modal="true"
                aria-label="Add relationship"
                className="flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-2xl border border-border bg-card shadow-xl sm:max-w-4xl sm:rounded-2xl"
              >
                <div className="flex shrink-0 items-start justify-between gap-4 border-b border-border bg-card p-4 sm:p-5">
                  <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-foreground">Add Relationship</h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Select what you want to add, then complete the linked tracker form.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (isSubmittingRelationship) return;
                      setIsRelationshipAddOpen(false);
                      setSelectedRelationshipOptionId("");
                      setTargetRelationshipProgramMetadata(null);
                      setTargetRelationshipFieldIds(null);
                      setRelationshipFormData({});
                      setRelationshipFormErrors({});
                      setRelationshipError("");
                    }}
                    className="shrink-0 rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    aria-label="Close add relationship"
                  >
                    <Icon name="X" size={18} />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto bg-background/40 p-4 sm:p-5">
                  {relationshipSubmitMessage && (
                    <div className="mb-4 rounded-lg border border-success/20 bg-success/10 px-3 py-2 text-sm text-success">
                      {relationshipSubmitMessage}
                    </div>
                  )}

                  {relationshipError && (
                    <div className="mb-4 rounded-lg border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                      {relationshipError}
                    </div>
                  )}

                  {isRelationshipTypesLoading ? (
                    <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-3 text-sm text-muted-foreground">
                      <Icon name="Loader2" size={16} className="animate-spin" />
                      Loading relationship options...
                    </div>
                  ) : relationshipOptions.length > 0 ? (
                    <div className="space-y-6">
                      <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
                        <label className="mb-2 block text-sm font-medium text-foreground">
                          Relationship to add
                          <span className="ml-1 text-destructive">*</span>
                        </label>
                        <select
                          value={selectedRelationshipOptionId}
                          onChange={(event) => {
                            setSelectedRelationshipOptionId(event?.target?.value || "");
                            setRelationshipError("");
                            setRelationshipSubmitMessage("");
                          }}
                          className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm transition-[border-color,box-shadow] focus:border-[#16325a] focus:outline-none focus:shadow-[0_0_0_3px_rgba(22,50,90,0.12)]"
                          required
                        >
                          <option value="">Select relationship</option>
                          {relationshipOptions.map((option) => (
                            <option key={option.id} value={option.id}>
                              {option.label} - {option.targetLabel}
                            </option>
                          ))}
                        </select>
                      </div>

                      {selectedRelationshipOption && (
                        <div className="rounded-xl border border-border bg-card shadow-sm">
                          <div className="border-b border-border px-4 py-4">
                            <h3 className="text-sm font-semibold text-foreground">
                              Register {selectedRelationshipOption.targetLabel}
                            </h3>
                            <p className="text-xs text-muted-foreground">
                              The linked record will be created, enrolled, and related to this tracker record.
                            </p>
                          </div>

                          {isTargetRelationshipMetadataLoading ? (
                            <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-3 text-sm text-muted-foreground">
                              <Icon name="Loader2" size={16} className="animate-spin" />
                              Preparing linked tracker form...
                            </div>
                          ) : visibleTargetRelationshipSections.length > 0 ? (
                            <form id="relationship-registration-form" className="space-y-0" onSubmit={handleSubmitRelationship}>
                              <div className="space-y-6 p-4">
                              {visibleTargetRelationshipSections.map((section) => (
                                <section key={`relationship:${section.title}`} className="space-y-4">
                                  <div>
                                    <h4 className="text-sm font-semibold text-foreground">{section.title}</h4>
                                    {section.description && (
                                      <p className="mt-1 text-xs text-muted-foreground">{section.description}</p>
                                    )}
                                  </div>
                                  <div className="grid gap-x-5 gap-y-5 md:grid-cols-2">
                                    {(section.fields || []).map((field) => (
                                      <RelationshipRegistrationField
                                        key={`relationship-field:${field.name}`}
                                        field={field}
                                        value={relationshipFormData?.[field.name]}
                                        error={relationshipFormErrors?.[field.name]}
                                        onChange={handleRelationshipFieldChange}
                                      />
                                    ))}
                                  </div>
                                </section>
                              ))}
                              </div>

                            </form>
                          ) : (
                            <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-6 text-sm text-muted-foreground">
                              This relationship does not expose a target tracker registration form yet.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-6 text-center">
                      <Icon name="Network" size={24} className="mx-auto text-muted-foreground" />
                      <p className="mt-2 text-sm text-muted-foreground">
                        No relationship options are configured for this tracker.
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setRelationshipError("");
                          loadRelationshipTypeOptions({ force: true });
                        }}
                        className="mt-4"
                      >
                        <Icon name="RefreshCw" size={14} />
                        Try again
                      </Button>
                    </div>
                  )}
                </div>
                {selectedRelationshipOption && !isTargetRelationshipMetadataLoading && visibleTargetRelationshipSections.length > 0 && (
                  <div className="flex shrink-0 flex-col gap-2 border-t border-border bg-card p-4 sm:flex-row sm:justify-end sm:p-5">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setSelectedRelationshipOptionId("");
                        setTargetRelationshipProgramMetadata(null);
                        setTargetRelationshipFieldIds(null);
                        setRelationshipFormData({});
                        setRelationshipFormErrors({});
                      }}
                      className="w-full sm:w-auto"
                      disabled={isSubmittingRelationship}
                    >
                      Clear Selection
                    </Button>
                    <Button
                      type="submit"
                      form="relationship-registration-form"
                      disabled={isSubmittingRelationship}
                      className="w-full sm:w-auto"
                    >
                      <Icon
                        name={isSubmittingRelationship ? "Loader2" : "Save"}
                        className={isSubmittingRelationship ? "animate-spin" : ""}
                        size={16}
                      />
                      {isSubmittingRelationship ? "Registering..." : "Register Relationship"}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </>,
          document.body,
        )}
      </main>

      <TrackerDocumentPreviewModal
        isOpen={Boolean(documentPreview)}
        onClose={() => setDocumentPreview(null)}
        title={documentPreview?.title}
        fileName={documentPreview?.fileName}
        fileResourceId={documentPreview?.fileResourceId}
        eventFileDownloadUrl={documentPreview?.eventFileDownloadUrl}
      />
      </div>
    </>
  );
}

function trackerRecordSearchText(record, allColumns) {
  return [
    record?.recordId,
    ...allColumns.map((columnName) =>
      formatTrackerCellValue(columnName, record?.attributeValueMap?.[columnName]),
    ),
  ]
    .join(" ")
    .toLowerCase();
}

function trackerRecordMatchesSearch(record, allColumns, searchTerm, queryMode) {
  const normalizedSearchTerm = String(searchTerm || "").trim().toLowerCase();
  if (!normalizedSearchTerm) return true;

  const haystack = trackerRecordSearchText(record, allColumns);
  if (queryMode === "any") {
    const tokens = normalizedSearchTerm
      .split(/[^a-z0-9]+/)
      .filter((token) => token.length > 3 && !["and", "the", "for", "with"].includes(token));
    if (tokens.length === 0) return haystack.includes(normalizedSearchTerm);
    return tokens.some((token) => haystack.includes(token));
  }

  if (normalizedSearchTerm.includes("|")) {
    return normalizedSearchTerm
      .split("|")
      .map((phrase) => phrase.trim())
      .filter(Boolean)
      .some((phrase) => haystack.includes(phrase));
  }

  return haystack.includes(normalizedSearchTerm);
}

function trackerRecordMatchesYear(record, allColumns, year) {
  if (!year) return true;
  return trackerRecordSearchText(record, allColumns).includes(String(year).toLowerCase());
}

function TrackerRecordsWorkspace({
  currentLocation,
  selectedTracker,
  trackerOptions = [],
  isTrackerMetadataLoading = false,
  isLoading,
  error,
  records,
  totalCount,
  currentPage,
  itemsPerPage,
  onPageChange,
  onRetry,
  onViewingTrackerChange,
  onDetailActiveChange,
}) {
  const [searchParams] = useSearchParams();
  const linkedSearch = searchParams.get("q") || "";
  const linkedYearParam = searchParams.get("year") || "";
  const linkedStart = searchParams.get("start") || "";
  const linkedEnd = searchParams.get("end") || "";
  const linkedQueryMode = searchParams.get("qMode") || "";
  const [searchTerm, setSearchTerm] = useState(linkedSearch);
  const [linkedYear, setLinkedYear] = useState(linkedYearParam);
  const [queryMode, setQueryMode] = useState(linkedQueryMode);
  const [selectedType, setSelectedType] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);
  const [isBulkloadPanelOpen, setIsBulkloadPanelOpen] = useState(false);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);
  const [isPrintMenuOpen, setIsPrintMenuOpen] = useState(false);
  const [printOrientation, setPrintOrientation] = useState("portrait");
  const [isColumnMenuOpen, setIsColumnMenuOpen] = useState(false);
  const [selectedColumnNames, setSelectedColumnNames] = useState(null);
  const [savedColumnConfig, setSavedColumnConfig] = useState(null);
  const [columnConfigProgramId, setColumnConfigProgramId] = useState(null);
  const [hasStoredColumnConfig, setHasStoredColumnConfig] = useState(false);
  const [isLoadingColumnConfig, setIsLoadingColumnConfig] = useState(false);
  const [isSavingColumnConfig, setIsSavingColumnConfig] = useState(false);
  const [columnConfigMessage, setColumnConfigMessage] = useState("");
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const columnMenuRef = useRef(null);

  const currentOrgUnitId = currentLocation?.orgUnitId || null;
  const requiresScopedOrgUnit = !currentLocation?.orgUnitId;
  const trackerProfile = useMemo(
    () => getTrackerProfile(selectedTracker?.value, selectedTracker?.label),
    [selectedTracker?.label, selectedTracker?.value],
  );
  const filterConfig = trackerProfile?.filterConfig || {};
  const activeSavedColumnConfig =
    columnConfigProgramId === selectedTracker?.value ? savedColumnConfig : null;

  const normalizedRecords = useMemo(
    () =>
      (Array.isArray(records) ? records : []).map((record, index) => ({
        recordId: getTrackerRecordId(record, index),
        attributeValueMap: {
          ...buildTrackerSystemValueMap(record, currentLocation, selectedTracker),
          ...buildAttributeValueMap(getTrackerRecordAttributes(record, selectedTracker), activeSavedColumnConfig, selectedTracker),
        },
        rawRecord: record,
      })),
    [activeSavedColumnConfig, currentLocation, records, selectedTracker],
  );

  const {
    columns: visibleColumns,
    allColumns,
  } = getTrackerAttributeColumns(records, selectedTracker, activeSavedColumnConfig);
  const displayColumns = useMemo(() => {
    if (!Array.isArray(allColumns) || allColumns.length === 0) return [];
    if (!Array.isArray(selectedColumnNames)) return visibleColumns;

    const selectedSet = new Set(selectedColumnNames);
    const selectedColumns = allColumns.filter((columnName) => selectedSet.has(columnName));
    return selectedColumns.length > 0 ? selectedColumns : visibleColumns;
  }, [allColumns, selectedColumnNames, visibleColumns]);
  const hiddenDisplayColumnCount = Math.max(allColumns.length - displayColumns.length, 0);

  const typeColumn = useMemo(
    () =>
      detectTrackerColumn(allColumns, filterConfig?.type?.matchers || []),
    [allColumns, filterConfig?.type?.matchers],
  );

  const statusColumn = useMemo(
    () =>
      detectTrackerColumn(allColumns, filterConfig?.status?.matchers || []),
    [allColumns, filterConfig?.status?.matchers],
  );

  const dateColumn = useMemo(
    () =>
      detectTrackerColumn(allColumns, filterConfig?.date?.matchers || []),
    [allColumns, filterConfig?.date?.matchers],
  );
  const registrationDateColumn = allColumns.includes("Registration date") ? "Registration date" : null;
  const effectiveDateColumn = dateColumn || registrationDateColumn;

  const typeOptions = useMemo(
    () =>
      buildTrackerFilterOptions(
        normalizedRecords,
        typeColumn,
        filterConfig?.type?.allLabel || "All Types",
        filterConfig?.type?.options || [],
      ),
    [filterConfig?.type?.allLabel, filterConfig?.type?.options, normalizedRecords, typeColumn],
  );

  const statusOptions = useMemo(
    () =>
      buildTrackerFilterOptions(
        normalizedRecords,
        statusColumn,
        filterConfig?.status?.allLabel || "All Statuses",
        filterConfig?.status?.options || [],
      ),
    [filterConfig?.status?.allLabel, filterConfig?.status?.options, normalizedRecords, statusColumn],
  );

  const selectedTypeOption =
    typeOptions.find((option) => option.value === selectedType) || typeOptions[0] || null;
  const selectedStatusOption =
    statusOptions.find((option) => option.value === selectedStatus) || statusOptions[0] || null;

  useEffect(() => {
    setSearchTerm(linkedSearch);
    setLinkedYear(linkedYearParam);
    setQueryMode(linkedQueryMode);
    setSelectedType("all");
    setSelectedStatus("all");
    setDateRange({ start: linkedStart, end: linkedEnd });
    setSortConfig({ key: null, direction: "asc" });
    setIsRegistrationModalOpen(false);
    setIsBulkloadPanelOpen(false);
    setIsMobileFiltersOpen(false);
    setIsDownloadMenuOpen(false);
    setIsPrintMenuOpen(false);
    setIsColumnMenuOpen(false);
    setSelectedColumnNames(null);
    setColumnConfigMessage("");
    setEditingRecord(null);
    setSelectedRecord(null);
    onViewingTrackerChange?.(null);
  }, [
    linkedEnd,
    linkedQueryMode,
    linkedSearch,
    linkedStart,
    linkedYearParam,
    onViewingTrackerChange,
    selectedTracker?.value,
  ]);

  useEffect(() => {
    const programId = selectedTracker?.value;
    if (!programId) {
      setSavedColumnConfig(null);
      setColumnConfigProgramId(null);
      setHasStoredColumnConfig(false);
      setIsLoadingColumnConfig(false);
      return undefined;
    }

    let isActive = true;
    const preferenceUrl = `/userDataStore/${TRACKER_COLUMN_PREFERENCES_NAMESPACE}/${programId}`;
    setSavedColumnConfig(null);
    setColumnConfigProgramId(null);
    setHasStoredColumnConfig(false);
    setIsLoadingColumnConfig(true);

    axios
      .get(preferenceUrl)
      .then((response) => {
        if (!isActive) return;
        const config = response?.data;
        setSavedColumnConfig(config && typeof config === "object" ? config : null);
        setColumnConfigProgramId(programId);
        setHasStoredColumnConfig(Boolean(config && typeof config === "object"));
      })
      .catch((error) => {
        if (!isActive) return;
        if (error?.response?.status !== 404) {
          console.error("Failed to load tracker column preferences:", error);
          setColumnConfigMessage("Saved column preferences could not be loaded.");
        }
        setSavedColumnConfig(null);
        setColumnConfigProgramId(programId);
        setHasStoredColumnConfig(false);
      })
      .finally(() => {
        if (isActive) setIsLoadingColumnConfig(false);
      });

    return () => {
      isActive = false;
    };
  }, [selectedTracker?.value]);

  useEffect(() => {
    if (!isColumnMenuOpen && !isDownloadMenuOpen && !isPrintMenuOpen) return undefined;

    const handleClickOutside = (event) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
        setIsColumnMenuOpen(false);
        setIsDownloadMenuOpen(false);
        setIsPrintMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isColumnMenuOpen, isDownloadMenuOpen, isPrintMenuOpen]);

  useEffect(() => {
    if (selectedRecord) return;
    onViewingTrackerChange?.(null);
  }, [onViewingTrackerChange, selectedRecord]);

  const filteredRecords = useMemo(() => {
    return normalizedRecords.filter((record) => {
      const matchesSearch = trackerRecordMatchesSearch(record, allColumns, searchTerm, queryMode);
      const matchesYear = trackerRecordMatchesYear(record, allColumns, linkedYear);

      const typeValue = typeColumn
        ? formatTrackerCellValue(typeColumn, record?.attributeValueMap?.[typeColumn])
        : "all";
      const statusValue = statusColumn
        ? formatTrackerCellValue(statusColumn, record?.attributeValueMap?.[statusColumn])
        : "all";
      const normalizedRecordDate = effectiveDateColumn
        ? normalizeTrackerDateValue(effectiveDateColumn, record?.attributeValueMap?.[effectiveDateColumn])
        : null;

      const matchesType = selectedType === "all" || typeValue === selectedType;
      const matchesStatus = selectedStatus === "all" || statusValue === selectedStatus;
      const matchesDateRange =
        (!dateRange.start || (normalizedRecordDate && normalizedRecordDate >= dateRange.start)) &&
        (!dateRange.end || (normalizedRecordDate && normalizedRecordDate <= dateRange.end));

      return matchesSearch && matchesYear && matchesType && matchesStatus && matchesDateRange;
    });
  }, [
    allColumns,
    dateColumn,
    dateRange.end,
    dateRange.start,
    effectiveDateColumn,
    linkedYear,
    normalizedRecords,
    queryMode,
    searchTerm,
    selectedStatus,
    selectedType,
    statusColumn,
    typeColumn,
  ]);

  const sortedRecords = useMemo(() => {
    if (!sortConfig?.key) return filteredRecords;

    return [...filteredRecords].sort((leftRecord, rightRecord) => {
      const leftValue = getTrackerSortValue(
        sortConfig.key,
        leftRecord?.attributeValueMap?.[sortConfig.key],
      );
      const rightValue = getTrackerSortValue(
        sortConfig.key,
        rightRecord?.attributeValueMap?.[sortConfig.key],
      );

      if (leftValue === rightValue) return 0;

      const direction = sortConfig.direction === "desc" ? -1 : 1;
      return leftValue > rightValue ? direction : -direction;
    });
  }, [filteredRecords, sortConfig]);

  const resolvedTotalPages = Math.max(1, Math.ceil(sortedRecords.length / itemsPerPage));
  const safeCurrentPage = Math.min(Math.max(currentPage, 1), resolvedTotalPages);
  const paginatedRecords = useMemo(() => {
    const startIndex = (safeCurrentPage - 1) * itemsPerPage;
    return sortedRecords.slice(startIndex, startIndex + itemsPerPage);
  }, [itemsPerPage, safeCurrentPage, sortedRecords]);

  const activeFiltersCount = [
    searchTerm !== "",
    linkedYear !== "",
    selectedType !== "all",
    selectedStatus !== "all",
    dateRange.start !== "" || dateRange.end !== "",
  ].filter(Boolean).length;

  const hasDynamicFilters = Boolean(typeColumn || statusColumn || effectiveDateColumn);
  const loadedRecordsCount = normalizedRecords.length;
  const registeredRecordsCount = typeof totalCount === "number" ? totalCount : loadedRecordsCount;
  const isBulkloadMode = isBulkloadPanelOpen;
  const isSelectedTrackerMetadataReady = Array.isArray(
    selectedTracker?.program?.programTrackedEntityAttributes,
  );
  const areTrackerActionsDisabled = isTrackerMetadataLoading || !isSelectedTrackerMetadataReady;
  const searchPlaceholder =
    filterConfig?.searchPlaceholder ||
    `Search ${selectedTracker?.label?.toLowerCase() || "tracker"} records...`;
  const clearFilters = () => {
    setSearchTerm("");
    setLinkedYear("");
    setQueryMode("");
    setSelectedType("all");
    setSelectedStatus("all");
    setDateRange({ start: "", end: "" });
  };
  const resetDisplayColumns = () => {
    setSelectedColumnNames(null);
    setColumnConfigMessage("");
  };
  const toggleDisplayColumn = (columnName) => {
    setSelectedColumnNames((prev) => {
      const currentColumns = Array.isArray(prev) ? prev : visibleColumns;
      const currentSet = new Set(currentColumns);

      if (currentSet.has(columnName)) {
        if (currentSet.size <= 1) return currentColumns;
        currentSet.delete(columnName);
      } else {
        currentSet.add(columnName);
      }

      return allColumns.filter((candidate) => currentSet.has(candidate));
    });
    setColumnConfigMessage("");
  };
  const saveDisplayColumns = async () => {
    const programId = selectedTracker?.value;
    if (!programId || isSavingColumnConfig) return;

    const preferenceUrl = `/userDataStore/${TRACKER_COLUMN_PREFERENCES_NAMESPACE}/${programId}`;
    const payload = buildTrackerColumnPreferencePayload({
      selectedTracker,
      allColumns,
      visibleColumns: displayColumns,
      savedColumnConfig: activeSavedColumnConfig,
    });

    setIsSavingColumnConfig(true);
    setColumnConfigMessage("");

    try {
      if (hasStoredColumnConfig) {
        await axios.put(preferenceUrl, payload);
      } else {
        await axios.post(preferenceUrl, payload);
      }
      setSavedColumnConfig(payload);
      setColumnConfigProgramId(programId);
      setHasStoredColumnConfig(true);
      setSelectedColumnNames(null);
      setColumnConfigMessage("");
      setIsColumnMenuOpen(false);
    } catch (error) {
      const status = error?.response?.status;

      try {
        if (hasStoredColumnConfig && status === 404) {
          await axios.post(preferenceUrl, payload);
        } else if (!hasStoredColumnConfig && status === 409) {
          await axios.put(preferenceUrl, payload);
        } else {
          throw error;
        }
        setSavedColumnConfig(payload);
        setColumnConfigProgramId(programId);
        setHasStoredColumnConfig(true);
        setSelectedColumnNames(null);
        setColumnConfigMessage("");
        setIsColumnMenuOpen(false);
      } catch (retryError) {
        console.error("Failed to save tracker column preferences:", retryError);
        setColumnConfigMessage("Column preferences could not be saved.");
      }
    } finally {
      setIsSavingColumnConfig(false);
    }
  };
  const handleDownloadRecords = (format) => {
    if (sortedRecords.length === 0 || displayColumns.length === 0) return;

    downloadTrackerRecords({
      records: sortedRecords,
      columns: displayColumns,
      selectedTracker,
      currentLocation,
      format,
    });
    setIsDownloadMenuOpen(false);
  };
  const handlePrintRecords = (orientation = printOrientation) => {
    if (sortedRecords.length === 0 || displayColumns.length === 0) return;

    setPrintOrientation(orientation);
    setIsPrintMenuOpen(false);
    window.requestAnimationFrame(() => window.print());
  };
  const handleRegistrationSuccess = async (result, meta = {}) => {
    if (meta?.intent === "continue" && meta?.recordId) {
      setIsRegistrationModalOpen(false);
      onRetry?.();

      try {
        const registeredRecord = await fetchRelationshipTrackerRecord(meta.recordId, selectedTracker?.value);
        const attributeValueMap = {
          ...buildTrackerSystemValueMap(registeredRecord, currentLocation, selectedTracker),
          ...buildAttributeValueMap(
            getTrackerRecordAttributes(registeredRecord, selectedTracker),
            activeSavedColumnConfig,
            selectedTracker,
          ),
        };
        setSelectedRecord({
          recordId: getTrackerRecordId(registeredRecord, 0),
          attributeValueMap,
          rawRecord: registeredRecord,
          allColumnsOverride: Object.keys(attributeValueMap || {}),
        });
      } catch (error) {
        console.error("Failed to open newly registered tracker record:", error);
        setSelectedRecord({
          recordId: meta.recordId,
          attributeValueMap: {},
          rawRecord: result?.payload?.trackedEntities?.[0] || { trackedEntity: meta.recordId },
          allColumnsOverride: [],
        });
      }

      return;
    }

    setTimeout(onRetry, 1500);
  };

  useEffect(() => {
    if (currentPage !== 1) {
      onPageChange?.(1);
    }
  }, [dateRange.end, dateRange.start, onPageChange, searchTerm, selectedStatus, selectedType]);

  useEffect(() => {
    if (currentPage > resolvedTotalPages) {
      onPageChange?.(resolvedTotalPages);
    }
  }, [currentPage, onPageChange, resolvedTotalPages]);

  useEffect(() => {
    onDetailActiveChange?.(Boolean(selectedRecord && !isBulkloadMode));
  }, [isBulkloadMode, onDetailActiveChange, selectedRecord]);

  useEffect(() => {
    return () => {
      onDetailActiveChange?.(false);
    };
  }, [onDetailActiveChange]);

  useEffect(() => {
    if (!selectedRecord) return;

    const refreshedRecord = normalizedRecords.find((record) => record.recordId === selectedRecord.recordId);

    if (refreshedRecord) {
      setSelectedRecord(refreshedRecord);
    }
  }, [normalizedRecords, selectedRecord]);

  if (requiresScopedOrgUnit) {
    return (
      <div className="px-4 py-4 md:px-6">
        <div className="card p-5">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-warning/10 text-warning flex items-center justify-center shrink-0">
              <Icon name="MapPinned" className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Select a District or Sub-area</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Select a district or sub-area to load records for {selectedTracker?.label || "this tracker"}.
                Choose a location from the GeoSidebar, then select the tracker again.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (selectedRecord && !isBulkloadMode) {
    return (
      <>
        <TrackerRecordDetailWorkspace
          record={selectedRecord}
          selectedTracker={selectedTracker}
          currentLocation={currentLocation}
          allColumns={selectedRecord?.allColumnsOverride || allColumns}
          trackerOptions={trackerOptions}
          onBack={() => {
            setSelectedRecord(null);
            onViewingTrackerChange?.(null);
            onRetry?.();
          }}
          onEdit={(record) => setEditingRecord(record)}
          onViewingTrackerChange={onViewingTrackerChange}
        />

        <TrackerRegistrationModal
          isOpen={Boolean(editingRecord)}
          selectedTracker={selectedTracker}
          currentOrgUnitId={currentOrgUnitId}
          editingRecord={editingRecord}
          onSuccess={() => {
            setEditingRecord(null);
            setTimeout(onRetry, 1500);
          }}
          onClose={() => setEditingRecord(null)}
        />
      </>
    );
  }

  return (
    <>
      <style>
        {`
          .tracker-records-print-sheet {
            display: none;
          }

          @media print {
            @page {
              size: ${printOrientation};
              margin: 10mm;
            }

            body * {
              visibility: hidden !important;
            }

            .tracker-records-print-sheet,
            .tracker-records-print-sheet * {
              visibility: visible !important;
            }

            .tracker-records-print-sheet {
              background: #ffffff;
              color: #111827;
              display: block !important;
              font-family: Arial, sans-serif;
              left: 0;
              padding: 0;
              position: absolute;
              top: 0;
              width: 100%;
            }

            .tracker-records-print-sheet header {
              border-bottom: 1px solid #d1d5db;
              margin-bottom: 12px;
              padding-bottom: 8px;
            }

            .tracker-records-print-sheet h1 {
              font-size: 18px;
              margin: 0;
            }

            .tracker-records-print-sheet .print-meta {
              color: #4b5563;
              font-size: 10px;
              margin-top: 5px;
            }

            .tracker-records-print-sheet table {
              border-collapse: collapse;
              font-size: 8px;
              table-layout: fixed;
              width: 100%;
            }

            .tracker-records-print-sheet th,
            .tracker-records-print-sheet td {
              border: 1px solid #d1d5db;
              padding: 4px;
              text-align: left;
              vertical-align: top;
              word-break: break-word;
            }

            .tracker-records-print-sheet th {
              background: #f3f4f6;
              font-weight: 700;
            }
          }
        `}
      </style>
      <div className={`tracker-records-print-sheet print-orientation-${printOrientation}`} aria-hidden="true">
        <header>
          <h1>{selectedTracker?.label || "Tracker Records"}</h1>
          <div className="print-meta">
            <div>Location: {formatLocationScope(currentLocation)}</div>
            <div>
              Records: {sortedRecords.length} {activeFiltersCount > 0 ? "matching" : "registered"}
            </div>
            <div>Printed: {new Date().toLocaleString("en-GB")}</div>
          </div>
        </header>
        {displayColumns.length > 0 && sortedRecords.length > 0 ? (
          <table>
            <thead>
              <tr>
                {displayColumns.map((columnName) => (
                  <th key={`records-print-column:${columnName}`}>{columnName}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedRecords.map((record) => (
                <tr key={`records-print-row:${record.recordId}`}>
                  {displayColumns.map((columnName) => (
                    <td key={`records-print-cell:${record.recordId}:${columnName}`}>
                      {formatTrackerCellValue(columnName, record?.attributeValueMap?.[columnName])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>No records available to print.</p>
        )}
      </div>
      <div className="px-3 py-3 sm:px-4 sm:py-4 md:px-6">
      <main className="container mx-auto px-0 py-2 space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground md:text-3xl">
              {isBulkloadMode ? `${selectedTracker?.label} Bulkload` : selectedTracker?.label}
            </h1>
            <p className="text-muted-foreground mt-1">
              {isBulkloadMode
                ? "Import records from an Excel template and return to the records view when you are done."
              : getTrackerWorkspaceDescription(selectedTracker)}
            </p>
          </div>
          <div className="hidden gap-2 md:flex md:flex-row">
            {isBulkloadMode ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsBulkloadPanelOpen(false)}
              >
                <Icon name="ArrowLeft" size={16} />
                Back to Records
              </Button>
            ) : (
              <>
                <Button type="button" variant="outline" onClick={onRetry} className="w-full sm:w-auto">
                  <Icon name="RefreshCw" size={16} />
                  Refresh Data
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsBulkloadPanelOpen(true)}
                  className="w-full sm:w-auto"
                  disabled={areTrackerActionsDisabled}
                >
                  <Icon name="Upload" size={16} />
                  Bulkload
                </Button>
                <Button
                  type="button"
                  onClick={() => setIsRegistrationModalOpen(true)}
                  className="w-full sm:w-auto"
                  disabled={areTrackerActionsDisabled}
                >
                  <Icon name="Plus" size={16} />
                  Register
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="md:hidden">
          {isBulkloadMode ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsBulkloadPanelOpen(false)}
              className="w-full"
            >
              <Icon name="ArrowLeft" size={16} />
              Back to Records
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                onClick={() => setIsRegistrationModalOpen(true)}
                className="h-10 flex-1"
                disabled={areTrackerActionsDisabled}
              >
                <Icon name="Plus" size={16} />
                Register
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={onRetry}
                className="h-10 w-10 p-0"
                aria-label="Refresh data"
              >
                <Icon name="RefreshCw" size={16} />
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsBulkloadPanelOpen(true)}
                className="h-10 w-10 p-0"
                aria-label="Bulkload"
                disabled={areTrackerActionsDisabled}
              >
                <Icon name="Upload" size={16} />
              </Button>
            </div>
          )}
        </div>


        {isBulkloadMode ? (
          <div className="space-y-4">
            <div className="rounded-xl border border-border bg-card px-4 py-3 shadow-sm">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-muted-foreground">
                    <Icon name="Database" size={14} />
                    <span className="font-medium text-foreground">{selectedTracker?.label}</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-muted-foreground">
                    <Icon name="MapPinned" size={14} />
                    <span className="font-medium text-foreground">{formatLocationScope(currentLocation)}</span>
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <span className="inline-flex items-center rounded-full border border-border px-2.5 py-1">
                    Excel template
                  </span>
                  <span className="inline-flex items-center rounded-full border border-border px-2.5 py-1">
                    TEI Instances sheet preferred
                  </span>
                  <span className="inline-flex items-center rounded-full border border-border px-2.5 py-1">
                    One file per import
                  </span>
                </div>
              </div>
            </div>

            <TrackerBulkloadPanel
              key={selectedTracker?.value || "bulkload-panel"}
              isOpen={isBulkloadPanelOpen}
              currentLocation={currentLocation}
              selectedTracker={selectedTracker}
              columnConfig={activeSavedColumnConfig}
              initialDateRange={dateRange}
              onImportSuccess={onRetry}
            />
          </div>
        ) : (
          <>
            <div className="card space-y-3 p-3 md:hidden">
              <div className="flex items-center gap-2">
                <div className="relative min-w-0 flex-1">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <Icon name="Search" size={16} className="text-muted-foreground" />
                  </div>
                  <Input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchTerm}
                    onChange={(event) => {
                      setSearchTerm(event?.target?.value || "");
                    }}
                    className="h-10 pl-10 pr-10"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchTerm("");
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
                      aria-label="Clear search"
                    >
                      <Icon name="X" size={16} />
                    </button>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsMobileFiltersOpen(true)}
                  className="h-10 shrink-0 px-3"
                  aria-label={`Open filters${activeFiltersCount > 0 ? `, ${activeFiltersCount} active` : ""}`}
                >
                  <Icon name="SlidersHorizontal" size={16} />
                  <span>Filters</span>
                  {activeFiltersCount > 0 ? (
                    <span className="ml-0.5 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground">
                      {activeFiltersCount}
                    </span>
                  ) : null}
                </Button>
              </div>

              {activeFiltersCount > 0 && (
                <div className="flex items-center justify-between gap-3 rounded-lg bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                  <span>
                    {activeFiltersCount} active filter{activeFiltersCount === 1 ? "" : "s"}
                  </span>
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="font-medium text-foreground transition-colors hover:text-primary"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>

            <div className="card hidden space-y-4 p-5 md:block">
              <div className="flex flex-col gap-4 lg:flex-row">
                <div className="flex-1">
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <Icon name="Search" size={16} className="text-muted-foreground" />
                    </div>
                    <Input
                      type="text"
                      placeholder={searchPlaceholder}
                      value={searchTerm}
                      onChange={(event) => {
                        setSearchTerm(event?.target?.value || "");
                      }}
                      className="pl-10 pr-10"
                    />
                    {searchTerm && (
                      <button
                        type="button"
                        onClick={() => {
                          setSearchTerm("");
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <Icon name="X" size={16} />
                      </button>
                    )}
                  </div>
                  {linkedYear ? (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Year under review: <span className="font-semibold text-foreground">{linkedYear}</span>
                    </p>
                  ) : null}
                </div>

                <div className="flex items-end gap-2">
                  {activeFiltersCount > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full sm:w-auto"
                      onClick={clearFilters}
                    >
                      <Icon name="X" size={16} />
                      Clear Filters ({activeFiltersCount})
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Select
                  label={filterConfig?.type?.label || "Type"}
                  options={typeOptions}
                  value={selectedTypeOption}
                  onChange={(option) => {
                    setSelectedType(option?.value || "all");
                  }}
                  placeholder={
                    typeColumn
                      ? `Select ${(filterConfig?.type?.label || typeColumn).toLowerCase()}`
                      : "Type unavailable"
                  }
                  disabled={!typeColumn}
                />

                <Select
                  label={filterConfig?.status?.label || "Status"}
                  options={statusOptions}
                  value={selectedStatusOption}
                  onChange={(option) => {
                    setSelectedStatus(option?.value || "all");
                  }}
                  placeholder={
                    statusColumn
                      ? `Select ${(filterConfig?.status?.label || statusColumn).toLowerCase()}`
                      : "Status unavailable"
                  }
                  disabled={!statusColumn}
                />

                <Input
                  type="date"
                  label={filterConfig?.date?.startLabel || "Start Date"}
                  value={dateRange.start}
                  onChange={(event) => {
                    setDateRange((prev) => ({ ...prev, start: event?.target?.value || "" }));
                  }}
                  disabled={!effectiveDateColumn}
                />

                <Input
                  type="date"
                  label={filterConfig?.date?.endLabel || "End Date"}
                  value={dateRange.end}
                  onChange={(event) => {
                    setDateRange((prev) => ({ ...prev, end: event?.target?.value || "" }));
                  }}
                  disabled={!effectiveDateColumn}
                />
              </div>

              {!hasDynamicFilters && (
                <p className="text-xs text-muted-foreground">
                  Search is the main filter available for this tracker right now.
                </p>
              )}
            </div>

            {isMobileFiltersOpen && createPortal(
              <>
                <div
                  className="fixed inset-0 z-[1200] bg-black/50 backdrop-blur-[2px] md:hidden"
                  onClick={() => setIsMobileFiltersOpen(false)}
                />
                <div className="fixed inset-x-0 bottom-0 z-[1300] max-h-[88dvh] overflow-y-auto rounded-t-2xl border border-border bg-card p-4 shadow-xl md:hidden">
                  <div className="mb-4 flex items-start justify-between gap-4">
                    <div>
                      <h2 className="text-base font-semibold text-foreground">Filters</h2>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Narrow the tracker records shown below.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsMobileFiltersOpen(false)}
                      className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      aria-label="Close filters"
                    >
                      <Icon name="X" size={18} />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <Select
                      label={filterConfig?.type?.label || "Type"}
                      options={typeOptions}
                      value={selectedTypeOption}
                      onChange={(option) => {
                        setSelectedType(option?.value || "all");
                      }}
                      placeholder={
                        typeColumn
                          ? `Select ${(filterConfig?.type?.label || typeColumn).toLowerCase()}`
                          : "Type unavailable"
                      }
                      disabled={!typeColumn}
                    />

                    <Select
                      label={filterConfig?.status?.label || "Status"}
                      options={statusOptions}
                      value={selectedStatusOption}
                      onChange={(option) => {
                        setSelectedStatus(option?.value || "all");
                      }}
                      placeholder={
                        statusColumn
                          ? `Select ${(filterConfig?.status?.label || statusColumn).toLowerCase()}`
                          : "Status unavailable"
                      }
                      disabled={!statusColumn}
                    />

                    <div className="grid grid-cols-1 gap-4">
                      <Input
                        type="date"
                        label={filterConfig?.date?.startLabel || "Start Date"}
                        value={dateRange.start}
                        onChange={(event) => {
                          setDateRange((prev) => ({ ...prev, start: event?.target?.value || "" }));
                        }}
                        disabled={!effectiveDateColumn}
                      />

                      <Input
                        type="date"
                        label={filterConfig?.date?.endLabel || "End Date"}
                        value={dateRange.end}
                        onChange={(event) => {
                          setDateRange((prev) => ({ ...prev, end: event?.target?.value || "" }));
                        }}
                        disabled={!effectiveDateColumn}
                      />
                    </div>

                    {!hasDynamicFilters && (
                      <p className="rounded-lg bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                        Search is the main filter available for this tracker right now.
                      </p>
                    )}
                  </div>

                  <div className="mt-5 grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={clearFilters}
                      disabled={activeFiltersCount === 0}
                      className="w-full"
                    >
                      Clear
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setIsMobileFiltersOpen(false)}
                      className="w-full"
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              </>,
              document.body,
            )}

            {isLoading && (
              <div className="card p-5 text-sm text-muted-foreground flex items-center gap-2">
                <Icon name="Loader2" className="w-4 h-4 animate-spin" />
                Loading tracker records...
              </div>
            )}

            {!isLoading && error && (
              <div className="card p-5 border border-destructive/20">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-destructive/10 text-destructive flex items-center justify-center shrink-0">
                    <Icon name="AlertCircle" className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-foreground">Unable to Load Tracker Records</h3>
                    <p className="text-sm text-muted-foreground mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {!error && !isLoading && loadedRecordsCount === 0 && (
              <div className="card p-5">
                <div className="rounded-lg border border-border bg-muted/30 px-4 py-4 text-sm text-muted-foreground">
                  No records were returned for this tracker at the selected location.
                </div>
              </div>
            )}

            {!error && !isLoading && loadedRecordsCount > 0 && visibleColumns.length === 0 && (
              <div className="card p-5">
                <div className="rounded-lg border border-border bg-muted/30 px-4 py-4 text-sm text-muted-foreground">
                  Records were found, but there are no fields ready to display in the table yet.
                </div>
              </div>
            )}

            {!error && !isLoading && allColumns.length > 0 && (
              <div className="relative flex flex-col justify-end gap-2 sm:flex-row sm:items-center" ref={columnMenuRef}>
                <div className="relative">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIsDownloadMenuOpen((prev) => !prev);
                      setIsColumnMenuOpen(false);
                      setIsPrintMenuOpen(false);
                    }}
                    disabled={sortedRecords.length === 0 || displayColumns.length === 0}
                    className="w-full sm:w-auto"
                  >
                    <Icon name="Download" size={15} />
                    Download
                    <Icon name="ChevronDown" size={14} />
                  </Button>
                  {isDownloadMenuOpen && (
                    <div className="absolute right-0 top-11 z-50 w-full min-w-40 overflow-hidden rounded-lg border border-border bg-background shadow-lg sm:w-44">
                      {[
                        { format: "csv", label: "CSV" },
                        { format: "json", label: "JSON" },
                        { format: "xml", label: "XML" },
                      ].map((option) => (
                        <button
                          key={`download-format:${option.format}`}
                          type="button"
                          onClick={() => handleDownloadRecords(option.format)}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground hover:bg-muted/70"
                        >
                          <Icon name="FileDown" size={14} />
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="relative">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIsPrintMenuOpen((prev) => !prev);
                      setIsDownloadMenuOpen(false);
                      setIsColumnMenuOpen(false);
                    }}
                    disabled={sortedRecords.length === 0 || displayColumns.length === 0}
                    className="w-full sm:w-auto"
                  >
                    <Icon name="Printer" size={15} />
                    Print
                    <Icon name="ChevronDown" size={14} />
                  </Button>
                  {isPrintMenuOpen && (
                    <div className="absolute right-0 top-11 z-50 w-full min-w-44 overflow-hidden rounded-lg border border-border bg-background shadow-lg sm:w-48">
                      {[
                        { orientation: "portrait", label: "Portrait", icon: "PanelTop" },
                        { orientation: "landscape", label: "Landscape", icon: "PanelLeft" },
                      ].map((option) => (
                        <button
                          key={`print-orientation:${option.orientation}`}
                          type="button"
                          onClick={() => handlePrintRecords(option.orientation)}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground hover:bg-muted/70"
                        >
                          <Icon name={option.icon} size={14} />
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsColumnMenuOpen((prev) => !prev);
                    setIsDownloadMenuOpen(false);
                    setIsPrintMenuOpen(false);
                  }}
                  className="w-full sm:w-auto"
                >
                  <Icon name="Columns3" size={15} />
                  Columns
                  {hiddenDisplayColumnCount > 0 ? (
                    <span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                      {displayColumns.length}/{allColumns.length}
                    </span>
                  ) : null}
                </Button>

                {isColumnMenuOpen && (
                  <div className="absolute right-0 top-11 z-50 w-full max-w-sm rounded-lg border border-border bg-background shadow-lg sm:w-80">
                    <div className="flex items-start justify-between gap-3 border-b border-border px-3 py-3">
                       <div>
                         <p className="text-sm font-semibold text-foreground">Columns</p>
                         <p className="mt-0.5 text-xs text-muted-foreground">
                           {isLoadingColumnConfig
                             ? "Loading your saved columns..."
                             : "Choose and save the fields shown in the records table."}
                         </p>
                       </div>
                       <div className="flex shrink-0 items-center gap-1">
                         <Button
                           type="button"
                           variant="ghost"
                           size="sm"
                           onClick={resetDisplayColumns}
                           disabled={!Array.isArray(selectedColumnNames) || isSavingColumnConfig}
                           className="h-8 px-2"
                         >
                           Reset
                         </Button>
                         <Button
                           type="button"
                           size="sm"
                           onClick={saveDisplayColumns}
                           disabled={isLoadingColumnConfig || isSavingColumnConfig}
                           className="h-8 px-2.5"
                         >
                           <Icon
                             name={isSavingColumnConfig ? "Loader2" : "Save"}
                             size={14}
                             className={isSavingColumnConfig ? "animate-spin" : ""}
                           />
                           {isSavingColumnConfig ? "Saving" : "Save"}
                         </Button>
                       </div>
                    </div>
                     <div className="max-h-72 overflow-y-auto p-2">
                      {allColumns.map((columnName) => {
                        const isChecked = displayColumns.includes(columnName);
                        const isLastChecked = isChecked && displayColumns.length <= 1;

                        return (
                          <label
                            key={`column-toggle:${columnName}`}
                            className="flex cursor-pointer items-start gap-2 rounded-md px-2 py-2 text-sm text-foreground hover:bg-muted/60"
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              disabled={isLastChecked}
                              onChange={() => toggleDisplayColumn(columnName)}
                              className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary"
                            />
                            <span className="min-w-0 flex-1 break-words">{columnName}</span>
                          </label>
                        );
                       })}
                     </div>
                     {columnConfigMessage && (
                       <p className="border-t border-border px-3 py-2 text-xs text-muted-foreground">
                         {columnConfigMessage}
                       </p>
                     )}
                   </div>
                )}
              </div>
            )}

              {!error && displayColumns.length > 0 && (
              <>
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between text-sm text-muted-foreground">
                  <p>
                    Showing{" "}
                    <span className="font-semibold text-foreground">
                      {sortedRecords.length === 0 ? 0 : (safeCurrentPage - 1) * itemsPerPage + 1}
                    </span>{" "}
                    -{" "}
                    <span className="font-semibold text-foreground">
                      {Math.min(safeCurrentPage * itemsPerPage, sortedRecords.length)}
                    </span>{" "}
                    of <span className="font-semibold text-foreground">{sortedRecords.length}</span>{" "}
                    {activeFiltersCount > 0 ? "matching" : "registered"} records
                  </p>
                  {registeredRecordsCount > sortedRecords.length ? (
                    <span>
                      Total registered: <span className="font-semibold text-foreground">{registeredRecordsCount}</span>
                    </span>
                  ) : null}
                </div>

                <TrackerRecordsTable
                  records={paginatedRecords}
                  visibleColumns={displayColumns}
                  allColumns={allColumns}
                  sortConfig={sortConfig}
                  onOpenRecord={(record) => setSelectedRecord(record)}
                  onEdit={(record) => setEditingRecord(record)}
                  onSortChange={(columnName) =>
                    setSortConfig((prev) => ({
                      key: columnName,
                      direction:
                        prev?.key === columnName && prev?.direction === "asc" ? "desc" : "asc",
                    }))
                  }
                  emptyTitle={loadedRecordsCount === 0 ? "No records found" : "No matching records"}
                  emptyDescription={
                    loadedRecordsCount === 0
                      ? "No records were returned for this tracker at the selected location."
                      : "Try adjusting your search terms or filters."
                  }
                />

                {hiddenDisplayColumnCount > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Showing {displayColumns.length} of {allColumns.length} available columns in the main table.
                  </p>
                )}

                {resolvedTotalPages > 1 && (
                  <div className="card">
                    <Pagination
                      currentPage={safeCurrentPage}
                      totalPages={resolvedTotalPages}
                      totalItems={sortedRecords.length}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}

        <TrackerRegistrationModal
          isOpen={isRegistrationModalOpen}
          selectedTracker={selectedTracker}
          currentOrgUnitId={currentOrgUnitId}
          onSuccess={handleRegistrationSuccess}
          onClose={() => setIsRegistrationModalOpen(false)}
        />

        <TrackerRegistrationModal
          isOpen={Boolean(editingRecord)}
          selectedTracker={selectedTracker}
          currentOrgUnitId={currentOrgUnitId}
          editingRecord={editingRecord}
          onSuccess={() => {
            setEditingRecord(null);
            setTimeout(onRetry, 1500);
          }}
          onClose={() => setEditingRecord(null)}
        />
      </main>
      </div>
    </>
  );
}

function getBulkloadFeedbackStyles(tone) {
  switch (tone) {
    case "success":
      return {
        wrapper: "border border-success/20 bg-success/10",
        icon: "text-success",
        title: "text-success",
        body: "text-success/90",
        iconName: "CheckCircle",
      };
    case "warning":
      return {
        wrapper: "border border-warning/20 bg-warning/10",
        icon: "text-warning",
        title: "text-warning",
        body: "text-warning/90",
        iconName: "AlertCircle",
      };
    case "error":
      return {
        wrapper: "border border-destructive/20 bg-destructive/10",
        icon: "text-destructive",
        title: "text-destructive",
        body: "text-destructive/90",
        iconName: "AlertCircle",
      };
    default:
      return {
        wrapper: "border border-border bg-muted/30",
        icon: "text-primary",
        title: "text-foreground",
        body: "text-muted-foreground",
        iconName: "Info",
      };
  }
}

function formatBulkloadPreviewCell(value) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  return String(value);
}

function TrackerBulkloadPanel({
  isOpen,
  currentLocation,
  selectedTracker,
  columnConfig,
  initialDateRange = { start: "", end: "" },
  onImportSuccess,
}) {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [preparedImport, setPreparedImport] = useState(null);
  const [validationIssues, setValidationIssues] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [previewPage, setPreviewPage] = useState(1);
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [templateWithDataStartDate, setTemplateWithDataStartDate] = useState("");
  const [templateWithDataEndDate, setTemplateWithDataEndDate] = useState("");
  const [importStatusText, setImportStatusText] = useState("");

  useEffect(() => {
    setTemplateWithDataStartDate(initialDateRange?.start || "");
    setTemplateWithDataEndDate(initialDateRange?.end || "");
  }, [initialDateRange?.end, initialDateRange?.start, selectedTracker?.value]);

  const bulkloadConfig = useMemo(
    () => getTrackerBulkloadConfig(selectedTracker),
    [selectedTracker],
  );
  const currentOrgUnitId = currentLocation?.orgUnitId || null;
  const trackerMetadataReady = Boolean(
    selectedTracker?.value && selectedTracker?.program?.trackedEntityType?.id,
  );
  const blockingMessage = !selectedTracker
    ? "Select a tracker to begin."
    : !bulkloadConfig
      ? "Bulkload is not ready yet for this tracker."
      : !currentOrgUnitId
        ? "Choose a district or sub-area before importing data."
        : !trackerMetadataReady
          ? "Tracker details are still loading. Refresh and try again."
          : "";
  const visibleValidationIssues = validationIssues.slice(0, 6);
  const remainingValidationIssues = Math.max(validationIssues.length - visibleValidationIssues.length, 0);
  const previewRows = useMemo(() => previewData?.rows || [], [previewData]);
  const previewColumns = useMemo(
    () => (previewData ? getBulkloadPreviewColumns(previewData) : []),
    [previewData]
  );
  const previewItemsPerPage = 6;
  const previewTotalPages = Math.ceil(previewRows.length / previewItemsPerPage);
  const paginatedPreviewRows = useMemo(() => {
    if (!previewRows.length) return [];

    const startIndex = (previewPage - 1) * previewItemsPerPage;
    return previewRows.slice(startIndex, startIndex + previewItemsPerPage);
  }, [previewPage, previewRows]);

  useEffect(() => {
    if (!isOpen) {
      setIsDragging(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!feedback || !isOpen) {
      return undefined;
    }

    if (feedback.tone !== "success") {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setFeedback((currentFeedback) => {
        if (
          currentFeedback?.tone === feedback.tone &&
          currentFeedback?.title === feedback.title &&
          currentFeedback?.message === feedback.message
        ) {
          return null;
        }

        return currentFeedback;
      });
    }, 5000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [feedback, isOpen]);

  useEffect(() => {
    if (previewTotalPages === 0) {
      setPreviewPage(1);
      return;
    }

    if (previewPage > previewTotalPages) {
      setPreviewPage(previewTotalPages);
    }
  }, [previewPage, previewTotalPages]);

  const resetBulkloadState = () => {
    setSelectedFile(null);
    setPreviewData(null);
    setPreparedImport(null);
    setValidationIssues([]);
    setPreviewPage(1);
    setImportStatusText("");

    if (fileInputRef?.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileUpload = async (file, options = {}) => {
    if (!file || blockingMessage) {
      return;
    }

    const { fromMultipleSelection = false } = options;

    setIsParsing(true);
    setImportStatusText("");
    setFeedback(null);
    setValidationIssues([]);

    try {
      const parsedWorkbook = await parseTrackerBulkloadWorkbook(file);
      const nextPreparedImport = await buildTrackerBulkloadPayload({
        headers: parsedWorkbook.headers,
        attributeIds: parsedWorkbook.attributeIds,
        attributeColumns: parsedWorkbook.attributeColumns,
        systemColumnIndexes: parsedWorkbook.systemColumnIndexes,
        rows: parsedWorkbook.rows,
        timelineSheets: parsedWorkbook.timelineSheets,
        selectedTracker,
        columnConfig,
        orgUnitId: currentOrgUnitId,
      });

      setSelectedFile(file);
      setPreviewData(parsedWorkbook);
      setPreparedImport(nextPreparedImport);
      setValidationIssues(nextPreparedImport.validationErrors);
      setPreviewPage(1);

      const multipleSelectionMessage = fromMultipleSelection
        ? "Only the first file was used because bulkload currently imports one file at a time. "
        : "";
      const sheetMessage = parsedWorkbook.matchesExpectedSheet
        ? ""
        : `Using the "${parsedWorkbook.sheetName}" sheet for this preview. `;
      const timelineMessage =
        parsedWorkbook.timelineSheetCount > 0
          ? `${parsedWorkbook.timelineSheetCount} timeline sheet${
              parsedWorkbook.timelineSheetCount === 1 ? "" : "s"
            } included. `
          : "";
      const validationMessage =
        nextPreparedImport.validationErrors.length > 0
          ? `${nextPreparedImport.validationErrors.length} row${
              nextPreparedImport.validationErrors.length === 1 ? "" : "s"
            } need attention before import.`
          : "The file is ready to import.";

      setFeedback({
        tone: nextPreparedImport.validationErrors.length > 0 ? "warning" : "success",
        title:
          nextPreparedImport.validationErrors.length > 0
            ? "Review this file before import"
            : "File ready for import",
        message: `${multipleSelectionMessage}${sheetMessage}${timelineMessage}${validationMessage}`.trim(),
      });
    } catch (error) {
      resetBulkloadState();
      setFeedback({
        tone: "error",
        title: "Could not read this file",
        message: error?.message || "The selected file could not be prepared for bulkload.",
      });
    } finally {
      setIsParsing(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event?.target?.files?.[0] || null;

    if (event?.target) {
      event.target.value = "";
    }

    await handleFileUpload(file);
  };

  const handleDragOver = (event) => {
    event?.preventDefault();
    if (blockingMessage || isParsing || isImporting) return;
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event?.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event?.preventDefault();
    setIsDragging(false);

    if (blockingMessage || isParsing || isImporting) {
      return;
    }

    const droppedFiles = Array.from(event?.dataTransfer?.files || []).filter(Boolean);
    const [firstFile] = droppedFiles;

    handleFileUpload(firstFile, { fromMultipleSelection: droppedFiles.length > 1 });
  };

  const handleDownloadTemplate = async () => {
    if (!selectedTracker || isDownloadingTemplate) return;

    setIsDownloadingTemplate(true);
    setFeedback(null);

    try {
      await downloadTrackerBulkloadTemplate(selectedTracker, columnConfig);
    } catch (error) {
      console.error("Failed to generate tracker bulkload template:", error);
      setFeedback({
        tone: "error",
        title: "Template download failed",
        message: error?.message || "The Excel template could not be generated. Please try again.",
      });
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleDownloadTemplateWithData = async () => {
    if (!selectedTracker || isDownloadingTemplate) return;

    if (!currentOrgUnitId) {
      setFeedback({
        tone: "error",
        title: "Template download failed",
        message: "Choose a district or sub-area before downloading data.",
      });
      return;
    }

    if (!templateWithDataStartDate || !templateWithDataEndDate) {
      setFeedback({
        tone: "error",
        title: "Template download failed",
        message: "Select a registration date range before downloading data.",
      });
      return;
    }

    if (templateWithDataStartDate > templateWithDataEndDate) {
      setFeedback({
        tone: "error",
        title: "Template download failed",
        message: "The start date must be on or before the end date.",
      });
      return;
    }

    setIsDownloadingTemplate(true);
    setFeedback(null);

    try {
      await downloadTrackerBulkloadTemplateWithData(
        selectedTracker,
        columnConfig,
        currentOrgUnitId,
        templateWithDataStartDate,
        templateWithDataEndDate,
      );
      setFeedback({
        tone: "success",
        title: "Template downloaded",
        message: `Records with registration dates from ${templateWithDataStartDate} to ${templateWithDataEndDate} were included.`,
      });
    } catch (error) {
      console.error("Failed to generate tracker bulkload template with data:", error);
      setFeedback({
        tone: "error",
        title: "Template download failed",
        message: error?.message || "The Excel template with data could not be generated.",
      });
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleImport = async () => {
    if (!preparedImport || validationIssues.length > 0 || isImporting) {
      return;
    }

    const importedRowCount = previewData?.rowCount || preparedImport?.trackedEntities?.length || 0;

    setIsImporting(true);
    setImportStatusText("Submitting your bulkload file...");
    setFeedback({
      tone: "info",
      title: "Bulkload in progress",
      message: "Your file is being submitted for import.",
    });

    try {
      await runTrackerBulkloadImport(preparedImport.payload, {
        fieldLabelMap: preparedImport.fieldLabelMap,
        onStatus: (report) => {
          const nextStatus = String(report?.status || "").trim();
          setImportStatusText(
            nextStatus ? `Checking the import job... (${nextStatus})` : "Checking the import job...",
          );
        },
      });

      resetBulkloadState();
      setFeedback({
        tone: "success",
        title: "Bulkload completed",
        message: `${importedRowCount} row${importedRowCount === 1 ? "" : "s"} were imported successfully.`,
      });
      onImportSuccess?.();
    } catch (error) {
      setFeedback({
        tone: "error",
        title: "Bulkload failed",
        message: error?.message || "The file could not be imported. Please try again.",
      });
    } finally {
      setIsImporting(false);
      setImportStatusText("");
    }
  };

  if (!isOpen) {
    return null;
  }

  const feedbackStyles = feedback ? getBulkloadFeedbackStyles(feedback.tone) : null;

  return (
    <div className="card space-y-5 border border-border/80 p-5 md:p-6 shadow-sm">
      <div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Upload File</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Choose an Excel template, review the mapped rows, and import the file.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleDownloadTemplate}
            disabled={!selectedTracker || isDownloadingTemplate}
          >
            <Icon
              name={isDownloadingTemplate ? "Loader2" : "Download"}
              size={16}
              className={isDownloadingTemplate ? "animate-spin" : ""}
            />
            {isDownloadingTemplate ? "Preparing..." : "Download Template"}
          </Button>
        </div>

        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-muted-foreground">Registration from</span>
              <Input
                type="date"
                value={templateWithDataStartDate}
                onChange={(e) => setTemplateWithDataStartDate(e?.target?.value || "")}
                disabled={!selectedTracker || !currentOrgUnitId || isDownloadingTemplate}
                max={templateWithDataEndDate || undefined}
                style={{ width: 170 }}
              />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-muted-foreground">Registration to</span>
              <Input
                type="date"
                value={templateWithDataEndDate}
                onChange={(e) => setTemplateWithDataEndDate(e?.target?.value || "")}
                disabled={!selectedTracker || !currentOrgUnitId || isDownloadingTemplate}
                min={templateWithDataStartDate || undefined}
                style={{ width: 170 }}
              />
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleDownloadTemplateWithData}
            disabled={
              !selectedTracker ||
              !currentOrgUnitId ||
              !templateWithDataStartDate ||
              !templateWithDataEndDate ||
              isDownloadingTemplate
            }
          >
            <Icon
              name={isDownloadingTemplate ? "Loader2" : "Download"}
              size={16}
              className={isDownloadingTemplate ? "animate-spin" : ""}
            />
            {isDownloadingTemplate ? "Preparing..." : "Download Template with Data"}
          </Button>
        </div>
      </div>

      {feedback && feedbackStyles && (
        <div className={`rounded-xl px-4 py-4 ${feedbackStyles.wrapper}`}>
          <div className="flex items-start gap-3">
            <Icon name={feedbackStyles.iconName} size={18} className={feedbackStyles.icon} />
            <div className="min-w-0 flex-1">
              <p className={`font-medium ${feedbackStyles.title}`}>{feedback.title}</p>
              <p className={`mt-1 whitespace-pre-wrap break-words text-sm ${feedbackStyles.body}`}>
                {feedback.message}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setFeedback(null)}
              className={`rounded-md p-1 transition-colors hover:bg-background/60 ${feedbackStyles.body}`}
              aria-label="Dismiss bulkload message"
              title="Dismiss"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>
      )}

      {blockingMessage ? (
        <div className="rounded-2xl border border-warning/20 bg-warning/10 px-4 py-4">
          <div className="flex items-start gap-3">
            <Icon name="AlertCircle" size={18} className="mt-0.5 text-warning" />
            <div>
              <p className="font-medium text-warning">Bulkload unavailable</p>
              <p className="mt-1 text-sm text-warning/90">{blockingMessage}</p>
            </div>
          </div>
        </div>
      ) : (
        <div
          className={[
            "rounded-2xl border-2 border-dashed p-6 transition-colors",
            isDragging
              ? "border-primary bg-primary/5"
              : "border-border bg-muted/20 hover:border-primary/40",
          ]
            .filter(Boolean)
            .join(" ")}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {selectedFile && previewData ? (
            <div className="space-y-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-success/10 text-success">
                    <Icon name={isImporting ? "Loader2" : "FileCheck"} className={isImporting ? "animate-spin" : ""} size={22} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="truncate text-base font-semibold text-foreground">
                      {selectedFile.name}
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      This file is staged for bulkload. You can import it now or replace it with another template.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs text-muted-foreground">
                      <span className="inline-flex items-center rounded-full bg-background px-2.5 py-1">
                        {formatTrackerBulkloadFileSize(selectedFile.size)}
                      </span>
                      <span className="inline-flex items-center rounded-full bg-background px-2.5 py-1">
                        {previewData.rowCount} row{previewData.rowCount === 1 ? "" : "s"}
                      </span>
                      <span className="inline-flex items-center rounded-full bg-background px-2.5 py-1">
                        {previewColumns.length || previewData.columnCount} column
                        {(previewColumns.length || previewData.columnCount) === 1 ? "" : "s"}
                      </span>
                      <span className="inline-flex items-center rounded-full bg-background px-2.5 py-1">
                        Sheet: {previewData.sheetName}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef?.current?.click()}
                    disabled={isParsing || isImporting}
                  >
                    <Icon name="RefreshCw" size={16} />
                    Replace File
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetBulkloadState}
                    disabled={isImporting}
                  >
                    <Icon name="Trash2" size={16} />
                    Clear
                  </Button>
                  <Button
                    type="button"
                    onClick={handleImport}
                    disabled={!preparedImport || validationIssues.length > 0 || isImporting || isParsing}
                  >
                    <Icon
                      name={isImporting ? "Loader2" : "Upload"}
                      className={isImporting ? "animate-spin" : ""}
                      size={16}
                    />
                    {isImporting ? "Importing..." : "Import Data"}
                  </Button>
                </div>
              </div>

              <div className="rounded-xl border border-border/70 bg-background/80 px-4 py-3 text-sm text-muted-foreground">
                Drop a new file anywhere in this bulkload area, or use Replace File to swap the current one.
              </div>

              {importStatusText && (
                <p className="text-sm text-muted-foreground">{importStatusText}</p>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-4 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-background text-muted-foreground shadow-sm">
                <Icon
                  name={isParsing ? "Loader2" : "FileSpreadsheet"}
                  className={isParsing ? "animate-spin" : ""}
                  size={28}
                />
              </div>

              <div className="space-y-1">
                <h3 className="text-lg font-semibold text-foreground">
                  {isParsing ? "Reading your workbook..." : "Drop your bulkload file here"}
                </h3>
                <p className="text-sm text-muted-foreground">
                  Drag and drop the tracker template here, or choose it from your device to preview before import.
                </p>
              </div>

              <div className="flex flex-col items-center gap-3 sm:flex-row">
                <Button
                  type="button"
                  onClick={() => fileInputRef?.current?.click()}
                  disabled={isParsing || isImporting}
                >
                  <Icon name="Upload" size={16} />
                  Choose File
                </Button>

                <span className="text-xs text-muted-foreground">
                  Only one file will be imported at a time.
                </span>
              </div>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      )}

      {selectedFile && previewData && (
        <div className="space-y-4">
          {validationIssues.length > 0 && (
            <div className="rounded-2xl border border-warning/20 bg-warning/10 px-4 py-4">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={18} className="mt-0.5 text-warning" />
                <div className="min-w-0">
                  <p className="font-medium text-warning">
                    {validationIssues.length} row{validationIssues.length === 1 ? "" : "s"} need attention before import
                  </p>
                  <div className="mt-2 space-y-1 text-sm text-warning/90">
                    {visibleValidationIssues.map((issue) => (
                      <p key={issue}>{issue}</p>
                    ))}
                    {remainingValidationIssues > 0 && (
                      <p>
                        {remainingValidationIssues} more row{remainingValidationIssues === 1 ? "" : "s"} still need attention.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="overflow-hidden rounded-2xl border border-border bg-background">
            <div className="border-b border-border px-4 py-3">
              <h3 className="text-sm font-semibold text-foreground">File Preview</h3>
              <p className="mt-1 text-xs text-muted-foreground">
                Review the mapped spreadsheet rows before importing them into {selectedTracker?.label}.
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">#</th>
                    {(previewColumns || []).map((column) => (
                      <th
                        key={`bulkload-header-${column.columnIndex}`}
                        className="px-4 py-3 text-left font-medium text-muted-foreground whitespace-nowrap"
                      >
                        {column.header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedPreviewRows.map((row, rowIndex) => {
                    const actualRowNumber = (previewPage - 1) * previewItemsPerPage + rowIndex + 1;

                    return (
                      <tr key={`bulkload-row-${actualRowNumber}`} className="border-t border-border/70">
                        <td className="px-4 py-3 font-medium text-foreground whitespace-nowrap">
                          {actualRowNumber}
                        </td>
                        {(previewColumns || []).map((column) => (
                          <td
                            key={`bulkload-row-${actualRowNumber}-cell-${column.columnIndex}`}
                            className="max-w-[240px] px-4 py-3 text-muted-foreground align-top"
                          >
                            <div className="break-words whitespace-pre-wrap">
                              {formatBulkloadPreviewCell(row[column.columnIndex])}
                            </div>
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {previewTotalPages > 1 && (
              <Pagination
                currentPage={previewPage}
                totalPages={previewTotalPages}
                totalItems={previewRows.length}
                itemsPerPage={previewItemsPerPage}
                onPageChange={setPreviewPage}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TrackerRegistrationModal({
  isOpen,
  selectedTracker,
  currentOrgUnitId,
  editingRecord = null,
  onClose,
  onSuccess,
}) {
  useEffect(() => {
    if (!isOpen || typeof document === "undefined") {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose?.();
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !selectedTracker || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <>
      <div className="fixed inset-0 z-[1200] bg-black/55 backdrop-blur-[2px]" onClick={onClose} />
      <div className="fixed inset-0 z-[1300] overflow-y-auto p-4 md:p-6">
        <div className="flex min-h-full items-start justify-center">
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`${selectedTracker?.label || "Tracker"} registration`}
            className="relative w-full max-w-6xl py-4"
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute right-4 top-8 z-10 rounded-full border border-border bg-background/95 p-2 text-muted-foreground shadow-sm transition-colors hover:bg-muted hover:text-foreground"
            >
              <span className="sr-only">Close registration modal</span>
              <Icon name="X" size={18} />
            </button>

            <TrackerRegistrationWorkspace
              trackerId={selectedTracker?.value}
              trackerLabel={selectedTracker?.label}
              programMetadata={selectedTracker?.program || null}
              currentOrgUnitId={currentOrgUnitId}
              editingRecord={editingRecord}
              onSuccess={onSuccess}
              onCancel={onClose}
              cancelLabel="Close"
              className="rounded-2xl shadow-xl"
            />
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

export default function TrackersModuleAdapter() {
  const { currentLocation } = useLocationContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const [trackerProgramIndex, setTrackerProgramIndex] = useState([]);
  const [trackerMetadataById, setTrackerMetadataById] = useState({});
  const [viewingTrackerOverride, setViewingTrackerOverride] = useState(null);
  const [isTrackerListLoading, setIsTrackerListLoading] = useState(false);
  const [trackerListError, setTrackerListError] = useState("");
  const [isSelectedTrackerMetadataLoading, setIsSelectedTrackerMetadataLoading] = useState(false);

  const [trackerRecords, setTrackerRecords] = useState([]);
  const [trackerRecordsTotal, setTrackerRecordsTotal] = useState(null);
  const [trackerRecordsTotalPages, setTrackerRecordsTotalPages] = useState(null);
  const [isTrackerRecordsLoading, setIsTrackerRecordsLoading] = useState(false);
  const [trackerRecordsError, setTrackerRecordsError] = useState("");
  const [trackerRecordsPage, setTrackerRecordsPage] = useState(1);
  const [recordsReloadKey, setRecordsReloadKey] = useState(0);
  const [isTrackerDetailActive, setIsTrackerDetailActive] = useState(false);

  const currentOrgUnitId = currentLocation?.orgUnitId || null;
  const currentLocationLevel = currentLocation?.level || "national";
  const trackerOptions = useMemo(() => {
    const options = normalizeTrackerProgramOptions(trackerProgramIndex, {
      level: currentLocationLevel,
      orgUnitId: currentOrgUnitId,
    });

    return options.map((option) => {
      const fullProgramMetadata = trackerMetadataById[option.value];
      if (!fullProgramMetadata) return option;

      return {
        ...option,
        label: fullProgramMetadata.displayName || fullProgramMetadata.name || option.label,
        description: fullProgramMetadata?.trackedEntityType?.displayName
          ? `${fullProgramMetadata.trackedEntityType.displayName} tracker`
          : option.description,
        program: fullProgramMetadata,
        attributeOrder: buildProgramAttributeOrder(fullProgramMetadata),
      };
    });
  }, [currentLocationLevel, currentOrgUnitId, trackerMetadataById, trackerProgramIndex]);

  const selectedTracker = useMemo(
    () => getSelectedTrackerOption(searchParams, trackerOptions),
    [searchParams, trackerOptions],
  );

  const selectedTrackerId = selectedTracker?.value || null;
  const displayedTracker = viewingTrackerOverride || selectedTracker;
  const handleViewingTrackerChange = useCallback((trackerOption) => {
    setViewingTrackerOverride(trackerOption || null);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadTrackerPrograms = async () => {
      setIsTrackerListLoading(true);
      setTrackerListError("");

      try {
        const response = await axios.get("/programs", {
          params: {
            paging: false,
            fields: TRACKER_PROGRAM_INDEX_FIELDS,
          },
        });

        const programs =
          response?.data?.programs ||
          response?.data?.organisationUnits ||
          response?.data?.items ||
          [];

        if (isMounted) {
          setTrackerProgramIndex(programs);
        }
      } catch (error) {
        console.error("Failed to load tracker program list:", error);
        if (isMounted) {
          setTrackerProgramIndex([]);
          setTrackerListError("Unable to load tracker list from endpoint.");
        }
      } finally {
        if (isMounted) {
          setIsTrackerListLoading(false);
        }
      }
    };

    loadTrackerPrograms();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (trackerOptions.length === 0) return;

    const requestedTrackerId = searchParams.get("tracker");
    if (requestedTrackerId && selectedTracker?.value === requestedTrackerId) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tracker", trackerOptions[0].value);
    setSearchParams(nextParams, { replace: true });
  }, [selectedTracker, trackerOptions, searchParams, setSearchParams]);

  useEffect(() => {
    if (!selectedTrackerId || trackerMetadataById[selectedTrackerId]) {
      setIsSelectedTrackerMetadataLoading(false);
      return;
    }

    let isMounted = true;

    const loadSelectedTrackerMetadata = async () => {
      setIsSelectedTrackerMetadataLoading(true);

      try {
        const response = await axios.get(`/programs/${selectedTrackerId}`, {
          params: {
            fields: PROGRAM_METADATA_FIELDS,
          },
        });

        if (isMounted && response?.data?.id) {
          setTrackerMetadataById((prev) => ({
            ...prev,
            [response.data.id]: response.data,
          }));
        }
      } catch (error) {
        console.error("Failed to load selected tracker metadata:", error);
      } finally {
        if (isMounted) {
          setIsSelectedTrackerMetadataLoading(false);
        }
      }
    };

    loadSelectedTrackerMetadata();

    return () => {
      isMounted = false;
    };
  }, [selectedTrackerId, trackerMetadataById]);

  useEffect(() => {
    setTrackerRecordsPage(1);
  }, [currentOrgUnitId]);

  useEffect(() => {
    if (typeof trackerRecordsTotalPages !== "number" || trackerRecordsTotalPages < 1) {
      return;
    }

    if (trackerRecordsPage > trackerRecordsTotalPages) {
      setTrackerRecordsPage(trackerRecordsTotalPages);
    }
  }, [trackerRecordsPage, trackerRecordsTotalPages]);

  useEffect(() => {
    if (!selectedTrackerId) {
      setTrackerRecords([]);
      setTrackerRecordsTotal(null);
      setTrackerRecordsTotalPages(null);
      setTrackerRecordsError("");
      setIsTrackerRecordsLoading(false);
      return;
    }

    if (!currentOrgUnitId) {
      setTrackerRecords([]);
      setTrackerRecordsTotal(null);
      setTrackerRecordsTotalPages(null);
      setTrackerRecordsError("");
      setIsTrackerRecordsLoading(false);
      return;
    }

    let isMounted = true;

    const loadTrackerRecords = async () => {
      setIsTrackerRecordsLoading(true);
      setTrackerRecordsError("");

      try {
        const { records, total, pageCount } = await loadAllTrackerRecords({
          orgUnitId: currentOrgUnitId,
          programId: selectedTrackerId,
        });

        if (isMounted) {
          setTrackerRecords(Array.isArray(records) ? records : []);
          setTrackerRecordsTotal(typeof total === "number" ? total : null);
          setTrackerRecordsTotalPages(
            Math.max(1, typeof total === "number" ? Math.ceil(total / TRACKER_RECORDS_PAGE_SIZE) : pageCount || 1),
          );
        }
      } catch (error) {
        console.error("Failed to load tracker records:", error);
        if (isMounted) {
          setTrackerRecords([]);
          setTrackerRecordsTotal(null);
          setTrackerRecordsTotalPages(null);
          setTrackerRecordsError("The selected tracker could not be loaded for the current location.");
        }
      } finally {
        if (isMounted) {
          setIsTrackerRecordsLoading(false);
        }
      }
    };

    loadTrackerRecords();

    return () => {
      isMounted = false;
    };
  }, [selectedTrackerId, currentOrgUnitId, recordsReloadKey]);

  const handleTrackerChange = (nextOption) => {
    if (!nextOption?.value) return;

    setViewingTrackerOverride(null);
    setIsTrackerDetailActive(false);
    setTrackerRecordsPage(1);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tracker", nextOption.value);
    nextParams.delete("q");
    nextParams.delete("qMode");
    nextParams.delete("year");
    nextParams.delete("start");
    nextParams.delete("end");
    setSearchParams(nextParams, { replace: true });
  };

  const handleRefreshRecords = () => {
    setRecordsReloadKey((prev) => prev + 1);
  };

  return (
    <div className="min-h-full bg-background">
      {!isTrackerDetailActive && (
        <div className="px-4 pt-4 md:px-6 md:pt-5">
          <div className="rounded-xl border border-border bg-card shadow-sm">
            <div className="px-4 py-3 border-b border-border flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Trackers</p>
                <p className="text-sm text-foreground font-medium truncate">
                  Select an existing tracker to view records
                </p>
              </div>
              <div className="w-full lg:w-[380px]">
                <Select
                  options={trackerOptions}
                  value={displayedTracker}
                  onChange={handleTrackerChange}
                  placeholder={isTrackerListLoading ? "Loading trackers..." : "Select tracker"}
                  searchable
                  disabled={isTrackerListLoading}
                />
              </div>
            </div>

            {trackerListError && (
              <div className="mx-4 mb-4 rounded-lg border border-warning/20 bg-warning/10 px-3 py-2 text-xs text-warning">
                {trackerListError}
              </div>
            )}
          </div>
        </div>
      )}

      {selectedTracker ? (
        <TrackerRecordsWorkspace
          currentLocation={currentLocation}
          selectedTracker={selectedTracker}
          trackerOptions={trackerOptions}
          isTrackerMetadataLoading={isSelectedTrackerMetadataLoading}
          isLoading={isTrackerRecordsLoading}
          error={trackerRecordsError}
          records={trackerRecords}
          totalCount={trackerRecordsTotal}
          currentPage={trackerRecordsPage}
          itemsPerPage={TRACKER_RECORDS_PAGE_SIZE}
          onPageChange={setTrackerRecordsPage}
          onRetry={handleRefreshRecords}
          onViewingTrackerChange={handleViewingTrackerChange}
          onDetailActiveChange={setIsTrackerDetailActive}
        />
      ) : (
        <div className="px-4 py-4 md:px-6">
          <div className="card p-5">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-warning/10 text-warning flex items-center justify-center shrink-0">
                <Icon name="Database" className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">No Trackers Available</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  The tracker list could not be loaded from the endpoint. Refresh when the connection is available.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
